import React, { useState, useEffect, useRef } from 'react';
import { useResumeRefresh } from '../hooks/useResumeRefresh';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Menu, Download, Upload, Plus, Mail, Sparkles, PaintBucket, FileText, Users, ChartBar, Edit2, DollarSign, Calendar, CheckCircle2, XCircle, Building2, Trash2, ChevronDown, ChevronUp, MoreVertical, BarChart3, AlertCircle, TrendingUp, Landmark, Percent, Clock, Paperclip, FileCheck, ListChecks, HelpCircle, Pencil, Camera, Receipt, Shield, ClipboardCheck, Eye, ClipboardList, Loader2, Globe, LogOut, LayoutDashboard, Workflow, Rocket, Hammer, X, AlertTriangle, MapPin, FileSpreadsheet, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TourLauncher from '@/components/TourLauncher';
import BidImportWizard from '@/components/BidImportWizard';
import RequestCOIDialog from '@/components/RequestCOIDialog';
import { useFeatureGates } from '@/hooks/useFeatureGates';
import { hasAdvancedModules, ownsAddon } from '@/lib/featureFlags';
import AddOnUpgrade from '@/components/AddOnUpgrade';
import SendScheduleQuestionnaireModal from '@/components/SendScheduleQuestionnaireModal';
import DrawInterestProjection from '@/components/DrawInterestProjection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { apiService, api } from '@/lib/api';
import { formatDate, formatDateWithMonth, formatDateShort, formatCurrentDate, formatDateWithTimezone, getTimezoneAbbrev, formatDateShortWithTimezone } from '@/lib/dateUtils';
import { HILLS_BANK_CATEGORIES } from '@/lib/hillsBankCategories';
import { STANDARD_CATEGORIES, getAllCategories, normalizeCategory } from '@/lib/constructionCategories';
import GanttChart from '@/components/GanttChart';
import PhasePaymentDialog from '@/components/PhasePaymentDialog';
import LogPaymentDrawer from '@/components/LogPaymentDrawer';
import LoanDetailsEditForm from '@/components/LoanDetailsEditForm';
import ConstructionPhasesV2 from '@/components/ConstructionPhasesV2';
import UnitCostBreakdown from '@/components/UnitCostBreakdown';
import { BuilderCompanyPicker } from '@/components/BuilderCompanyPicker';
import { PERMISSIONS, hasPermission } from '@/lib/permissions';
import { DrawRequestPreviewModal } from '@/components/DrawRequestPreviewModal';
import DrawScoreGate from '@/components/DrawScoreGate';
import { DrawFormMapper } from '@/components/DrawFormMapper';
import { QuickCaptureTopButton } from '@/components/QuickCapture';
import TransferWizard from '@/components/TransferWizard';
import ProjectScheduleTab from '@/components/ProjectScheduleTab';
import { MonthlyStatement } from '@/components/MonthlyStatement';

// Helper function to format dates in MM/DD/YYYY format (uses shared utility)
// Helper function to format dates in MM/DD/YYYY format (uses shared utility)
const formatDateString = (dateStr) => {
  if (!dateStr) return null;
  return formatDate(dateStr);
};

function ProjectDetails({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const gates = useFeatureGates(user);
  // Advanced project tabs (Timeline, Schedule, Spec Sheets, Paint Colors) are a
  // paid add-on for non-owner accounts — hide their nav + gate the content.
  const modulesLocked = !hasAdvancedModules(user);
  const [bidImportOpen, setBidImportOpen] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [paintColors, setPaintColors] = useState([]);
  const [aiPrediction, setAiPrediction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'budget');
  // Mobile nav drawer (hamburger) — the fixed sidebar becomes a slide-in drawer below lg
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Auto-close the mobile nav drawer whenever the active tab changes
  useEffect(() => { setMobileNavOpen(false); }, [activeTab]);

  // URL -> state: restore the tab from ?tab= (deep-links, back button, refresh).
  // Depends ONLY on searchParams (uses functional setState) so it never fights
  // the state->URL effect below when a tab is clicked.
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setActiveTab((prev) => (t !== prev ? t : prev));
  }, [searchParams]);
  // state -> URL: keep ?tab= in sync so refreshing restores the current tab
  // instead of snapping back to Budget & Progress. Functional updater avoids a
  // searchParams dependency (and the two-way loop).
  useEffect(() => {
    if (!activeTab) return;
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (sp.get('tab') === activeTab) return prev;
      sp.set('tab', activeTab);
      return sp;
    }, { replace: true });
  }, [activeTab, setSearchParams]);
  const [phaseView, setPhaseView] = useState('timeline'); // 'timeline' or 'budget'
  const [buildingComponents, setBuildingComponents] = useState([]);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [expandedBudgetPhases, setExpandedBudgetPhases] = useState({}); // Track expanded phases in budget view
  const [isPhasePaymentDialogOpen, setIsPhasePaymentDialogOpen] = useState(false);
  const [selectedPhaseForPayment, setSelectedPhaseForPayment] = useState(null);
  // v2.4: feature-flag gated new Log Payment 4-step drawer
  const [useNewPaymentFlow, setUseNewPaymentFlow] = useState(false);
  // v2.5.4 (Batch B): morphing Pay/View-Docs button + inline docs drawer
  const [useBatchB, setUseBatchB] = useState(false);
  // v2.5.6: Inspection compliance gate (HVAC/Electrical/Plumbing/Concrete/Framing)
  const [inspectionGateEnabled, setInspectionGateEnabled] = useState(false);
  
  // Holding Costs state (for draw requests)
  const [holdingCostsForDraw, setHoldingCostsForDraw] = useState([]);
  const [loanInterestForDraw, setLoanInterestForDraw] = useState(null);
  const [selectedHoldingCostsForReimbursement, setSelectedHoldingCostsForReimbursement] = useState([]);
  
  // Photos tab state
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);
  
  // Spec Sheets tab state
  const [specSheets, setSpecSheets] = useState([]);
  const [specSheetUploadLoading, setSpecSheetUploadLoading] = useState(false);
  const [newSpecSheet, setNewSpecSheet] = useState({
    category: 'appliances',
    name: '',
    description: '',
    file: null
  });
  
  // Role-based permissions state
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [teamRoles, setTeamRoles] = useState([]);
  
  // Permission helper - checks if user can perform action
  const can = (permission) => {
    if (!isTeamMember) return true; // Owner has all permissions
    return hasPermission(teamRoles, permission);
  };
  
  // Check if user is in read-only mode (investor or accountant without edit permissions)
  const isReadOnlyUser = isTeamMember && !can(PERMISSIONS.EDIT_PROJECTS);
  const isInvestor = teamRoles.includes('investor');
  // Lender-demo viewers "home" is the portfolio, not the builder dashboard.
  const isLenderViewer = user?.account_type === 'lender';
  const homePath = isLenderViewer ? '/lender' : '/dashboard';
  
  // Toggle expanded state for a phase in budget view
  const toggleBudgetPhaseExpanded = (phaseId) => {
    setExpandedBudgetPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };
  
  // Loan Details / Rate History states
  const [rateHistory, setRateHistory] = useState([]);
  const [interestCalc, setInterestCalc] = useState(null);
  const [interestSchedule, setInterestSchedule] = useState(null);
  const [savingFrequency, setSavingFrequency] = useState(false);
  const [newRateEntry, setNewRateEntry] = useState({ effective_date: '', end_date: '', interest_rate: '', notes: '' });
  const [isAddRateDialogOpen, setIsAddRateDialogOpen] = useState(false);
  const [editingRateEntry, setEditingRateEntry] = useState(null);
  const [isEditRateDialogOpen, setIsEditRateDialogOpen] = useState(false);
  // Interest payments log (v2.6.3)
  const [interestPayments, setInterestPayments] = useState([]);
  const [interestPaidTotal, setInterestPaidTotal] = useState(0);
  const [newInterestPayment, setNewInterestPayment] = useState({ amount: '', date_paid: '', notes: '' });
  const [savingInterestPayment, setSavingInterestPayment] = useState(false);
  
  // Draw History states
  const [drawHistory, setDrawHistory] = useState([]);
  const [submittedDraws, setSubmittedDraws] = useState([]);
  const [newDrawEntry, setNewDrawEntry] = useState({ draw_date: '', draw_amount: '', description: '', rate_changed: false, new_rate: '' });
  const [isAddDrawDialogOpen, setIsAddDrawDialogOpen] = useState(false);
  const [editingDrawEntry, setEditingDrawEntry] = useState(null);
  const [isEditDrawDialogOpen, setIsEditDrawDialogOpen] = useState(false);
  const [editingFundedDraw, setEditingFundedDraw] = useState(null);
  const [isEditFundedDrawOpen, setIsEditFundedDrawOpen] = useState(false);
  
  // Task Status Overview
  const [isTaskOverviewExpanded, setIsTaskOverviewExpanded] = useState(true);
  const [expandedStatusSections, setExpandedStatusSections] = useState({
    in_progress: true,
    scheduled: true,
    completed: false
  });
  
  // Phase filter states
  const [budgetStatusFilter, setBudgetStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');

  // Dialog states
  const [isPhaseDialogOpen, setIsPhaseDialogOpen] = useState(false);
  const [isEditPhaseDialogOpen, setIsEditPhaseDialogOpen] = useState(false);
  const [isPhaseDetailModalOpen, setIsPhaseDetailModalOpen] = useState(false);
  const [selectedPhaseForModal, setSelectedPhaseForModal] = useState(null);
  const [isContractorDialogOpen, setIsContractorDialogOpen] = useState(false);
  const [returnToEditPhase, setReturnToEditPhase] = useState(false);
  const [isPaintDialogOpen, setIsPaintDialogOpen] = useState(false);
  const [isEditPaintDialogOpen, setIsEditPaintDialogOpen] = useState(false);
  const [editPaint, setEditPaint] = useState(null);
  const [isPayFormDialogOpen, setIsPayFormDialogOpen] = useState(false);
  const [isCOIRequestOpen, setIsCOIRequestOpen] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [bankTemplates, setBankTemplates] = useState([]);
  const [selectedBankTemplate, setSelectedBankTemplate] = useState(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isEditProjectDialogOpen, setIsEditProjectDialogOpen] = useState(false);
  // v2.5.5.6 — schedule timing questionnaire modal
  const [showScheduleQModal, setShowScheduleQModal] = useState(false);
  const [isScheduleWizardOpen, setIsScheduleWizardOpen] = useState(false);
  const [isReimbursementDialogOpen, setIsReimbursementDialogOpen] = useState(false);
  const [isDrawScoreGateOpen, setIsDrawScoreGateOpen] = useState(false);
  const [isDrawPreviewOpen, setIsDrawPreviewOpen] = useState(false); // Draw request preview modal
  const [isGeneratingDraw, setIsGeneratingDraw] = useState(false);
  const [selectedPhasesForReimbursement, setSelectedPhasesForReimbursement] = useState([]);
  // Per-line custom draw amounts (partial draws). Keyed by phase id -> string input.
  const [customDrawAmounts, setCustomDrawAmounts] = useState({});
  const [selectedExpensesForReimbursement, setSelectedExpensesForReimbursement] = useState([]);
  const [selectedReceiptsForDraw, setSelectedReceiptsForDraw] = useState({}); // {phaseId: [receiptIds]}
  const [phaseReceiptsForDraw, setPhaseReceiptsForDraw] = useState({}); // {phaseId: [receipts]}
  const [phasePayments, setPhasePayments] = useState({});
  const [isAddPaymentDialogOpen, setIsAddPaymentDialogOpen] = useState(false);
  const [currentPhaseForPayment, setCurrentPhaseForPayment] = useState(null);
  const [reimbursementHistory, setReimbursementHistory] = useState([]);
  const [previouslyRequestedPhases, setPreviouslyRequestedPhases] = useState(new Set());
  const [isConfirmDepositDialogOpen, setIsConfirmDepositDialogOpen] = useState(false);
  const [selectedReimbursementForDeposit, setSelectedReimbursementForDeposit] = useState(null);
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDrawDetails, setSelectedDrawDetails] = useState(null);
  const [isDrawDetailsDialogOpen, setIsDrawDetailsDialogOpen] = useState(false);
  const [signDrawDocument, setSignDrawDocument] = useState(false); // Sign draw request with user's signature
  const [userSignature, setUserSignature] = useState(null); // User's saved signature
  const [downloadExcelOnly, setDownloadExcelOnly] = useState(false); // Download just Excel (no ZIP)
  const [drawFormStyle, setDrawFormStyle] = useState('hills'); // 'hills' (bank format) | 'drawready' (DrawReady Standard)
  const [isSubmittingToBank, setIsSubmittingToBank] = useState(false);
  const handleSubmitDrawToBank = async ({ lenderEmail, testEmail }) => {
    try {
      setIsSubmittingToBank(true);
      if (lenderEmail && lenderEmail !== project?.lender_email) {
        await apiService.updateProject(projectId, { lender_email: lenderEmail });
        setProject((prev) => ({ ...prev, lender_email: lenderEmail }));
      }
      await handleGenerateReimbursementRequest({ emailToBank: true, testEmail });
    } catch (e) {
      toast.dismiss();
      toast.error(e?.response?.data?.detail || 'Could not submit to bank');
    } finally {
      setIsSubmittingToBank(false);
    }
  };
  const [compareUrls, setCompareUrls] = useState(null); // { hills, drawready } object URLs for side-by-side preview
  const [isComparing, setIsComparing] = useState(false);
  
  // Receipt scanning states
  const [phaseReceipts, setPhaseReceipts] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [receiptsTotalAmount, setReceiptsTotalAmount] = useState(0);
  // Edit-receipt (fix AI mis-parse) state
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editReceiptAmount, setEditReceiptAmount] = useState('');
  const [editReceiptVendor, setEditReceiptVendor] = useState('');
  const [savingReceiptEdit, setSavingReceiptEdit] = useState(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [receiptFileInput, setReceiptFileInput] = useState(null);
  const [isUploadingExpenseReceipt, setIsUploadingExpenseReceipt] = useState(false);
  
  // Standalone AI Receipt Scanner
  const [isReceiptScannerOpen, setIsReceiptScannerOpen] = useState(false);
  const [scannerMode, setScannerMode] = useState('new'); // 'new' or 'existing'
  const [scannedReceiptData, setScannedReceiptData] = useState(null);
  const [isScanningReceipt, setIsScanningReceipt] = useState(false);
  
  // Custom Lien Waiver Template
  const [lienWaiverTemplateFile, setLienWaiverTemplateFile] = useState(null);
  const [isUploadingLienTemplate, setIsUploadingLienTemplate] = useState(false);
  // Project-specific bank draw request form (unique per address, like the lien waiver)
  const [drawFormFile, setDrawFormFile] = useState(null);
  const [isUploadingDrawForm, setIsUploadingDrawForm] = useState(false);
  const [isDrawMapperOpen, setIsDrawMapperOpen] = useState(false);
  const [customFormMapped, setCustomFormMapped] = useState(false);

  // Track whether the uploaded bank form has a saved field map (enables the
  // "Your bank's form" draw-request option).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!project?.draw_request_form_id) { setCustomFormMapped(false); return; }
      try {
        const res = await apiService.getProjectDrawFormMap(projectId);
        const fm = res?.data?.field_map;
        const mapped = !!(fm && (Object.keys(fm.fields || {}).length > 0 || fm.line_items?.start_y != null));
        if (!cancelled) setCustomFormMapped(mapped);
      } catch { if (!cancelled) setCustomFormMapped(false); }
    })();
    return () => { cancelled = true; };
  }, [project?.draw_request_form_id, projectId]);
  
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    description: '',
    payment_method: 'check',
  });
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isSoftCostsOpen, setIsSoftCostsOpen] = useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(true);
  const [newExpense, setNewExpense] = useState({
    expense_type: 'permit',
    description: '',
    vendor_name: '',
    assigned_contractor_id: '',
    estimated_amount: '',
    amount: '',
    is_paid: false,
    date_paid: new Date().toISOString().split('T')[0],
    is_reimbursable: true,
    is_running_tab: false,
  });
  // Edit expense state
  const [isEditExpenseDialogOpen, setIsEditExpenseDialogOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  // Running tab payment state (for soft costs/expenses)
  const [isExpensePaymentDialogOpen, setIsExpensePaymentDialogOpen] = useState(false);
  const [selectedExpenseForPayment, setSelectedExpenseForPayment] = useState(null);
  const [newExpensePayment, setNewExpensePayment] = useState({ amount: '', date: new Date().toISOString().split('T')[0], description: '' });
  const [expandedExpenseId, setExpandedExpenseId] = useState(null);
  const [editProjectData, setEditProjectData] = useState({
    start_date: '',
    projected_completion_date: '',
    total_budget: 0,
    contingency_budget: 0,
    purchase_price_home: 0,
    purchase_price_land: 0,
    acquisition_closing_costs: 0,
    construction_budget: 0,
    insurance: 0,
    loan_number: '',
    draw_account: '',
    bank_name: '',
    short_legal: '',
    loan_interest_rate: '',
    loan_principal_amount: '',
    loan_period_months: '',
    loan_available_for_draw: '',
    interest_start_date: '',
    loan_maturity_date: '',
    interest_day_basis: 360,
    borrower: '',
    borrower_entity: '',
    address: '',
    timezone: '',
    city: '',
    builder_company_id: null,
    builder_company_name: null,
    builder_company_logo: null,
    bank_contacts: [], // [{name: '', phone: ''}]
    listing_date: '',
    actual_completion_date: '',
    sold_date: '',
    permit_submitted_date: '',
    permit_approved_date: '',
    survey_completion_date: '',
  });
  // Timezone state for auto-detection
  const [isDetectingTimezone, setIsDetectingTimezone] = useState(false);
  const [availableTimezones, setAvailableTimezones] = useState([]);
  const [showTimezoneBanner, setShowTimezoneBanner] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isCompletionPaymentDialogOpen, setIsCompletionPaymentDialogOpen] = useState(false);
  const [completingPhase, setCompletingPhase] = useState(null);
  const [is1099DialogOpen, setIs1099DialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    contractor_id: '',
    phase_id: '',
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    is_partial: false,
    lien_waiver_received: false,
    w9_received: false,
    notes: '',
  });
  const [contractorPayments, setContractorPayments] = useState([]);
  const [selected1099Contractor, setSelected1099Contractor] = useState('');
  const [selected1099Year, setSelected1099Year] = useState(new Date().getFullYear());
  const [report1099Data, setReport1099Data] = useState(null);
  const [isFinancialAnalysisDialogOpen, setIsFinancialAnalysisDialogOpen] = useState(false);
  const [financialAnalysisTab, setFinancialAnalysisTab] = useState('sell');
  const [financialAnalysisInputs, setFinancialAnalysisInputs] = useState({
    // Sell inputs
    sale_price: 0,
    total_project_cost: 0,  // User can override this
    land_cost: 0,           // Land acquisition cost
    construction_cost: 0,   // Construction/renovation cost
    agent_commission_percent: 6.0,
    closing_costs_fixed: 0,
    interest_expense: 0,
    holding_costs: 0,       // Property taxes, insurance during construction
    // Rent inputs
    property_value: 0,
    monthly_rental_income: 0,
    annual_insurance: 0,
    annual_maintenance: 0,
    annual_property_tax: 0,
    management_fee_percent: 5.0,
    annual_reserves: 0,
    misc_expenses: 0,
    first_mortgage_amount: 0,
    first_mortgage_rate: 0,
    first_mortgage_term_years: 25,
    second_mortgage_amount: 0,
    second_mortgage_rate: 0,
    second_mortgage_interest_only: false,
  });
  const [financialAnalysisResults, setFinancialAnalysisResults] = useState(null);
  const [isCalculatingAnalysis, setIsCalculatingAnalysis] = useState(false);

  // Form states
  const [newPhase, setNewPhase] = useState({ 
    name: '', 
    category: 'general',
    bank_category: '',
    description: '', 
    phase_type: 'labor',
    estimated_cost: 0, 
    start_date: '',
    contractor_id: '',
    exclude_from_sqft: false, 
    all_inclusive_bid: false, 
    inspection_status: 'n/a',
    building_component_id: '',
    supplier_vendor: '',
    order_date: '',
    estimated_delivery_date: '',
    estimated_install_date: '',
    linked_phase_id: '',
  });
  const [editPhase, setEditPhase] = useState({ 
    id: '', 
    name: '',
    category: '',
    bank_category: '',
    phase_type: 'labor',
    status: 'pending',
    start_date: '', 
    end_date: '',
    actual_start_date: '',
    actual_end_date: '',
    contractor_id: '',
    estimated_cost: 0,
    actual_cost: 0,
    amount_drawn: 0,
    shipping_fees: 0,
    sales_tax: 0,
    credit_card_fees: 0,
    wire_fees: 0,
    notes: '',
    supplier_vendor: '',
    order_date: '',
    estimated_delivery_date: '',
    actual_delivery_date: '',
    estimated_install_date: '',
    actual_install_date: '',
    linked_phase_id: '',
    supplier_paid: false,
    supplier_amount_paid: 0,
    requires_inspection: false,
    lien_waiver_required: true,
    completion_photos_required: true,
  });
  const [newContractor, setNewContractor] = useState({ name: '', email: '', phone: '', company: '' });
  const [editingContractor, setEditingContractor] = useState(null);
  const [isEditContractorDialogOpen, setIsEditContractorDialogOpen] = useState(false);
  const [newPaint, setNewPaint] = useState({ room: '', color_name: '', color_code: '#000000', type: 'flat', purchased_from: '' });
  const [payFormData, setPayFormData] = useState({ 
    phase_id: '', 
    contractor_id: '', 
    form_types: ['lien_waiver'],
    attached_document_ids: [],
    notes: '',
    lien_waiver_amount: '',
    send_option: 'now',  // 'now' or 'schedule'
    scheduled_for: ''
  });
  const [isDocumentSelectOpen, setIsDocumentSelectOpen] = useState(false);
  const [lienWaiverStatus, setLienWaiverStatus] = useState([]);
  
  // Stripe contractor payment removed - only using Stripe for membership payments
  // const [isPayContractorDialogOpen, setIsPayContractorDialogOpen] = useState(false);
  // const [payContractorData, setPayContractorData] = useState({ contractor_id: '', amount: '', phase_id: '', notes: '' });
  // const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Check team membership for permissions
  const checkTeamMembership = async () => {
    // Lender demo viewers see every project in read-only "investor" mode.
    if (user?.account_type === 'lender') {
      setIsTeamMember(true);
      setTeamRoles(['investor']);
      return;
    }
    try {
      const response = await apiService.getMyTeamAccess(user.id);
      if (response.data && response.data.length > 0) {
        setIsTeamMember(true);
        const access = response.data[0];
        const roles = access.roles && access.roles.length > 0 
          ? access.roles 
          : [access.role || 'accountant'];
        setTeamRoles(roles);
      }
    } catch (error) {
      console.error('Failed to check team membership:', error);
    }
  };

  useResumeRefresh(() => { loadProjectData(); loadProjectExpenses(); });
  useEffect(() => {
    checkTeamMembership();
    loadProjectData();
    loadProjectExpenses();
    loadBankTemplates();
    
    // Stripe contractor payment URL handling removed - only using Stripe for membership payments
  }, [projectId]);

  // Load the user's project list once, to power the header project switcher.
  useEffect(() => {
    if (!user?.id) return;
    apiService.getProjects(user.id)
      .then((res) => setAllProjects(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (project) {
      setEditProjectData({
        start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
        projected_completion_date: project.projected_completion_date ? new Date(project.projected_completion_date).toISOString().split('T')[0] : '',
        total_budget: project.total_budget,
        contingency_budget: project.contingency_budget || 0,
        purchase_price_home: project.purchase_price_home || 0,
        purchase_price_land: project.purchase_price_land || 0,
        acquisition_closing_costs: project.acquisition_closing_costs || 0,
        construction_budget: project.construction_budget || 0,
        insurance: project.insurance || 0,
        loan_number: project.loan_number || '',
        draw_account: project.draw_account || '',
        bank_name: project.bank_name || '',
        short_legal: project.short_legal || '',
        loan_interest_rate: project.loan_interest_rate || '',
        loan_principal_amount: project.loan_principal_amount || '',
        loan_period_months: project.loan_period_months || '',
        loan_available_for_draw: project.loan_available_for_draw || '',
        interest_start_date: project.interest_start_date ? new Date(project.interest_start_date).toISOString().split('T')[0] : '',
        loan_maturity_date: project.loan_maturity_date ? String(project.loan_maturity_date).split('T')[0] : '',
        interest_day_basis: project.interest_day_basis || 360,
        borrower: project.borrower || '',
        borrower_entity: project.borrower_entity || '',
        address: project.address || '',
        timezone: project.timezone || '',
        city: project.city || '',
        builder_company_id: project.builder_company_id || null,
        builder_company_name: project.builder_company_name || null,
        builder_company_logo: project.builder_company_logo || null,
        bank_contacts: project.bank_contacts || [],
        // v2.5.5.11 — Multi-building layout
        buildings: project.buildings || 1,
        units_per_building: Array.isArray(project.units_per_building)
          ? project.units_per_building
          : (project.units_per_building ? [project.units_per_building] : [project.units || 1]),
        // v2.5.5.16 — total finished sqft powers the cost-per-sqft card
        sqft: project.sqft || '',
        // Milestone dates (Jun 2026)
        listing_date: project.listing_date || '',
        actual_completion_date: project.actual_completion_date || '',
        sold_date: project.sold_date || '',
        permit_submitted_date: project.permit_submitted_date || '',
        permit_approved_date: project.permit_approved_date || '',
        survey_completion_date: project.survey_completion_date || '',
      });
      // Show timezone banner if project has address but no timezone set
      setShowTimezoneBanner(!project.timezone && project.address);
    }
  }, [project]);
  
  // Load available timezones on mount
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        const response = await apiService.getTimezones();
        setAvailableTimezones(response.data.timezones || []);
      } catch (error) {
        console.error('Failed to load timezones:', error);
      }
    };
    loadTimezones();
  }, []);

  // Load user's signature when reimbursement dialog opens
  useEffect(() => {
    const loadUserSignature = async () => {
      if (isReimbursementDialogOpen && user?.id) {
        try {
          const response = await apiService.getSignature(user.id);
          const sig = response.data.signature_image;
          setUserSignature(sig);
          // Default to signing when the user already has a saved signature so
          // the signature reliably transfers onto the draw form (users often
          // miss the separate "Sign electronically" checkbox).
          if (sig) setSignDrawDocument(true);
        } catch (error) {
          console.error('Failed to load user signature:', error);
          setUserSignature(null);
        }
      }
    };
    loadUserSignature();
  }, [isReimbursementDialogOpen, user?.id]);

  // Check for openDraw URL parameter to auto-open draw request dialog
  useEffect(() => {
    if (searchParams.get('openDraw') === 'true' && project && !isLoading) {
      setIsReimbursementDialogOpen(true);
      // Clear the URL parameter
      searchParams.delete('openDraw');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, project, isLoading]);

  // v2.4: load the new-payment-flow feature flag for this user
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const res = await axios.get(`${API}/feature-flags/${user.id}`);
        if (!cancelled) {
          setUseNewPaymentFlow(!!res?.data?.feature_flags?.new_payment_flow_enabled);
          setUseBatchB(!!res?.data?.feature_flags?.batch_b_morphing_pay_button);
          setInspectionGateEnabled(!!res?.data?.feature_flags?.inspection_gate_enabled);
        }
      } catch (e) { /* default false */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const loadBankTemplates = async () => {
    try {
      const response = await apiService.getBankTemplates(user.id);
      setBankTemplates(response.data);
    } catch (error) {
      console.error('Failed to load bank templates:', error);
    }
  };

  const loadProjectData = async () => {
    // Lightweight retry-with-backoff for the critical-path getProject call.
    // Preview ingress occasionally surfaces 429s when many components mount
    // at once; we don't want the project page to look broken because of it.
    const getProjectWithRetry = async () => {
      let lastErr;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          return await apiService.getProject(projectId);
        } catch (e) {
          lastErr = e;
          const status = e?.response?.status;
          // Retry transient conditions: rate-limit (429), server/gateway
          // hiccups (5xx), network blips (no response), AND a racy/aborted 401.
          // iOS Safari fires requests during bfcache restore (app-switching)
          // before the auth header is attached, which 401 even though the
          // session is valid — the response interceptor already verifies
          // /auth/me and redirects to /login when the session is TRULY dead,
          // so any 401 that reaches us here means "recoverable, retry".
          const transient = status === 429 || status === 401 || (status >= 500 && status <= 599) || !e?.response;
          if (transient && attempt < 3) {
            await new Promise(r => setTimeout(r, 350 * Math.pow(2, attempt)));
            continue;
          }
          throw e;
        }
      }
      throw lastErr;
    };
    try {
      // PERF (P0-2, Jun 2026): fire EVERY independent load in ONE parallel
      // batch instead of the old sequential waterfall (which chained
      // contractor-payments → phase-payments → rate/draw/interest →
      // interest-payments → holding-costs, ~6 extra round-trips). None of
      // these depend on getProject's *result* — they only need projectId —
      // so batching removes the waterfall latency entirely.
      // allSettled so a single 429/transient failure on any side-load doesn't
      // trap the page on "Loading project...". getProject is the critical
      // path — if THAT fails we surface an error and exit; everything else
      // degrades gracefully to empty state.
      const results = await Promise.allSettled([
        getProjectWithRetry(),                                 // 0 critical
        apiService.getPhases(projectId),                       // 1
        apiService.getContractorsByUser(user.id),              // 2
        apiService.getDocuments(projectId),                    // 3
        apiService.getPaintColors(projectId),                  // 4
        apiService.getBuildingComponents(projectId),           // 5
        apiService.getCostBreakdown(projectId),                // 6
        apiService.getContractorPayments(projectId, null),     // 7
        apiService.getPhasePaymentsByProject(projectId),       // 8
        apiService.getRateHistory(projectId),                  // 9
        apiService.getDrawHistory(projectId),                  // 10
        apiService.getInterestCalculation(projectId),          // 11
        apiService.getLienWaiverStatus(projectId),             // 12
        apiService.listInterestPayments(projectId),            // 13
        apiService.getInterestSchedule(projectId),             // 14
        apiService.getSubmittedDraws(projectId, user?.id),     // 15
      ]);
      const val = (i) => (results[i] && results[i].status === 'fulfilled' ? results[i].value : null);

      const projectRes = results[0];
      if (projectRes.status !== 'fulfilled' || !projectRes.value?.data) {
        console.error('getProject failed:', projectRes.reason);
        // Only alarm the user on the INITIAL load. loadProjectData() is also
        // called after many mutations/refreshes — a transient failure there
        // must NOT throw a scary toast when the project is already on screen.
        if (!project) {
          toast.error('Could not load this project — please try again in a moment.');
        }
        return; // finally{} below will flip isLoading=false → the !project guard renders an error state
      }
      setProject(projectRes.value.data);
      if (val(1)) setPhases(val(1).data);
      if (val(2)) setContractors(val(2).data);
      if (val(3)) setDocuments(val(3).data);
      if (val(4)) setPaintColors(val(4).data);
      if (val(5)) setBuildingComponents(val(5).data);
      if (val(6)) setCostBreakdown(val(6).data);
      if (val(7)) setContractorPayments(val(7).data);

      // Phase payments — group by phase_id (fixes partial payments disappearing on refresh)
      if (val(8)) {
        const paymentsByPhase = {};
        const paymentsData = Array.isArray(val(8).data) ? val(8).data : [];
        paymentsData.forEach(payment => {
          if (!paymentsByPhase[payment.phase_id]) paymentsByPhase[payment.phase_id] = [];
          paymentsByPhase[payment.phase_id].push(payment);
        });
        setPhasePayments(paymentsByPhase);
      } else {
        setPhasePayments({});
      }

      if (val(9)) setRateHistory(val(9).data);
      if (val(10)) setDrawHistory(val(10).data);
      if (val(15)) setSubmittedDraws(val(15).data?.submitted_draws || []);
      if (val(11)) setInterestCalc(val(11).data);
      if (val(12)) setLienWaiverStatus(val(12).data);
      if (val(13)) {
        setInterestPayments(val(13).data.payments || []);
        setInterestPaidTotal(val(13).data.total_paid || 0);
      }
      if (val(14)) setInterestSchedule(val(14).data);
      // NOTE: holding-costs are intentionally NOT fetched on mount — the
      // "New Draw Request" dialog fetches them fresh on open (see onOpenChange).
      // Fetching here too was a redundant duplicate call.
    } catch (error) {
      toast.error('Failed to load project data');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const reloadInterestPayments = async () => {
    try {
      const [payRes, calcRes, schedRes] = await Promise.all([
        apiService.listInterestPayments(projectId),
        apiService.getInterestCalculation(projectId),
        apiService.getInterestSchedule(projectId),
      ]);
      setInterestPayments(payRes.data.payments || []);
      setInterestPaidTotal(payRes.data.total_paid || 0);
      if (calcRes?.data) setInterestCalc(calcRes.data);
      if (schedRes?.data) setInterestSchedule(schedRes.data);
    } catch (e) {
      console.log('Interest payments reload failed:', e);
    }
  };

  const handleUpdateInterestFrequency = async (updates) => {
    setSavingFrequency(true);
    try {
      await apiService.updateProject(projectId, updates);
      setProject((prev) => ({ ...prev, ...updates }));
      const schedRes = await apiService.getInterestSchedule(projectId);
      if (schedRes?.data) setInterestSchedule(schedRes.data);
      toast.success('Interest payment schedule updated');
    } catch (e) {
      toast.error('Could not update payment schedule');
      console.error(e);
    } finally {
      setSavingFrequency(false);
    }
  };

  const [savingMaturity, setSavingMaturity] = useState(false);
  const [maturityInput, setMaturityInput] = useState('');
  const handleSaveMaturityDate = async () => {
    if (!maturityInput) { toast.error('Pick a maturity date'); return; }
    setSavingMaturity(true);
    try {
      await apiService.updateProject(projectId, { loan_maturity_date: maturityInput });
      setProject((prev) => ({ ...prev, loan_maturity_date: maturityInput }));
      const calcRes = await apiService.getInterestCalculation(projectId);
      if (calcRes?.data) setInterestCalc(calcRes.data);
      toast.success('Loan maturity date saved');
    } catch (e) {
      toast.error('Could not save maturity date');
      console.error(e);
    } finally {
      setSavingMaturity(false);
    }
  };

  const handleAddInterestPayment = async () => {
    const amount = parseFloat(newInterestPayment.amount);
    if (!amount || amount <= 0) { toast.error('Enter an interest amount'); return; }
    if (!newInterestPayment.date_paid) { toast.error('Select the payment date'); return; }
    setSavingInterestPayment(true);
    try {
      await apiService.addInterestPayment(projectId, {
        amount,
        date_paid: newInterestPayment.date_paid,
        notes: newInterestPayment.notes || null,
      });
      setNewInterestPayment({ amount: '', date_paid: '', notes: '' });
      await reloadInterestPayments();
      toast.success('Interest payment recorded');
    } catch (e) {
      toast.error('Could not record interest payment');
    } finally {
      setSavingInterestPayment(false);
    }
  };

  const handleDeleteInterestPayment = async (paymentId) => {
    try {
      await apiService.deleteInterestPayment(paymentId);
      await reloadInterestPayments();
      toast.success('Interest payment removed');
    } catch (e) {
      toast.error('Could not remove entry');
    }
  };

  
  const loadLienWaiverStatus = async () => {
    try {
      const response = await apiService.getLienWaiverStatus(projectId);
      setLienWaiverStatus(response.data);
    } catch (e) {
      console.log('Lien waiver status not available:', e);
    }
  };

  const handleCreatePhase = async (e) => {
    e.preventDefault();
    try {
      const phaseData = {
        ...newPhase,
        project_id: projectId,
        estimated_cost: parseFloat(newPhase.estimated_cost) || 0,
      };
      
      // Remove empty string values - convert to null or undefined
      Object.keys(phaseData).forEach(key => {
        if (phaseData[key] === '') {
          delete phaseData[key];
        }
      });
      
      // Convert start_date to ISO format if provided
      if (newPhase.start_date) {
        phaseData.start_date = new Date(newPhase.start_date).toISOString();
      }
      
      // Convert material phase dates if provided
      if (newPhase.order_date) {
        phaseData.order_date = new Date(newPhase.order_date).toISOString();
      }
      if (newPhase.estimated_delivery_date) {
        phaseData.estimated_delivery_date = new Date(newPhase.estimated_delivery_date).toISOString();
      }
      if (newPhase.estimated_install_date) {
        phaseData.estimated_install_date = new Date(newPhase.estimated_install_date).toISOString();
      }
      
      console.log('Creating phase with data:', phaseData);
      
      await apiService.createPhase(phaseData);
      toast.success('Phase created successfully!');
      setIsPhaseDialogOpen(false);
      setNewPhase({ 
        name: '', 
        category: 'general',
        bank_category: '',
        description: '', 
        phase_type: 'labor',
        estimated_cost: 0, 
        start_date: '',
        contractor_id: '',
        exclude_from_sqft: false, 
        all_inclusive_bid: false, 
        inspection_status: 'n/a',
        building_component_id: '',
        supplier_vendor: '',
        order_date: '',
        estimated_delivery_date: '',
        estimated_install_date: '',
        linked_phase_id: '',
      });
      loadProjectData();
    } catch (error) {
      toast.error('Failed to create phase');
    }
  };

  const handleUpdatePhaseStatus = async (phaseId, status) => {
    // If completing a labor phase, check if payment exists first
    if (status === 'completed') {
      const phase = phases.find(p => p.id === phaseId);
      if (phase && phase.phase_type === 'labor') {
        // Check if payment already exists for this phase
        const existingPayment = contractorPayments.find(p => p.phase_id === phaseId);
        
        if (existingPayment) {
          // Payment already recorded, just mark as complete
          if (window.confirm('Payment already recorded for this phase. Mark as complete without creating duplicate payment?')) {
            try {
              await apiService.updatePhase(phaseId, {
                status: 'completed',
                end_date: new Date().toISOString()
              });
              toast.success('Phase marked as completed');
              loadProjectData();
            } catch (error) {
              toast.error('Failed to complete phase');
            }
          }
          return;
        }
        
        // No existing payment, open payment dialog
        setCompletingPhase(phase);
        setPaymentData({
          phase_id: phaseId,
          contractor_id: phase.contractor_id || '',
          amount: phase.actual_cost || phase.estimated_cost || 0,
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: 'check',
          is_partial: false,
          lien_waiver_received: false,
          w9_received: false,
          notes: ''
        });
        setIsCompletionPaymentDialogOpen(true);
        return;
      }
    }
    
    // For non-labor or other statuses, update directly
    try {
      const updateData = { status };
      if (status === 'completed') {
        updateData.end_date = new Date().toISOString();
      }
      await apiService.updatePhase(phaseId, updateData);
      toast.success(`Phase marked as ${status}!`);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update phase');
    }
  };

  const handleCompletePhaseWithPayment = async () => {
    if (!completingPhase) return;
    
    // Validate contractor is assigned
    if (!paymentData.contractor_id) {
      toast.error('Please assign a contractor before recording payment');
      return;
    }
    
    try {
      // Mark phase as completed
      await apiService.updatePhase(completingPhase.id, {
        status: 'completed',
        end_date: new Date().toISOString(),
        is_paid: true,
        payment_date: paymentData.payment_date
      });
      
      // Record payment
      await apiService.createContractorPayment({
        project_id: projectId,
        contractor_id: paymentData.contractor_id,
        phase_id: completingPhase.id,
        phase_name: completingPhase.name,
        amount: parseFloat(paymentData.amount),
        is_partial: paymentData.is_partial,
        payment_date: paymentData.payment_date + 'T12:00:00Z',  // Add noon UTC to avoid timezone shifting
        payment_method: paymentData.payment_method,
        lien_waiver_received: paymentData.lien_waiver_received,
        notes: paymentData.notes
      });
      
      toast.success('Phase completed and payment recorded!');
      setIsCompletionPaymentDialogOpen(false);
      setCompletingPhase(null);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to complete phase and record payment');
      console.error(error);
    }
  };

  const handleSkipPayment = async () => {
    if (!completingPhase) return;
    
    try {
      await apiService.updatePhase(completingPhase.id, {
        status: 'completed',
        end_date: new Date().toISOString()
      });
      toast.success('Phase marked as completed (payment skipped)');
      setIsCompletionPaymentDialogOpen(false);
      setCompletingPhase(null);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to complete phase');
    }
  };

  // Calculate automatic phase status based on dates and payment info (for UI badges)
  const getPhaseStatusBadge = (phase) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const isMaterial = phase.phase_type === 'material';
    
    // Check for budget status - applies to completed phases
    const estimatedCost = parseFloat(phase.estimated_cost) || 0;
    const actualCost = parseFloat(phase.actual_cost) || 0;
    const isOverBudget = actualCost > 0 && estimatedCost > 0 && actualCost > estimatedCost;
    const isUnderBudget = actualCost > 0 && estimatedCost > 0 && actualCost < estimatedCost;
    
    // Check for schedule status - compare actual_end_date vs end_date (scheduled)
    const scheduledEndDate = phase.end_date ? new Date(phase.end_date) : null;
    const actualEndDate = phase.actual_end_date ? new Date(phase.actual_end_date) : null;
    const isAheadOfSchedule = scheduledEndDate && actualEndDate && actualEndDate < scheduledEndDate;
    
    if (isMaterial) {
      // Material phase status flow
      const hasOrderDate = phase.order_date;
      const isPaid = phase.supplier_paid || (phase.actual_cost > 0);
      const hasDeliveryDate = phase.estimated_delivery_date;
      const hasActualDelivery = phase.actual_delivery_date;
      const hasActualInstall = phase.actual_install_date;
      
      // Check for completed/installed states
      if (hasActualInstall) {
        // Completed material phase - check for bonus badges
        const bonusBadges = [];
        if (isUnderBudget) bonusBadges.push({ type: 'underBudget', label: 'Under Budget', icon: '💰' });
        if (isAheadOfSchedule) bonusBadges.push({ type: 'aheadOfSchedule', label: 'Ahead of Schedule', icon: '🚀' });
        
        if (isOverBudget) {
          return { status: 'Installed / Over Budget', color: 'red', icon: '⚠️', isOverage: true, bonusBadges: [] };
        }
        return { status: 'Installed', color: 'emerald', icon: '✓', bonusBadges };
      }
      if (hasActualDelivery) {
        if (isOverBudget) {
          return { status: 'Delivered / Over Budget', color: 'red', icon: '⚠️', isOverage: true, bonusBadges: [] };
        }
        return { status: 'Delivered', color: 'teal', icon: '📦', bonusBadges: [] };
      }
      if (hasDeliveryDate && isPaid) {
        const deliveryDate = new Date(phase.estimated_delivery_date);
        const daysUntilDelivery = Math.ceil((deliveryDate - today) / (1000 * 60 * 60 * 24));
        
        if (daysUntilDelivery <= 0) {
          if (isOverBudget) {
            return { status: 'Delivered / Over Budget', color: 'red', icon: '⚠️', isOverage: true, bonusBadges: [] };
          }
          return { status: 'Delivered', color: 'teal', icon: '📦', bonusBadges: [] };
        }
        if (daysUntilDelivery <= 7) {
          return { status: 'In Transit', color: 'orange', icon: '🚚', animated: true, bonusBadges: [] };
        }
        return { status: 'Paid & Scheduled', color: 'purple', icon: '📅', gradient: true, bonusBadges: [] };
      }
      if (isPaid) {
        if (isOverBudget) {
          return { status: 'Paid / Over Budget', color: 'red', icon: '⚠️', isOverage: true, bonusBadges: [] };
        }
        return { status: 'Paid', color: 'green', icon: '💰', bonusBadges: [] };
      }
      if (hasOrderDate) {
        const orderDate = new Date(phase.order_date);
        orderDate.setHours(0, 0, 0, 0);
        if (orderDate > today) {
          return { status: 'Order Scheduled', color: 'purple', icon: '📅', bonusBadges: [] };
        }
        return { status: 'Ordered', color: 'blue', icon: '📋', bonusBadges: [] };
      }
      return { status: 'Pending', color: 'slate', icon: '⏳', bonusBadges: [] };
    } else {
      // Labor phase status flow
      const hasStartDate = phase.start_date;
      const hasEndDate = phase.end_date;
      const isCompleted = phase.status === 'completed';
      
      if (isCompleted || hasEndDate) {
        const endDate = hasEndDate ? new Date(phase.end_date) : null;
        if (!endDate || endDate <= today) {
          // Completed labor phase - check for bonus badges
          const bonusBadges = [];
          if (isUnderBudget) bonusBadges.push({ type: 'underBudget', label: 'Under Budget', icon: '💰' });
          if (isAheadOfSchedule) bonusBadges.push({ type: 'aheadOfSchedule', label: 'Ahead of Schedule', icon: '🚀' });
          
          if (isOverBudget) {
            return { status: 'Completed / Over Budget', color: 'red', icon: '⚠️', isOverage: true, bonusBadges: [] };
          }
          return { status: 'Completed', color: 'emerald', icon: '✓', bonusBadges };
        }
      }
      
      if (hasStartDate) {
        const startDate = new Date(phase.start_date);
        if (startDate <= today) {
          if (isOverBudget) {
            return { status: 'In Progress / Over Budget', color: 'red', icon: '⚠️', isOverage: true, animated: true, bonusBadges: [] };
          }
          return { status: 'In Progress', color: 'yellow', icon: '🔨', animated: true, bonusBadges: [] };
        }
        return { status: 'Scheduled', color: 'purple', icon: '📅', bonusBadges: [] };
      }
      
      return { status: 'Pending', color: 'slate', icon: '⏳', bonusBadges: [] };
    }
  };

  // Render status badge component with bonus badges for under budget and ahead of schedule
  const renderStatusBadge = (phase) => {
    const statusInfo = getPhaseStatusBadge(phase);
    
    const colorClasses = {
      slate: 'bg-slate-100 text-slate-600 border-slate-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200',
      teal: 'bg-teal-100 text-teal-700 border-teal-200',
      emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      red: 'bg-red-100 text-red-700 border-red-200',
    };
    
    const dotColors = {
      slate: 'bg-slate-400',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      teal: 'bg-teal-500',
      emerald: 'bg-emerald-500',
      yellow: 'bg-yellow-500',
      red: 'bg-red-500',
    };
    
    // Special styling for overage badges (no bonus badges shown)
    if (statusInfo.isOverage) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-red-100 text-red-700 border-red-300" data-testid="phase-overage-badge">
          <span className="text-base leading-none">⚠️</span>
          {statusInfo.status}
        </span>
      );
    }
    
    // Main badge + bonus badges (under budget, ahead of schedule)
    const bonusBadges = statusInfo.bonusBadges || [];
    
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Main status badge */}
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusInfo.gradient ? 'bg-gradient-to-r from-green-100 to-purple-100 text-purple-700 border-purple-200' : colorClasses[statusInfo.color]}`}>
          <span className={`w-2 h-2 rounded-full ${dotColors[statusInfo.color]} ${statusInfo.animated ? 'animate-pulse' : ''}`}></span>
          {statusInfo.icon === '✓' ? '✓ ' : ''}{statusInfo.status}
        </span>
        
        {/* Bonus badges for positive performance (under budget, ahead of schedule) */}
        {bonusBadges.map((badge, idx) => (
          <span 
            key={idx}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
              badge.type === 'underBudget' 
                ? 'bg-green-50 text-green-700 border-green-300' 
                : 'bg-blue-50 text-blue-700 border-blue-300'
            }`}
            data-testid={`phase-${badge.type}-badge`}
          >
            <span className="text-sm">{badge.icon}</span>
            {badge.label}
          </span>
        ))}
      </div>
    );
  };

  const handleGenerate1099 = async (contractorId) => {
    try {
      const year = new Date().getFullYear();
      const response = await apiService.get1099Report(contractorId, year, user.id);
      setReport1099Data(response.data);
      setSelected1099Contractor(contractorId);
      setSelected1099Year(year);
      setIs1099DialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate 1099 report');
      console.error(error);
    }
  };

  const handleDownload1099 = () => {
    if (!report1099Data) return;
    
    // Create formatted text content for download
    const content = `
1099-MISC TAX REPORT
Tax Year: ${report1099Data.year}
Generated: ${formatCurrentDate()}

═══════════════════════════════════════════════════
CONTRACTOR INFORMATION
═══════════════════════════════════════════════════

Name:        ${report1099Data.contractor.name}
Company:     ${report1099Data.contractor.company || 'N/A'}
Email:       ${report1099Data.contractor.email}
EIN/SSN:     ${report1099Data.contractor.ein_ssn}
Address:     ${report1099Data.contractor.address || 'Not provided'}
W9 Status:   ${report1099Data.contractor.w9_received ? '✓ On File' : '✗ Missing'}

═══════════════════════════════════════════════════
PAYMENT SUMMARY - ${report1099Data.year}
═══════════════════════════════════════════════════

Total Payments:     $${report1099Data.total_payments.toLocaleString()}
Number of Payments: ${report1099Data.payment_count}
Projects:           ${report1099Data.project_count}
1099 Required:      ${report1099Data.requires_1099 ? 'YES (≥ $600)' : 'NO (< $600)'}

═══════════════════════════════════════════════════
PAYMENT DETAILS
═══════════════════════════════════════════════════

${report1099Data.payments.map(p => 
  `${formatDate(p.date).padEnd(12)} | ${p.project.padEnd(25)} | ${p.phase.padEnd(25)} | $${p.amount.toLocaleString().padStart(10)}`
).join('\n')}

═══════════════════════════════════════════════════
TOTAL:                                              $${report1099Data.total_payments.toLocaleString().padStart(10)}
═══════════════════════════════════════════════════

IRS Form 1099-MISC Instructions:
- Box 1 (Rents): Leave blank
- Box 2 (Royalties): Leave blank  
- Box 7 (Nonemployee Compensation): $${report1099Data.total_payments.toLocaleString()}

This report includes all payments made to this contractor across
all projects for the ${report1099Data.year} tax year.

Generated by Real Estate Cost Tracker
    `.trim();

    // Create blob and download
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `1099_${report1099Data.contractor.name.replace(/\s/g, '_')}_${report1099Data.year}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('1099 report downloaded');
  };

  const handleEditPhase = async (phase) => {
    setEditPhase({
      id: phase.id,
      name: phase.name || '',
      category: phase.category || 'general',
      bank_category: phase.bank_category || '',
      phase_type: phase.phase_type || 'labor',
      status: phase.status || 'pending',
      start_date: phase.start_date ? new Date(phase.start_date).toISOString().split('T')[0] : '',
      end_date: phase.end_date ? new Date(phase.end_date).toISOString().split('T')[0] : '',
      actual_start_date: phase.actual_start_date ? new Date(phase.actual_start_date).toISOString().split('T')[0] : '',
      actual_end_date: phase.actual_end_date ? new Date(phase.actual_end_date).toISOString().split('T')[0] : '',
      contractor_id: phase.contractor_id || '',
      estimated_cost: phase.estimated_cost || 0,
      actual_cost: phase.actual_cost || 0,
      amount_drawn: phase.amount_drawn || 0,
      shipping_fees: phase.shipping_fees || 0,
      sales_tax: phase.sales_tax || 0,
      credit_card_fees: phase.credit_card_fees || 0,
      wire_fees: phase.wire_fees || 0,
      notes: phase.notes || '',
      supplier_vendor: phase.supplier_vendor || '',
      order_date: phase.order_date ? new Date(phase.order_date).toISOString().split('T')[0] : '',
      estimated_delivery_date: phase.estimated_delivery_date ? new Date(phase.estimated_delivery_date).toISOString().split('T')[0] : '',
      actual_delivery_date: phase.actual_delivery_date ? new Date(phase.actual_delivery_date).toISOString().split('T')[0] : '',
      estimated_install_date: phase.estimated_install_date ? new Date(phase.estimated_install_date).toISOString().split('T')[0] : '',
      actual_install_date: phase.actual_install_date ? new Date(phase.actual_install_date).toISOString().split('T')[0] : '',
      linked_phase_id: phase.linked_phase_id || '',
      supplier_paid: phase.supplier_paid || false,
      supplier_amount_paid: phase.supplier_amount_paid || 0,
      // Compliance settings
      requires_inspection: phase.requires_inspection || false,
      lien_waiver_required: phase.lien_waiver_required !== false, // Default true
      completion_photos_required: phase.completion_photos_required !== false, // Default true
      payment_status: phase.payment_status || null,
    });
    
    // Load existing payments for this phase
    try {
      const res = await apiService.getPhasePayments(phase.id);
      setPhasePayments(prev => ({
        ...prev,
        [phase.id]: res.data || []
      }));
    } catch (e) {
      console.error('Failed to load phase payments:', e);
    }
    
    setIsEditPhaseDialogOpen(true);
  };

  const handleSavePhaseEdit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: editPhase.name,
        category: editPhase.category,
        bank_category: editPhase.bank_category || null,
        phase_type: editPhase.phase_type,
        status: editPhase.status || 'pending',
        start_date: editPhase.start_date || null,
        end_date: editPhase.end_date || null,
        actual_start_date: editPhase.actual_start_date || null,
        actual_end_date: editPhase.actual_end_date || null,
        contractor_id: editPhase.contractor_id || null,
        estimated_cost: parseFloat(editPhase.estimated_cost) || 0,
        actual_cost: parseFloat(editPhase.actual_cost) || 0,
        amount_drawn: parseFloat(editPhase.amount_drawn) || 0,
        shipping_fees: parseFloat(editPhase.shipping_fees) || 0,
        sales_tax: parseFloat(editPhase.sales_tax) || 0,
        credit_card_fees: parseFloat(editPhase.credit_card_fees) || 0,
        wire_fees: parseFloat(editPhase.wire_fees) || 0,
        notes: editPhase.notes || null,
        supplier_vendor: editPhase.supplier_vendor || null,
        order_date: editPhase.order_date || null,
        estimated_delivery_date: editPhase.estimated_delivery_date || null,
        actual_delivery_date: editPhase.actual_delivery_date || null,
        estimated_install_date: editPhase.estimated_install_date || null,
        actual_install_date: editPhase.actual_install_date || null,
        linked_phase_id: editPhase.linked_phase_id || null,
        supplier_paid: editPhase.supplier_paid,
        supplier_amount_paid: parseFloat(editPhase.supplier_amount_paid) || 0,
        // Compliance settings
        requires_inspection: editPhase.requires_inspection || false,
        lien_waiver_required: editPhase.lien_waiver_required !== false, // Default true
        completion_photos_required: editPhase.completion_photos_required !== false, // Default true
      };
      await apiService.updatePhase(editPhase.id, updateData);
      toast.success('Phase updated successfully!');
      setIsEditPhaseDialogOpen(false);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update phase');
      console.error(error);
    }
  };

  const handleDeletePhase = async () => {
    if (!window.confirm(`Are you sure you want to delete "${editPhase.name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await apiService.deletePhase(editPhase.id);
      toast.success('Phase deleted successfully!');
      setIsEditPhaseDialogOpen(false);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to delete phase');
      console.error(error);
    }
  };

  const handleTogglePaidStatus = async (phaseId, currentPaidStatus, contractorId) => {
    try {
      const newPaidStatus = !currentPaidStatus;
      
      // If marking as paid, ensure a contractor is selected
      if (newPaidStatus && !contractorId) {
        toast.error('Please select a contractor before marking as paid');
        return;
      }
      
      // Get the phase data to get the payment amount
      const phase = phases.find(p => p.id === phaseId);
      const paymentAmount = phase?.actual_cost || phase?.estimated_cost || 0;
      
      // Update the phase as paid
      await apiService.updatePhase(phaseId, {
        is_paid: newPaidStatus,
        payment_date: newPaidStatus ? new Date().toISOString() : null,
        contractor_id: newPaidStatus ? contractorId : null,
      });
      
      // If marking as paid, also create a contractor payment record
      if (newPaidStatus && contractorId && paymentAmount > 0) {
        try {
          // Check if payment already exists for this phase
          const existingPayments = contractorPayments.filter(p => p.phase_id === phaseId);
          
          if (existingPayments.length === 0) {
            await apiService.createContractorPayment({
              contractor_id: contractorId,
              project_id: projectId,
              phase_id: phaseId,
              phase_name: phase?.name || 'Phase',
              amount: paymentAmount,
              payment_date: new Date().toISOString(),
              payment_method: 'check',
              notes: `Auto-logged from marking phase as paid`,
              is_partial: false,
              lien_waiver_received: false
            });
            toast.success('Marked as paid and payment logged to contractor!');
          } else {
            toast.success('Marked as paid! (Payment already logged)');
          }
        } catch (paymentError) {
          console.error('Failed to create contractor payment:', paymentError);
          toast.success('Marked as paid! (Note: Auto-payment log may have failed)');
        }
      } else if (!newPaidStatus) {
        toast.success('Marked as unpaid');
      } else {
        toast.success('Marked as paid!');
      }
      
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update payment status');
      console.error(error);
    }
  };

  const handleToggleSupplierPaidStatus = async (phaseId, currentPaidStatus, paymentType, amountPaid) => {
    try {
      let newPaidStatus = false;
      let newAmountPaid = 0;
      
      if (paymentType === 'unpaid') {
        newPaidStatus = false;
        newAmountPaid = 0;
      } else if (paymentType === 'partial') {
        newPaidStatus = false;
        newAmountPaid = parseFloat(amountPaid) || 0;
      } else if (paymentType === 'full') {
        newPaidStatus = true;
        newAmountPaid = 0; // Full payment doesn't need amount tracking
      }
      
      await apiService.updatePhase(phaseId, {
        supplier_paid: newPaidStatus,
        supplier_amount_paid: newAmountPaid,
        supplier_payment_date: (newPaidStatus || newAmountPaid > 0) ? new Date().toISOString() : null,
      });
      toast.success('Supplier payment status updated!');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update supplier payment status');
      console.error(error);
    }
  };

  const handlePaymentProofUpload = async (phaseId, paymentType, file) => {
    try {
      await apiService.uploadPaymentProof(phaseId, paymentType, file);
      toast.success('Payment proof uploaded successfully!');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to upload payment proof');
      console.error(error);
    }
  };

  const handleUpdateActualCost = async (phaseId, actualCost) => {
    try {
      await apiService.updatePhase(phaseId, { actual_cost: parseFloat(actualCost) });
      toast.success('Actual cost updated! Budget recalculated.');
      // Force reload to update budget
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to update cost');
    }
  };

  // Mark phase as fully drawn (amount_drawn = paid amount)
  // Option C: Smart Detection - only shows confirmation when partial draws exist
  const handleMarkAsFullyDrawn = async (phase, isDrawn) => {
    try {
      // Calculate the paid amount (same logic as table)
      const partialPaymentsTotal = phasePayments[phase.id] 
        ? phasePayments[phase.id].reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) 
        : 0;
      const paidAmount = partialPaymentsTotal > 0 ? partialPaymentsTotal : (parseFloat(phase.actual_cost) || 0);
      const currentDrawn = parseFloat(phase.amount_drawn) || 0;
      
      // Smart Detection: If marking as drawn and there's already a partial draw
      if (isDrawn && currentDrawn > 0 && currentDrawn < paidAmount) {
        const remainingAmount = paidAmount - currentDrawn;
        const confirmed = window.confirm(
          `This phase already has $${currentDrawn.toLocaleString()} drawn.\n\n` +
          `Do you want to mark the remaining $${remainingAmount.toLocaleString()} as drawn?\n\n` +
          `(Total will be $${paidAmount.toLocaleString()})`
        );
        if (!confirmed) return;
      }
      
      const newAmountDrawn = isDrawn ? paidAmount : 0;
      
      await apiService.updatePhase(phase.id, { amount_drawn: newAmountDrawn });
      toast.success(isDrawn ? 'Marked as fully drawn!' : 'Unmarked as drawn');
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to update drawn status');
      console.error(error);
    }
  };

  // Mark soft cost as fully drawn
  // Option C: Smart Detection - only shows confirmation when partial draws exist
  const handleMarkExpenseAsFullyDrawn = async (expense, isDrawn) => {
    try {
      const paidAmount = parseFloat(expense.amount) || 0;
      const currentDrawn = parseFloat(expense.amount_drawn) || 0;
      
      // Smart Detection: If marking as drawn and there's already a partial draw
      if (isDrawn && currentDrawn > 0 && currentDrawn < paidAmount) {
        const remainingAmount = paidAmount - currentDrawn;
        const confirmed = window.confirm(
          `This soft cost already has $${currentDrawn.toLocaleString()} drawn.\n\n` +
          `Do you want to mark the remaining $${remainingAmount.toLocaleString()} as drawn?\n\n` +
          `(Total will be $${paidAmount.toLocaleString()})`
        );
        if (!confirmed) return;
      }
      
      const newAmountDrawn = isDrawn ? paidAmount : 0;
      
      await apiService.updateProjectExpense(expense.id, { 
        amount_drawn: newAmountDrawn,
        is_drawn: isDrawn 
      });
      toast.success(isDrawn ? 'Marked as fully drawn!' : 'Unmarked as drawn');
      await loadProjectExpenses();
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to update drawn status');
      console.error(error);
    }
  };

  const handleMarkPhaseDrawn = async (phaseId, isDrawn) => {
    try {
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) return;
      
      const paidAmount = parseFloat(phase.actual_cost) || 0;
      const currentDrawn = parseFloat(phase.amount_drawn) || 0;
      
      // Smart Detection: If marking as drawn and there's already a partial draw
      if (isDrawn && currentDrawn > 0 && currentDrawn < paidAmount) {
        const remainingAmount = paidAmount - currentDrawn;
        const confirmed = window.confirm(
          `This phase already has $${currentDrawn.toLocaleString()} drawn.\n\n` +
          `Do you want to mark the remaining $${remainingAmount.toLocaleString()} as drawn?\n\n` +
          `(Total will be $${paidAmount.toLocaleString()})`
        );
        if (!confirmed) return;
      }
      
      const newAmountDrawn = isDrawn ? paidAmount : 0;
      
      await apiService.updatePhase(phaseId, { 
        amount_drawn: newAmountDrawn,
        is_drawn: isDrawn 
      });
      toast.success(isDrawn ? 'Marked as fully drawn!' : 'Unmarked as drawn');
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to update drawn status');
      console.error(error);
    }
  };

  const handleUpdateInspectionStatus = async (phaseId, inspectionStatus) => {
    try {
      await apiService.updatePhase(phaseId, { inspection_status: inspectionStatus });
      toast.success('Inspection status updated!');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update inspection status');
    }
  };

  const handleUpdatePhase = async (phaseId, updateData) => {
    try {
      await apiService.updatePhase(phaseId, updateData);
      toast.success('Phase updated successfully!');
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to update phase');
    }
  };

  // Resend portal email to contractor from phase card
  const handleResendPortalEmail = async (contractorId, contractorEmail) => {
    if (!window.confirm(`Resend portal access email to ${contractorEmail}?`)) return;
    try {
      await apiService.resendContractorPortalEmail(contractorId, user.id);
      toast.success(`Portal link sent to ${contractorEmail}`);
    } catch (error) {
      console.error('Failed to resend portal email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send portal email');
    }
  };

  const handleCreateContractor = async (e) => {
    e.preventDefault();
    try {
      await apiService.createContractor({ ...newContractor, project_id: projectId });
      toast.success('Contractor added successfully!');
      setIsContractorDialogOpen(false);
      setNewContractor({ name: '', email: '', phone: '', company: '' });
      await loadProjectData();
      
      // If we came from edit phase dialog, re-open it
      if (returnToEditPhase) {
        setTimeout(() => {
          setIsEditPhaseDialogOpen(true);
          setReturnToEditPhase(false);
        }, 100);
      }
    } catch (error) {
      toast.error('Failed to add contractor');
    }
  };

  const handleEditContractor = (contractor) => {
    console.log('handleEditContractor called with:', contractor);
    setEditingContractor({
      id: contractor.id,
      name: contractor.name || '',
      email: contractor.email || '',
      phone: contractor.phone || '',
      company: contractor.company || '',
      address: contractor.address || '',
      ein_ssn: contractor.ein_ssn || '',
    });
    console.log('Setting isEditContractorDialogOpen to true');
    setIsEditContractorDialogOpen(true);
  };

  const handleUpdateContractor = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateContractor(editingContractor.id, {
        name: editingContractor.name,
        email: editingContractor.email,
        phone: editingContractor.phone || null,
        company: editingContractor.company || null,
        address: editingContractor.address || null,
        ein_ssn: editingContractor.ein_ssn || null,
      });
      toast.success('Contractor updated successfully!');
      setIsEditContractorDialogOpen(false);
      setEditingContractor(null);
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to update contractor');
      console.error(error);
    }
  };

  const handleDeleteContractor = async (contractorId) => {
    if (!window.confirm('Are you sure you want to delete this contractor? This action cannot be undone.')) {
      return;
    }
    try {
      await apiService.deleteContractor(contractorId);
      toast.success('Contractor deleted successfully!');
      await loadProjectData();
    } catch (error) {
      toast.error('Failed to delete contractor');
      console.error(error);
    }
  };

  // Stripe Contractor Payment removed - only using Stripe for membership payments
  // handlePayContractor function removed

  const handleCreatePaintColor = async (e) => {
    e.preventDefault();
    try {
      await apiService.createPaintColor({ ...newPaint, project_id: projectId });
      toast.success('Paint color saved!');
      setIsPaintDialogOpen(false);
      setNewPaint({ room: '', color_name: '', color_code: '#000000', type: 'flat', purchased_from: '' });
      loadProjectData();
    } catch (error) {
      toast.error('Failed to save paint color');
    }
  };

  const handleUpdatePaintColor = async (e) => {
    e.preventDefault();
    try {
      await apiService.updatePaintColor(editPaint.id, editPaint);
      toast.success('Paint color updated!');
      setIsEditPaintDialogOpen(false);
      setEditPaint(null);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update paint color');
    }
  };

  const handleDeletePaintColor = async (colorId) => {
    if (!window.confirm('Delete this paint color?')) return;
    try {
      await apiService.deletePaintColor(colorId);
      toast.success('Paint color deleted');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to delete paint color');
    }
  };

  const handleSendPayForm = async (e) => {
    e.preventDefault();
    try {
      // Use the new Form Request API with secure upload links
      const hostUrl = window.location.origin;
      
      // Build request data with optional phase info for lien waivers
      const requestData = {
        project_id: projectId,
        contractor_id: payFormData.contractor_id,
        form_types: payFormData.form_types,
        attached_document_ids: payFormData.attached_document_ids || [],
        notes: payFormData.notes || null,
      };
      
      // Add phase info if a lien waiver is requested and phase is selected
      if (payFormData.form_types.includes('lien_waiver') && payFormData.phase_id) {
        requestData.phase_id = payFormData.phase_id;
        requestData.lien_waiver_amount = payFormData.lien_waiver_amount || null;
      }
      
      // Add scheduling if selected
      if (payFormData.send_option === 'schedule' && payFormData.scheduled_for) {
        requestData.scheduled_for = new Date(payFormData.scheduled_for).toISOString();
      }
      
      const response = await apiService.createFormRequest(requestData, hostUrl);
      
      // Show appropriate success message
      if (response.data.scheduled_for) {
        const scheduledDate = new Date(response.data.scheduled_for);
        toast.success(`📅 Email scheduled for ${scheduledDate.toLocaleString()}`, {
          duration: 5000,
        });
      } else {
        toast.success(response.data.email_sent 
          ? `Document request sent successfully!` 
          : `Request created. Share this link: ${response.data.upload_url}`, {
          duration: 5000,
        });
      }
      
      setIsPayFormDialogOpen(false);
      setPayFormData({ 
        phase_id: '', 
        contractor_id: '', 
        form_types: ['lien_waiver'],
        attached_document_ids: [],
        notes: '',
        lien_waiver_amount: '',
        send_option: 'now',
        scheduled_for: ''
      });
      // Refresh lien waiver status
      loadLienWaiverStatus();
    } catch (error) {
      console.error('Form request error:', error.response?.data || error);
      if (error.response?.data?.detail?.includes('SendGrid')) {
        toast.warning('Email not configured. Form request created - share the upload link manually.');
      } else if (error.response?.data?.detail?.includes('72 hours')) {
        toast.error('Scheduling is limited to 72 hours in advance. Please choose an earlier time.');
      } else {
        toast.error('Failed to send form request: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleFileUpload = async (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    formData.append('category', category);

    try {
      await apiService.uploadDocument(formData);
      toast.success('Document uploaded successfully!');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleExportSpreadsheet = async (bankTemplateId = null) => {
    try {
      let response;
      if (bankTemplateId) {
        response = await apiService.exportProjectBankFormat(projectId, bankTemplateId);
        const bankTemplate = bankTemplates.find(b => b.id === bankTemplateId);
        const fileName = `${project.name}_${bankTemplate?.bank_name || 'bank'}_format.xlsx`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        toast.success(`Exported in ${bankTemplate?.bank_name} format!`);
      } else {
        response = await apiService.exportProject(projectId);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${project.name}_bid_sheet.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        toast.success('Spreadsheet exported!');
      }
      setIsExportDialogOpen(false);
    } catch (error) {
      toast.error('Failed to export spreadsheet');
    }
  };

  const handleDownloadDrawRecord = async (requestId, variant, drawNumber) => {
    try {
      let res, filename, mime;
      if (variant === 'package') {
        res = await apiService.downloadDrawPackage(requestId);
        filename = `Draw-${drawNumber || ''}-Package.zip`;
        mime = 'application/zip';
      } else if (variant === 'summary') {
        res = await apiService.downloadDrawSummaryPdf(requestId);
        filename = `Summary-Draw-${drawNumber || ''}.pdf`;
        mime = 'application/pdf';
      } else {
        res = await apiService.downloadDrawBankFormPdf(requestId);
        filename = `DrawRequest-Draw-${drawNumber || ''}.pdf`;
        mime = 'application/pdf';
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download. Please try again.');
      console.error(error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await apiService.exportProjectPDF(projectId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.name}_cost_breakdown.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported!');
      setIsExportDialogOpen(false);
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    }
  };

  const handleAiPrediction = async () => {
    setIsAiLoading(true);
    try {
      const response = await apiService.predictCosts(projectId);
      setAiPrediction(response.data.prediction);
      toast.success('AI prediction generated!');
    } catch (error) {
      toast.error('Failed to generate prediction');
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleUpdateProjectTimeline = async (e) => {
    e.preventDefault();
    try {
      // Calculate total budget from breakdown
      const purchasePriceHome = parseFloat(editProjectData.purchase_price_home) || 0;
      const purchasePriceLand = parseFloat(editProjectData.purchase_price_land) || 0;
      const acquisitionClosingCosts = parseFloat(editProjectData.acquisition_closing_costs) || 0;
      const constructionBudget = parseFloat(editProjectData.construction_budget) || 0;
      const contingencyBudget = parseFloat(editProjectData.contingency_budget) || 0;
      const insurance = parseFloat(editProjectData.insurance) || 0;
      const totalBudget = purchasePriceHome + purchasePriceLand + acquisitionClosingCosts + constructionBudget + contingencyBudget + insurance;
      
      await apiService.updateProject(projectId, {
        start_date: editProjectData.start_date,
        projected_completion_date: editProjectData.projected_completion_date,
        address: editProjectData.address || null,
        timezone: editProjectData.timezone || null,
        city: editProjectData.city || null,
        bank_contacts: editProjectData.bank_contacts || [],
        purchase_price_home: purchasePriceHome,
        purchase_price_land: purchasePriceLand,
        acquisition_closing_costs: acquisitionClosingCosts,
        construction_budget: constructionBudget,
        contingency_budget: contingencyBudget,
        insurance: insurance,
        total_budget: totalBudget,
        loan_number: editProjectData.loan_number,
        draw_account: editProjectData.draw_account,
        bank_name: editProjectData.bank_name,
        short_legal: editProjectData.short_legal,
        loan_interest_rate: parseFloat(editProjectData.loan_interest_rate) || null,
        loan_principal_amount: parseFloat(editProjectData.loan_principal_amount) || null,
        loan_period_months: parseInt(editProjectData.loan_period_months) || null,
        loan_available_for_draw: parseFloat(editProjectData.loan_available_for_draw) || null,
        interest_start_date: editProjectData.interest_start_date || null,
        loan_maturity_date: editProjectData.loan_maturity_date || null,
        interest_day_basis: parseInt(editProjectData.interest_day_basis, 10) || 360,
        borrower: editProjectData.borrower || null,
        borrower_entity: editProjectData.borrower_entity || null,
        builder_company_id: editProjectData.builder_company_id || null,
        builder_company_name: editProjectData.builder_company_name || null,
        builder_company_logo: editProjectData.builder_company_logo || null,
        // v2.5.5.11 — layout edit
        buildings: Math.max(1, parseInt(editProjectData.buildings, 10) || 1),
        units_per_building: (editProjectData.units_per_building || []).map(u => Math.max(1, parseInt(u, 10) || 1)),
        units: (editProjectData.units_per_building || []).reduce((s, u) => s + (parseInt(u, 10) || 0), 0),
        // v2.5.5.16 — total finished sqft (null = clear it)
        sqft: editProjectData.sqft === '' || editProjectData.sqft == null
          ? null
          : Math.max(0, parseFloat(editProjectData.sqft) || 0),
        // Milestone dates (Jun 2026) — optional, improve valuation/benchmarking
        listing_date: editProjectData.listing_date || null,
        actual_completion_date: editProjectData.actual_completion_date || null,
        sold_date: editProjectData.sold_date || null,
        permit_submitted_date: editProjectData.permit_submitted_date || null,
        permit_approved_date: editProjectData.permit_approved_date || null,
        survey_completion_date: editProjectData.survey_completion_date || null,
      });
      toast.success('Project updated!');
      setIsEditProjectDialogOpen(false);
      setShowTimezoneBanner(false); // Clear banner after saving
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update project');
      console.error(error);
    }
  };
  
  // Auto-detect timezone from address
  const handleDetectTimezone = async (address) => {
    if (!address || address.length < 5) return;
    
    setIsDetectingTimezone(true);
    try {
      const response = await apiService.detectTimezone(address);
      if (response.data.success && response.data.timezone) {
        setEditProjectData(prev => ({
          ...prev,
          timezone: response.data.timezone
        }));
        toast.success(`Detected timezone: ${response.data.abbreviation}`);
      }
    } catch (error) {
      console.error('Timezone detection failed:', error);
    } finally {
      setIsDetectingTimezone(false);
    }
  };
  
  // Quick set timezone from banner
  const handleQuickSetTimezone = async () => {
    if (!project?.address) {
      setIsEditProjectDialogOpen(true);
      return;
    }
    
    setIsDetectingTimezone(true);
    try {
      const response = await apiService.detectTimezone(project.address);
      if (response.data.success && response.data.timezone) {
        await apiService.updateProject(projectId, {
          timezone: response.data.timezone
        });
        toast.success(`Timezone set to ${response.data.abbreviation}`);
        setShowTimezoneBanner(false);
        loadProjectData();
      } else {
        // Open dialog for manual selection
        setIsEditProjectDialogOpen(true);
        toast.info('Could not auto-detect timezone. Please select manually.');
      }
    } catch (error) {
      console.error('Timezone detection failed:', error);
      setIsEditProjectDialogOpen(true);
    } finally {
      setIsDetectingTimezone(false);
    }
  };

  const handleCompleteProject = async () => {
    if (!window.confirm('Are you sure you want to mark this project as completed? You can reopen it later if needed.')) {
      return;
    }
    
    try {
      await apiService.updateProject(projectId, {
        status: 'completed'
      });
      toast.success('Project marked as completed!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to complete project');
      console.error(error);
    }
  };

  // ---- Start Project / Break Ground ----
  // Owner pressing Start fires the "we broke ground" cascade to all contractors
  // with their phases highlighted, embeds the owner's company logo, and includes
  // any job site rules configured on the project.
  const [isStartProjectDialogOpen, setIsStartProjectDialogOpen] = useState(false);
  const [startCustomMessage, setStartCustomMessage] = useState('');
  const [isStartingProject, setIsStartingProject] = useState(false);

  // ---- Push Start Date ----
  // Owner can push the planned start date (from the project page or from the
  // "Push start date" CTA in start-reminder emails via ?action=push-start).
  // The new endpoint shifts unstarted phases by the same delta AND notifies
  // every contractor on the project so the schedule cascade stays aligned.
  const [isPushStartDialogOpen, setIsPushStartDialogOpen] = useState(false);
  const [pushStartNewDate, setPushStartNewDate] = useState('');
  const [pushStartReason, setPushStartReason] = useState('');
  const [pushStartShiftPhases, setPushStartShiftPhases] = useState(true);
  const [pushStartNotifyContractors, setPushStartNotifyContractors] = useState(true);
  const [isPushingStart, setIsPushingStart] = useState(false);

  const contractorCount = (project?.construction_phases || [])
    .map((p) => p?.contractor_id)
    .filter(Boolean)
    .reduce((set, id) => set.add(id) && set, new Set()).size;

  const handleStartProject = async () => {
    if (!user?.id) return toast.error('Not logged in');
    setIsStartingProject(true);
    try {
      const res = await api.post(`/projects/${projectId}/start`, {
        user_id: user.id,
        custom_message: startCustomMessage.trim() || null,
      });
      const data = res.data || {};
      if (data.already_started) {
        toast.info('Project was already started — nothing to send.');
      } else {
        toast.success(
          `🏗️ Kickoff sent! ${data.emails_sent} contractor${data.emails_sent === 1 ? '' : 's'} notified.`
        );
      }
      setIsStartProjectDialogOpen(false);
      setStartCustomMessage('');
      loadProjectData();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to start project';
      toast.error(detail);
    } finally {
      setIsStartingProject(false);
    }
  };

  const handlePushStartDate = async () => {
    if (!pushStartNewDate) return toast.error('Pick a new start date');
    if (!user?.id) return toast.error('Not logged in');
    setIsPushingStart(true);
    try {
      const res = await api.post(`/projects/${projectId}/push-start-date`, {
        user_id: user.id,
        new_start_date: pushStartNewDate,
        reason: pushStartReason.trim() || null,
        shift_phases: pushStartShiftPhases,
        notify_contractors: pushStartNotifyContractors,
      });
      const data = res.data || {};
      const notified = data.contractors_notified || 0;
      const shifted = data.phases_shifted || 0;
      toast.success(
        notified > 0
          ? `📅 Start pushed. ${notified} contractor${notified === 1 ? '' : 's'} notified${shifted > 0 ? ` · ${shifted} phase${shifted === 1 ? '' : 's'} re-dated` : ''}.`
          : `📅 Start date pushed${shifted > 0 ? ` · ${shifted} phase${shifted === 1 ? '' : 's'} re-dated` : ''}.`
      );
      setIsPushStartDialogOpen(false);
      setPushStartNewDate('');
      setPushStartReason('');
      loadProjectData();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to push start date';
      toast.error(detail);
    } finally {
      setIsPushingStart(false);
    }
  };

  // Deep-link from start-reminder email: /project/{id}?action=push-start opens the
  // Push Start Date dialog with the current start_date pre-filled.
  useEffect(() => {
    if (searchParams.get('action') === 'push-start' && project && !isLoading) {
      if (project.project_started_at) {
        toast.info('This project has already started — no need to push the date.');
      } else if (isReadOnlyUser || isInvestor) {
        toast.error("You don't have permission to push the start date.");
      } else {
        const current = project.start_date ? String(project.start_date).slice(0, 10) : '';
        setPushStartNewDate(current);
        setIsPushStartDialogOpen(true);
      }
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, project, isLoading]);

  const handleReopenProject = async () => {
    try {
      await apiService.updateProject(projectId, {
        status: 'active'
      });
      toast.success('Project reopened!');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to reopen project');
      console.error(error);
    }
  };

  const loadPhasePayments = async (phaseId) => {
    try {
      const response = await apiService.getPhasePayments(phaseId);
      const paymentsData = Array.isArray(response.data) ? response.data : [];
      setPhasePayments(prev => ({
        ...prev,
        [phaseId]: paymentsData
      }));
    } catch (error) {
      console.error('Failed to load phase payments:', error);
      setPhasePayments(prev => ({
        ...prev,
        [phaseId]: []
      }));
    }
  };

  const loadReimbursementHistory = async () => {
    try {
      const response = await apiService.getReimbursementHistory(projectId);
      const historyData = Array.isArray(response.data) ? response.data : [];
      setReimbursementHistory(historyData);
      
      // Build set of all phase IDs that were previously requested
      const requestedSet = new Set();
      historyData.forEach(request => {
        if (request.phase_ids && Array.isArray(request.phase_ids)) {
          request.phase_ids.forEach(phaseId => requestedSet.add(phaseId));
        }
      });
      setPreviouslyRequestedPhases(requestedSet);
    } catch (error) {
      console.error('Failed to load reimbursement history:', error);
      setReimbursementHistory([]);
      setPreviouslyRequestedPhases(new Set());
    }
  };

  // Load submitted draw requests on mount so the downloadable-records card populates.
  useEffect(() => {
    if (projectId) loadReimbursementHistory();
  }, [projectId]);


  // v2.8 — shared loader for the New Draw Request dialog so it can be opened
  // from the trigger OR directly from the Budget & Progress "Request Draw" button.
  const prepareDrawRequestData = () => {
    (phases || []).filter(p => (
      p.status === 'completed'
      || p.is_paid
      || parseFloat(p.actual_cost) > 0
      || parseFloat(p.amount_drawn) > 0
      || parseFloat(p.supplier_amount_paid) > 0
      || parseFloat(p.invoice_amount) > 0
    )).forEach(phase => {
      loadPhasePayments(phase.id);
    });
    loadReimbursementHistory();
    apiService.getHoldingCosts(projectId).then(res => {
      setHoldingCostsForDraw(res.data?.holding_costs || []);
      setLoanInterestForDraw(res.data?.loan_interest || null);
    }).catch((err) => {
      console.error('Error loading holding costs:', err);
      setHoldingCostsForDraw([]);
      setLoanInterestForDraw(null);
    });
  };

  // One-click: jump to the loan area and open the draw request dialog.
  const openDrawRequest = () => {
    setActiveTab('loan');
    setIsReimbursementDialogOpen(true);
    prepareDrawRequestData();
  };

  // --- In-progress draw persistence ---------------------------------------
  // iOS Safari can discard/reload the app tab when a large PDF blob is opened
  // (Generate & Download) or rendered (Compare formats), which used to wipe an
  // in-progress draw and force the user to start over. We snapshot the current
  // selection to sessionStorage while the dialog is open and restore it if the
  // tab comes back reloaded. Cleared on explicit close, cancel or successful submit.
  const drawWipKey = `drawWip:${projectId}`;
  const clearDrawWip = () => { try { sessionStorage.removeItem(drawWipKey); } catch (e) { /* noop */ } };
  const drawRestoredRef = useRef(false);

  useEffect(() => {
    if (!projectId) return;
    // Persist while ANY step of the draw flow is open (selection, score gate, or
    // the preview/compare modal) so a silent iPad tab reload can drop the user
    // back on the EXACT step they were on — not at the beginning.
    const open = isReimbursementDialogOpen || isDrawPreviewOpen || isDrawScoreGateOpen;
    if (!open) return;
    const step = isDrawPreviewOpen ? 'preview' : (isDrawScoreGateOpen ? 'gate' : 'select');
    try {
      sessionStorage.setItem(drawWipKey, JSON.stringify({
        step,
        phases: selectedPhasesForReimbursement,
        expenses: selectedExpensesForReimbursement,
        holding: selectedHoldingCostsForReimbursement,
        amounts: customDrawAmounts,
        style: drawFormStyle,
      }));
    } catch (e) { /* quota / private mode — ignore */ }
  }, [projectId, isReimbursementDialogOpen, isDrawPreviewOpen, isDrawScoreGateOpen, selectedPhasesForReimbursement, selectedExpensesForReimbursement, selectedHoldingCostsForReimbursement, customDrawAmounts, drawFormStyle]);

  useEffect(() => {
    if (drawRestoredRef.current) return;
    if (!projectId || !project?.id) return;
    if (!phases || phases.length === 0) return;
    drawRestoredRef.current = true;
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(drawWipKey) || 'null'); } catch (e) { saved = null; }
    const hasSelection = saved && ((saved.phases?.length || 0) + (saved.expenses?.length || 0) + (saved.holding?.length || 0)) > 0;
    if (!hasSelection) return;
    setSelectedPhasesForReimbursement(saved.phases || []);
    setSelectedExpensesForReimbursement(saved.expenses || []);
    setSelectedHoldingCostsForReimbursement(saved.holding || []);
    setCustomDrawAmounts(saved.amounts || {});
    if (saved.style) setDrawFormStyle(saved.style);
    setActiveTab('loan');
    prepareDrawRequestData();
    // Reopen the exact step so a reload never means "start over".
    if (saved.step === 'preview') {
      setIsDrawPreviewOpen(true);
    } else {
      setIsReimbursementDialogOpen(true);
    }
    toast.info('Resumed your draw right where you left off — nothing was lost.');
  }, [projectId, project?.id, phases]);


  const loadProjectExpenses = async () => {
    try {
      const response = await apiService.getProjectExpenses(projectId);
      setProjectExpenses(response.data);
    } catch (error) {
      console.error('Failed to load project expenses:', error);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      await apiService.createProjectExpense({
        project_id: projectId,
        ...newExpense,
        estimated_amount: newExpense.estimated_amount ? parseFloat(newExpense.estimated_amount) : null,
        amount: newExpense.amount ? parseFloat(newExpense.amount) : 0, // Default to 0 if not provided
        date_paid: newExpense.date_paid ? new Date(newExpense.date_paid).toISOString() : null,
        is_paid: newExpense.amount ? newExpense.is_paid : false, // Not paid if no amount entered
      });
      toast.success('Expense added!');
      setIsExpenseDialogOpen(false);
      setNewExpense({
        expense_type: 'permit',
        description: '',
        vendor_name: '',
        assigned_contractor_id: '',
        estimated_amount: '',
        amount: '',
        is_paid: false,
        date_paid: new Date().toISOString().split('T')[0],
        is_reimbursable: true,
      });
      loadProjectExpenses();
      loadProjectData();
    } catch (error) {
      toast.error('Failed to add expense');
      console.error(error);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await apiService.deleteProjectExpense(expenseId);
      toast.success('Expense deleted');
      loadProjectExpenses();
      loadProjectData();
    } catch (error) {
      toast.error('Failed to delete expense');
      console.error(error);
    }
  };

  // Edit expense handler
  const handleEditExpense = async (e) => {
    e.preventDefault();
    if (!editExpense) return;
    try {
      await apiService.updateProjectExpense(editExpense.id, {
        expense_type: editExpense.expense_type,
        description: editExpense.description,
        vendor_name: editExpense.vendor_name,
        estimated_amount: editExpense.estimated_amount ? parseFloat(editExpense.estimated_amount) : null,
        amount: parseFloat(editExpense.amount) || 0,
        is_paid: editExpense.is_paid,
        is_running_tab: editExpense.is_running_tab,
        amount_drawn: parseFloat(editExpense.amount_drawn) || 0,
        is_drawn: (parseFloat(editExpense.amount_drawn) || 0) >= (parseFloat(editExpense.amount) || 0),
      });
      toast.success('Expense updated!');
      setIsEditExpenseDialogOpen(false);
      setEditExpense(null);
      loadProjectExpenses();
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update expense');
      console.error(error);
    }
  };

  // Add payment to running tab expense
  const handleAddExpensePayment = async (e) => {
    e.preventDefault();
    if (!selectedExpenseForPayment) return;
    try {
      await apiService.addExpensePayment(selectedExpenseForPayment.id, {
        amount: parseFloat(newExpensePayment.amount),
        date: newExpensePayment.date,
        description: newExpensePayment.description,
      });
      toast.success('Payment added!');
      setIsExpensePaymentDialogOpen(false);
      setSelectedExpenseForPayment(null);
      setNewExpensePayment({ amount: '', date: new Date().toISOString().split('T')[0], description: '' });
      loadProjectExpenses();
      loadProjectData();
    } catch (error) {
      toast.error('Failed to add payment');
      console.error(error);
    }
  };

  // Delete payment from running tab
  const handleDeleteExpensePayment = async (expenseId, paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await apiService.deleteExpensePayment(expenseId, paymentId);
      toast.success('Payment deleted');
      loadProjectExpenses();
      loadProjectData();
    } catch (error) {
      toast.error('Failed to delete payment');
      console.error(error);
    }
  };

  const handleCleanupCategories = async () => {
    if (!window.confirm('This will standardize all phase categories. Continue?')) return;
    
    try {
      let updatedCount = 0;
      
      for (const phase of phases) {
        const normalizedCategory = normalizeCategory(phase.category);
        
        // Only update if the category changed after normalization
        if (phase.category !== normalizedCategory) {
          await apiService.updatePhase(phase.id, {
            category: normalizedCategory
          });
          updatedCount++;
        }
      }
      
      if (updatedCount > 0) {
        toast.success(`${updatedCount} phase categories standardized!`);
        loadProjectData();
      } else {
        toast.info('All categories are already standardized');
      }
    } catch (error) {
      toast.error('Failed to cleanup categories');
      console.error(error);
    }
  };

  const handleAddPhasePayment = async (e) => {
    e.preventDefault();
    if (!currentPhaseForPayment) return;

    const phaseId = typeof currentPhaseForPayment === 'string' 
      ? currentPhaseForPayment 
      : currentPhaseForPayment.id;

    try {
      await apiService.createPhasePayment({
        phase_id: phaseId,
        amount: parseFloat(newPayment.amount),
        payment_date: newPayment.payment_date + 'T12:00:00Z',  // Add noon UTC to avoid timezone shifting
        description: newPayment.description,
        payment_method: newPayment.payment_method,
      });

      toast.success('Payment recorded!');
      setIsAddPaymentDialogOpen(false);
      setNewPayment({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: 'check',
      });
      loadPhasePayments(phaseId);
      loadProjectData(); // Refresh to update totals
    } catch (error) {
      toast.error('Failed to record payment');
      console.error(error);
    }
  };

  const handleDeletePhasePayment = async (paymentId, phaseId) => {
    if (!window.confirm('Delete this payment record?')) return;

    try {
      await apiService.deletePhasePayment(paymentId);
      toast.success('Payment deleted');
      loadPhasePayments(phaseId);
    } catch (error) {
      toast.error('Failed to delete payment');
      console.error(error);
    }
  };

  const handleGenerateReimbursementRequest = async (options = {}) => {
    if (selectedPhasesForReimbursement.length === 0 && selectedExpensesForReimbursement.length === 0 && selectedHoldingCostsForReimbursement.length === 0) {
      toast.error('Please select at least one phase, expense, or holding cost');
      return;
    }

    // Validation: Check if project has required loan information
    if (!project.loan_number || !project.draw_account || !project.short_legal) {
      toast.error('Please add loan information (Loan #, Draw Account, Legal Description) via Edit Timeline before requesting reimbursement');
      return;
    }

    setIsGeneratingDraw(true);
    try {
      toast.loading('Generating draw request documents...');
      
      // Calculate total draw amount AND build phase_amounts for backend
      // Using the remaining drawable amount (paid - already drawn)
      let totalDrawAmount = 0;
      const phaseAmounts = {}; // Map of phase_id -> drawable amount
      
      console.log('=== GENERATING DRAW REQUEST ===');
      console.log('Selected phase IDs:', selectedPhasesForReimbursement);
      
      selectedPhasesForReimbursement.forEach(phaseId => {
        const phase = phases.find(p => p.id === phaseId);
        if (phase) {
          // Calculate paid amount using partial payments
          const partialPaymentsTotal = phasePayments[phase.id] 
            ? phasePayments[phase.id].reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) 
            : 0;
          
          let paidAmount = 0;
          if (phase.phase_type === 'material') {
            const supplierPaid = parseFloat(phase.supplier_amount_paid) || 0;
            const actualCost = parseFloat(phase.actual_cost) || 0;
            paidAmount = Math.max(partialPaymentsTotal, supplierPaid, actualCost);
          } else {
            const actualCost = parseFloat(phase.actual_cost) || 0;
            paidAmount = Math.max(partialPaymentsTotal, actualCost);
          }
          
          // v2.5.15 — Submit uses the same totalCost − alreadyDrawn math
          // as the selector + preview so the bank packet matches what the
          // owner sees on screen.
          const alreadyDrawn = parseFloat(phase.amount_drawn) || 0;
          const totalCost = Math.max(
            parseFloat(phase.invoice_amount) || 0,
            parseFloat(phase.actual_cost) || 0,
            parseFloat(phase.estimated_cost) || 0,
          ) || paidAmount;
          const drawableAmount = Math.max(0, totalCost - alreadyDrawn);
          const requested = getLineDrawAmount(phaseId, drawableAmount);
          totalDrawAmount += requested;
          
          // Store for backend (partial-aware)
          phaseAmounts[phaseId] = requested;
          
          console.log(`Phase: ${phase.name} (${phaseId})`);
          console.log(`  partialPayments: $${partialPaymentsTotal}, supplierPaid: $${phase.supplier_amount_paid || 0}, actualCost: $${phase.actual_cost || 0}`);
          console.log(`  paidAmount: $${paidAmount}, alreadyDrawn: $${alreadyDrawn}, drawableAmount: $${drawableAmount}`);
        }
      });
      
      const expenseAmounts = {}; // Map of expense_id -> drawable amount
      selectedExpensesForReimbursement.forEach(expenseId => {
        const expense = projectExpenses.find(e => e.id === expenseId);
        if (expense) {
          const paidAmount = parseFloat(expense.amount) || 0;
          const alreadyDrawn = parseFloat(expense.amount_drawn) || 0;
          const drawableAmount = Math.max(0, paidAmount - alreadyDrawn);
          totalDrawAmount += drawableAmount;
          expenseAmounts[expenseId] = drawableAmount;
        }
      });
      
      console.log('=== SENDING TO BACKEND ===');
      console.log('phaseAmounts:', JSON.stringify(phaseAmounts, null, 2));
      console.log('expenseAmounts:', JSON.stringify(expenseAmounts, null, 2));
      console.log('signDrawDocument:', signDrawDocument);
      console.log('downloadExcelOnly:', downloadExcelOnly);
      
      // Option A — submit the DrawReady draw form straight to the bank's
      // construction loan department (via DrawReady domain, Reply-To = builder).
      if (options.emailToBank) {
        const res = await apiService.submitDrawToBank({
          project_id: projectId,
          phase_ids: selectedPhasesForReimbursement,
          phase_amounts: phaseAmounts,
          expense_ids: selectedExpensesForReimbursement,
          expense_amounts: expenseAmounts,
          holding_cost_ids: selectedHoldingCostsForReimbursement,
          receipt_ids_by_phase: selectedReceiptsForDraw,
          sign_document: signDrawDocument && userSignature ? true : false,
          user_id: user?.id,
          doc_format: 'pdf',
          email_to_bank: true,
          reply_to: options.replyTo || undefined,
          test_email: options.testEmail || undefined,
        });
        toast.dismiss();
        toast.success(res.data?.is_test
          ? `Test draw emailed to ${res.data.sent_to} — check your inbox`
          : `Draw #${res.data.draw_number || ''} sent to ${res.data.sent_to} · replies go to ${res.data.reply_to || 'your email'}`);
        if (!res.data?.is_test) { loadSubmittedDraws(); }
        // A real submission is the point a draw is "recorded". Decrement the
        // available-to-draw balance now (not on preview/view). Skipped for tests.
        if (!res.data?.is_test) {
          try {
            if (project.loan_available_for_draw && totalDrawAmount > 0) {
              const newAvailableForDraw = Math.max(0, project.loan_available_for_draw - totalDrawAmount);
              await apiService.updateProject(projectId, { loan_available_for_draw: newAvailableForDraw });
            }
          } catch (balErr) {
            console.warn('Draw submitted but failed to update loan balance:', balErr);
          }
        }
        clearDrawWip();
        setIsDrawPreviewOpen(false);
        setIsReimbursementDialogOpen(false);
        setSelectedPhasesForReimbursement([]);
        setSelectedExpensesForReimbursement([]);
        setSelectedHoldingCostsForReimbursement([]);
        setSelectedReceiptsForDraw({});
        setPhaseReceiptsForDraw({});
        if (!res.data?.is_test) {
          try { await loadProjectData(); } catch (e) { /* ignore refresh error */ }
        }
        return;
      }

      // VIEW MY DRAW — this is a preview only. It produces the single PDF form
      // and opens it for viewing; it does NOT record a draw, decrement the
      // balance, or tear down the in-progress draw. The draw is only recorded
      // when it is successfully submitted to the bank (see the email branch).
      // Open the viewer tab NOW, inside the user's tap, so iPad Safari doesn't
      // block or background it (a tab opened after the await gets suppressed).
      const viewWin = window.open('', '_blank');

      const response = await apiService.generateReimbursementRequest({
        project_id: projectId,
        phase_ids: selectedPhasesForReimbursement,
        phase_amounts: phaseAmounts,
        expense_ids: selectedExpensesForReimbursement,
        expense_amounts: expenseAmounts,
        holding_cost_ids: selectedHoldingCostsForReimbursement,
        receipt_ids_by_phase: selectedReceiptsForDraw,
        sign_document: signDrawDocument && userSignature ? true : false,
        user_id: user?.id,
        doc_format: 'pdf',
        form_style: drawFormStyle,
        single_file: true,  // always the single form so it can be opened for viewing
      });

      // Point the already-open tab at the generated PDF. The app tab is never
      // navigated, so the draw stays exactly where it was.
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (viewWin) {
        viewWin.location.href = url;
      } else {
        // Popup was blocked — fall back to a direct-tap anchor link.
        const a = document.createElement('a');
        a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Delay revoke so the new tab has time to load the blob on iOS Safari.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);

      toast.dismiss();
      toast.success('Draw PDF opened in a new tab. Your draw here is untouched — pick up right where you left off.');
      // Intentionally NO teardown: keep the preview modal open with the
      // selection intact so the user can review, view again, or submit to bank.
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to open your draw');
      console.error(error);
    } finally {
      setIsGeneratingDraw(false);
    }
  };

  // Per-line partial-draw helpers. Users can draw LESS than the full drawable
  // amount on a line item; the value is clamped to [0, drawable].
  const setLineDrawAmount = (id, value) => {
    setCustomDrawAmounts(prev => ({ ...prev, [id]: value }));
  };
  const getLineDrawAmount = (id, drawable) => {
    const raw = customDrawAmounts[id];
    if (raw === undefined || raw === '') return drawable;
    const n = parseFloat(raw);
    if (isNaN(n) || n < 0) return 0;
    return Math.min(n, drawable);
  };

  // Compute drawable amounts for the current selection (shared by compare preview)
  const computeDrawAmounts = () => {
    let totalDrawAmount = 0;
    const phaseAmounts = {};
    selectedPhasesForReimbursement.forEach(phaseId => {
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) return;
      const partialPaymentsTotal = phasePayments[phase.id]
        ? phasePayments[phase.id].reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        : 0;
      let paidAmount = 0;
      if (phase.phase_type === 'material') {
        const supplierPaid = parseFloat(phase.supplier_amount_paid) || 0;
        const actualCost = parseFloat(phase.actual_cost) || 0;
        paidAmount = Math.max(partialPaymentsTotal, supplierPaid, actualCost);
      } else {
        const actualCost = parseFloat(phase.actual_cost) || 0;
        paidAmount = Math.max(partialPaymentsTotal, actualCost);
      }
      const alreadyDrawn = parseFloat(phase.amount_drawn) || 0;
      const totalCost = Math.max(
        parseFloat(phase.invoice_amount) || 0,
        parseFloat(phase.actual_cost) || 0,
        parseFloat(phase.estimated_cost) || 0,
      ) || paidAmount;
      const drawableAmount = Math.max(0, totalCost - alreadyDrawn);
      const requested = getLineDrawAmount(phaseId, drawableAmount);
      totalDrawAmount += requested;
      phaseAmounts[phaseId] = requested;
    });
    const expenseAmounts = {};
    selectedExpensesForReimbursement.forEach(expenseId => {
      const expense = projectExpenses.find(e => e.id === expenseId);
      if (!expense) return;
      const paidAmount = parseFloat(expense.amount) || 0;
      const alreadyDrawn = parseFloat(expense.amount_drawn) || 0;
      const drawableAmount = Math.max(0, paidAmount - alreadyDrawn);
      totalDrawAmount += drawableAmount;
      expenseAmounts[expenseId] = drawableAmount;
    });
    return { totalDrawAmount, phaseAmounts, expenseAmounts };
  };

  // Generate BOTH formats and show them side-by-side (lender demo + normal flow)
  const handleCompareFormats = async () => {
    if (!project.loan_number || !project.draw_account || !project.short_legal) {
      toast.error('Add loan info (Loan #, Draw Account, Legal Description) via Edit Loan Details first');
      return;
    }
    setIsComparing(true);
    try {
      const { phaseAmounts, expenseAmounts } = computeDrawAmounts();
      const base = {
        project_id: projectId,
        phase_ids: selectedPhasesForReimbursement,
        phase_amounts: phaseAmounts,
        expense_ids: selectedExpensesForReimbursement,
        expense_amounts: expenseAmounts,
        holding_cost_ids: selectedHoldingCostsForReimbursement,
        receipt_ids_by_phase: selectedReceiptsForDraw,
        sign_document: signDrawDocument && userSignature ? true : false,
        user_id: user?.id,
        doc_format: 'pdf',
        single_file: true,
      };
      const [hillsRes, drRes] = await Promise.all([
        apiService.generateReimbursementRequest({ ...base, form_style: 'hills' }),
        apiService.generateReimbursementRequest({ ...base, form_style: 'drawready' }),
      ]);
      const hillsUrl = window.URL.createObjectURL(new Blob([hillsRes.data], { type: 'application/pdf' }));
      const drUrl = window.URL.createObjectURL(new Blob([drRes.data], { type: 'application/pdf' }));
      setCompareUrls({ hills: hillsUrl, drawready: drUrl });
    } catch (error) {
      toast.error('Failed to build the format comparison');
      console.error(error);
    } finally {
      setIsComparing(false);
    }
  };
  
  // Prepare preview data for draw request
  const getDrawPreviewData = () => {
    const selectedItems = [];
    
    // Get phase items
    selectedPhasesForReimbursement.forEach(phaseId => {
      const phase = phases.find(p => p.id === phaseId);
      if (phase) {
        const partialPaymentsTotal = phasePayments[phase.id] 
          ? phasePayments[phase.id].reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) 
          : 0;
        
        let paidAmount = 0;
        if (phase.phase_type === 'material') {
          const supplierPaid = parseFloat(phase.supplier_amount_paid) || 0;
          const actualCost = parseFloat(phase.actual_cost) || 0;
          paidAmount = Math.max(partialPaymentsTotal, supplierPaid, actualCost);
        } else {
          const actualCost = parseFloat(phase.actual_cost) || 0;
          paidAmount = Math.max(partialPaymentsTotal, actualCost);
        }
        
        const alreadyDrawn = parseFloat(phase.amount_drawn) || 0;
        // v2.5.15 — drawable now covers both reimbursement and pay-the-sub
        // modes: totalCost − alreadyDrawn. Mirrors the filter in the
        // selector UI; without this, a phase appearing in the selector
        // because of unpaid balance would still preview $0 here.
        const totalCost = Math.max(
          parseFloat(phase.invoice_amount) || 0,
          parseFloat(phase.actual_cost) || 0,
          parseFloat(phase.estimated_cost) || 0,
        ) || paidAmount;
        const drawableAmount = Math.max(0, totalCost - alreadyDrawn);
        
        // Parse bank category
        const bankCategory = phase.bank_category || '';
        const categoryParts = bankCategory.split(' ');
        const categoryCode = categoryParts[0] || '';
        const categoryName = categoryParts.slice(1).join(' ') || phase.category || 'General';
        
        selectedItems.push({
          id: phase.id,
          vendor: phase.phase_type === 'material' ? phase.supplier_vendor : (phase.contractor_name || 'Contractor'),
          contractor: phase.contractor_name,
          categoryCode,
          categoryName,
          amount: getLineDrawAmount(phase.id, drawableAmount)
        });
      }
    });
    
    // Get holding costs
    const selectedHoldingCostsData = holdingCostsForDraw
      .filter(hc => selectedHoldingCostsForReimbursement.includes(hc.id))
      .map(hc => {
        const bankCategory = hc.bank_category || '';
        const categoryParts = bankCategory.split(' ');
        const categoryCode = categoryParts[0] || '805';
        const categoryName = categoryParts.slice(1).join(' ') || hc.category || 'Holding Cost';
        
        return {
          id: hc.id,
          vendor: hc.vendor || hc.name,
          name: hc.name,
          categoryCode,
          categoryName,
          amount: hc.actual_cost || hc.estimated_cost || 0
        };
      });
    
    return { selectedItems, holdingCosts: selectedHoldingCostsData };
  };
  
  // Open preview before generating
  // v3.0 — draw-scoped readiness gate. After item selection, show the Draw Score
  // for just these items before the preview/signature step.
  const handleReviewDrawScore = () => {
    if (selectedPhasesForReimbursement.length === 0 && selectedExpensesForReimbursement.length === 0 && selectedHoldingCostsForReimbursement.length === 0) {
      toast.error('Please select at least one phase, expense, or holding cost');
      return;
    }
    if (!project.loan_number || !project.draw_account || !project.short_legal) {
      toast.error('Please add loan information (Loan #, Draw Account, Legal Description) via Edit Project first');
      return;
    }
    setIsReimbursementDialogOpen(false);
    setIsDrawScoreGateOpen(true);
  };

  const handleOpenDrawPreview = () => {
    if (selectedPhasesForReimbursement.length === 0 && selectedExpensesForReimbursement.length === 0 && selectedHoldingCostsForReimbursement.length === 0) {
      toast.error('Please select at least one phase, expense, or holding cost');
      return;
    }
    
    if (!project.loan_number || !project.draw_account || !project.short_legal) {
      toast.error('Please add loan information (Loan #, Draw Account, Legal Description) via Edit Project first');
      return;
    }
    
    setIsDrawPreviewOpen(true);
  };

  const handleConfirmDeposit = async () => {
    if (!selectedReimbursementForDeposit) return;
    
    try {
      toast.loading('Confirming deposit...');
      await apiService.confirmReimbursementDeposit(selectedReimbursementForDeposit.id, {
        deposit_date: depositDate
      });
      toast.dismiss();
      toast.success('Deposit confirmed! Draw recorded and interest will accrue from this date.');
      setIsConfirmDepositDialogOpen(false);
      setSelectedReimbursementForDeposit(null);
      setDepositDate(new Date().toISOString().split('T')[0]);
      loadReimbursementHistory();
      loadProjectData();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to confirm deposit');
      console.error(error);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    try {
      await apiService.createContractorPayment({
        ...paymentData,
        project_id: projectId,
        amount: parseFloat(paymentData.amount),
      });
      toast.success('Payment recorded successfully!');
      setIsPaymentDialogOpen(false);
      setPaymentData({
        contractor_id: '',
        phase_id: '',
        amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'check',
        notes: '',
      });
      loadProjectData();
    } catch (error) {
      toast.error('Failed to record payment');
      console.error(error);
    }
  };

  const handleGenerate1099Report = async () => {
    if (!selected1099Contractor || !selected1099Year) {
      toast.error('Please select a contractor and year');
      return;
    }
    
    try {
      const response = await apiService.get1099Report(selected1099Contractor, selected1099Year);
      setReport1099Data(response.data);
      toast.success('1099 report generated!');
    } catch (error) {
      toast.error('Failed to generate 1099 report');
      console.error(error);
    }
  };

  const handleCalculateFinancialAnalysis = async () => {
    setIsCalculatingAnalysis(true);
    try {
      const requestData = {
        analysis_type: financialAnalysisTab,
        ...financialAnalysisInputs,
      };
      
      const response = await apiService.calculateFinancialAnalysis(projectId, requestData);
      setFinancialAnalysisResults(response.data);
      toast.success(`${financialAnalysisTab === 'sell' ? 'Build-to-Sell' : 'Build-to-Rent'} analysis complete!`);
    } catch (error) {
      toast.error('Failed to calculate analysis');
      console.error(error);
    } finally {
      setIsCalculatingAnalysis(false);
    }
  };

  // Rate History handlers
  const handleAddRateEntry = async (e) => {
    e.preventDefault();
    try {
      await apiService.addRateHistory(projectId, {
        project_id: projectId,
        effective_date: newRateEntry.effective_date,
        end_date: newRateEntry.end_date || null,  // null means ongoing/current rate
        interest_rate: parseFloat(newRateEntry.interest_rate),
        notes: newRateEntry.notes || null,
      });
      toast.success('Rate change recorded!');
      setIsAddRateDialogOpen(false);
      setNewRateEntry({ effective_date: '', end_date: '', interest_rate: '', notes: '' });
      loadProjectData();
    } catch (error) {
      toast.error('Failed to add rate change');
      console.error(error);
    }
  };

  const handleDeleteRateEntry = async (entryId) => {
    if (!window.confirm('Delete this rate entry?')) return;
    try {
      await apiService.deleteRateHistory(projectId, entryId);
      toast.success('Rate entry deleted');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to delete rate entry');
      console.error(error);
    }
  };

  const handleEditRateEntry = (entry) => {
    setEditingRateEntry({
      ...entry,
      effective_date: entry.effective_date,
      end_date: entry.end_date || '',  // Empty string if null (ongoing rate)
      interest_rate: entry.interest_rate,
      notes: entry.notes || ''
    });
    setIsEditRateDialogOpen(true);
  };

  const handleUpdateRateEntry = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateRateHistory(projectId, editingRateEntry.id, {
        effective_date: editingRateEntry.effective_date,
        end_date: editingRateEntry.end_date || null,  // null means ongoing/current rate
        interest_rate: parseFloat(editingRateEntry.interest_rate),
        notes: editingRateEntry.notes || null,
      });
      toast.success('Rate entry updated!');
      setIsEditRateDialogOpen(false);
      setEditingRateEntry(null);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update rate entry');
      console.error(error);
    }
  };

  // Submitted-to-bank audit log
  const loadSubmittedDraws = async () => {
    try {
      const r = await apiService.getSubmittedDraws(projectId, user?.id);
      setSubmittedDraws(r.data?.submitted_draws || []);
    } catch (e) { /* non-fatal */ }
  };
  const viewSentDrawPdf = (fileId) => {
    if (!fileId) { toast.error('No archived PDF for this draw'); return; }
    const token = localStorage.getItem('dr_session_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/files/${fileId}${token ? `?token=${encodeURIComponent(token)}` : ''}`, '_blank');
  };

  // Draw History handlers
  const handleAddDrawEntry = async (e) => {
    e.preventDefault();
    try {
      // Record the draw
      await apiService.addDrawHistory(projectId, {
        project_id: projectId,
        draw_date: newDrawEntry.draw_date,
        draw_amount: parseFloat(newDrawEntry.draw_amount),
        description: newDrawEntry.description || null,
      });
      
      // If rate changed on this date, add rate history entry
      if (newDrawEntry.rate_changed && newDrawEntry.new_rate) {
        const currentRates = project.rate_history || [];
        const newRateEntry = {
          rate: parseFloat(newDrawEntry.new_rate),
          effective_date: newDrawEntry.draw_date,
          added_at: new Date().toISOString()
        };
        await apiService.updateProject(projectId, {
          rate_history: [...currentRates, newRateEntry]
        });
        toast.success('Draw recorded with new rate!');
      } else {
        toast.success('Draw recorded!');
      }
      
      setIsAddDrawDialogOpen(false);
      setNewDrawEntry({ draw_date: '', draw_amount: '', description: '', rate_changed: false, new_rate: '' });
      loadProjectData();
    } catch (error) {
      toast.error('Failed to add draw');
      console.error(error);
    }
  };

  const handleDeleteDrawEntry = async (drawId) => {
    if (!window.confirm('Delete this draw entry? This will recalculate all cumulative balances.')) return;
    try {
      await apiService.deleteDrawHistory(projectId, drawId);
      toast.success('Draw entry deleted');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to delete draw entry');
      console.error(error);
    }
  };

  const handleEditDrawEntry = (entry) => {
    setEditingDrawEntry({
      ...entry,
      draw_date: entry.draw_date ? entry.draw_date.split('T')[0] : '',
      draw_amount: entry.draw_amount,
      description: entry.description || ''
    });
    setIsEditDrawDialogOpen(true);
  };

  const handleEditFundedDraw = (row) => {
    setEditingFundedDraw({
      id: row.id,
      draw_number: row.draw_number ?? '',
      request_date: row.date ? String(row.date).split('T')[0] : '',
    });
    setIsEditFundedDrawOpen(true);
  };

  const handleUpdateFundedDraw = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateReimbursementRequest(editingFundedDraw.id, {
        draw_number: editingFundedDraw.draw_number === '' ? null : parseInt(editingFundedDraw.draw_number, 10),
        request_date: editingFundedDraw.request_date || null,
      });
      toast.success('Draw updated!');
      setIsEditFundedDrawOpen(false);
      setEditingFundedDraw(null);
      loadReimbursementHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update draw');
      console.error(error);
    }
  };

  const handleUpdateDrawEntry = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateDrawHistory(projectId, editingDrawEntry.id, {
        draw_date: editingDrawEntry.draw_date,
        draw_amount: parseFloat(editingDrawEntry.draw_amount),
        description: editingDrawEntry.description || null,
      });
      toast.success('Draw entry updated!');
      setIsEditDrawDialogOpen(false);
      setEditingDrawEntry(null);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update draw entry');
      console.error(error);
    }
  };

  // Helper function to calculate phase status based on dates
  const getPhaseStatus = (phase) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check start_date first
    if (phase.start_date) {
      const startDate = new Date(phase.start_date);
      startDate.setHours(0, 0, 0, 0);
      
      // If start date is in the future, always show as scheduled
      // (can't be in progress or completed if work hasn't started yet)
      if (startDate > today) {
        return 'scheduled';
      }
      
      // Start date has passed - check if completed
      if (phase.end_date) {
        return 'completed';
      }
      
      // Started but not finished
      return 'in_progress';
    }
    
    // No start date set - check if somehow has end_date
    if (phase.end_date) {
      return 'completed';
    }
    
    // No dates set - treat as scheduled
    return 'scheduled';
  };

  // Calculate task stats for this project and group phases by status
  const getProjectTaskStats = () => {
    let completed = 0;
    let in_progress = 0;
    let scheduled = 0;
    let paid = 0;
    
    const completedPhases = [];
    const inProgressPhases = [];
    const scheduledPhases = [];
    
    phases.forEach(phase => {
      const derivedStatus = getPhaseStatus(phase);
      
      if (phase.is_paid) paid++;
      
      if (derivedStatus === 'completed' || phase.status === 'completed') {
        completed++;
        completedPhases.push(phase);
      } else if (derivedStatus === 'in_progress' || phase.status === 'in_progress') {
        in_progress++;
        inProgressPhases.push(phase);
      } else {
        scheduled++;
        scheduledPhases.push(phase);
      }
    });
    
    // Add paid soft costs to completed items
    const paidSoftCosts = projectExpenses.filter(e => e.is_paid);
    const completedWithSoftCosts = [
      ...completedPhases,
      ...paidSoftCosts.map(e => ({
        id: e.id,
        name: e.description || e.category,
        estimated_cost: e.amount || e.estimated_amount || 0,
        phase_type: 'soft_cost',
        is_paid: true
      }))
    ];
    
    return {
      completed: completed + paidSoftCosts.length,
      in_progress,
      scheduled,
      paid: paid + paidSoftCosts.length,
      total: phases.length + paidSoftCosts.length,
      completedPhases: completedWithSoftCosts,
      inProgressPhases,
      scheduledPhases
    };
  };

  const taskStats = getProjectTaskStats();
  
  const toggleStatusSection = (section) => {
    setExpandedStatusSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const calculateBudgetStatus = () => {
    // Calculate initial budget from project budget fields
    const purchasePriceHome = project?.purchase_price_home || 0;
    const purchasePriceLand = project?.purchase_price_land || 0;
    const constructionBudget = project?.construction_budget || 0;
    const contingencyBudget = project?.contingency_budget || 0;
    
    // For existing projects created before budget breakdown, use total_budget
    // For new projects with budget breakdown, calculate from components
    const hasNewBudgetBreakdown = purchasePriceHome > 0 || purchasePriceLand > 0 || constructionBudget > 0;
    const initialBudget = hasNewBudgetBreakdown 
      ? purchasePriceHome + purchasePriceLand + constructionBudget + contingencyBudget
      : (project?.total_budget || 0);
    
    // Calculate projected spend (sum of all estimated costs + estimated soft costs)
    const projectedSpend = phases.reduce((sum, p) => sum + (p.estimated_cost || 0), 0) +
                          projectExpenses.reduce((sum, e) => sum + (e.estimated_amount || 0), 0);
    
    // Calculate actual spend (total paid including partial payments)
    // This should match the "Total Paid" column in the phases table
    let actualSpend = 0;
    let phaseSpend = 0;
    let softCostSpend = 0;
    
    // Add phase costs including partial payments
    phases.forEach(p => {
      // Check for partial payments first
      const partialPaymentsTotal = phasePayments[p.id] 
        ? phasePayments[p.id].reduce((sum, pay) => sum + parseFloat(pay.amount || 0), 0) 
        : 0;
      
      // v2.4 fix: actual_cost is what the work COST, not what was paid.
      // Only count it as "paid" if either:
      //   (a) there's an explicit payment record, OR
      //   (b) the legacy is_paid / supplier_paid flag is true (pre-v2.4 quick mark)
      // Otherwise a phase with estimated/incurred actual_cost but no payment
      // logged would falsely inflate Total Paid (e.g., Ellis Row showing $11K
      // paid before the project even started).
      let paidAmount = 0;
      if (partialPaymentsTotal > 0) {
        paidAmount = partialPaymentsTotal;
      } else if (p.is_paid || p.supplier_paid) {
        paidAmount = parseFloat(p.actual_cost || 0);
      }
      if (paidAmount > 0) {
        phaseSpend += paidAmount;
        actualSpend += paidAmount;
      }
    });
    
    // Add paid soft costs to actual spend
    const paidSoftCosts = projectExpenses
      .filter(e => e.is_paid)
      .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    softCostSpend = paidSoftCosts;
    actualSpend += paidSoftCosts;
    
    // Debug logging
    console.log('=== ACTUAL SPEND BREAKDOWN ===');
    console.log('Phase Spend:', phaseSpend);
    console.log('Paid Soft Costs:', softCostSpend);
    console.log('Total Actual Spend:', actualSpend);
    console.log('Phase Payments Available:', Object.keys(phasePayments).length);
    
    // Variance calculations
    const projectedVsInitial = projectedSpend - initialBudget;
    const actualVsInitial = actualSpend - initialBudget;
    const remainingWork = projectedSpend - actualSpend;
    
    // Legacy calculations for compatibility
    const mainBudget = project ? project.total_budget : 0;
    const mainSpent = actualSpend;
    const overBudget = mainSpent > mainBudget;
    const overAmount = overBudget ? mainSpent - mainBudget : 0;
    const contingencyUsed = Math.min(overAmount, contingencyBudget);
    const contingencyRemaining = contingencyBudget - contingencyUsed;
    const percentage = mainBudget > 0 ? (mainSpent / mainBudget) * 100 : 0;
    const remaining = mainBudget - mainSpent;
    
    return {
      // New budget tracking
      initialBudget,
      projectedSpend,
      actualSpend,
      projectedVsInitial,
      actualVsInitial,
      remainingWork,
      
      // Legacy fields (keep for compatibility)
      totalActual: mainSpent,
      totalSpent: mainSpent,
      percentage,
      remaining,
      mainBudget,
      contingencyBudget,
      contingencyUsed,
      contingencyRemaining,
      overBudget,
      overAmount
    };
  };

  const handlePhaseFileUpload = async (phaseId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    formData.append('category', 'phase_attachment');
    formData.append('phase_id', phaseId);

    try {
      await apiService.uploadDocument(formData);
      toast.success('Attachment uploaded successfully!');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to upload attachment');
    }
  };

  const openEditReceipt = (receipt) => {
    setEditingReceipt(receipt);
    setEditReceiptAmount(String(receipt.amount ?? ''));
    setEditReceiptVendor(receipt.vendor || receipt.vendor_name || '');
  };

  const handleSaveReceiptEdit = async () => {
    if (!editingReceipt) return;
    const newAmt = parseFloat(editReceiptAmount);
    if (isNaN(newAmt) || newAmt < 0) { toast.error('Enter a valid amount'); return; }
    setSavingReceiptEdit(true);
    try {
      await apiService.updateReceipt(editingReceipt.id, { amount: newAmt, vendor: editReceiptVendor });
      const oldAmt = editingReceipt.amount || 0;
      setPhaseReceipts(prev => prev.map(r => r.id === editingReceipt.id ? { ...r, amount: newAmt, vendor: editReceiptVendor } : r));
      setReceiptsTotalAmount(prev => prev - oldAmt + newAmt);
      toast.success('Receipt updated');
      setEditingReceipt(null);
    } catch (e) {
      toast.error('Failed to update receipt');
    } finally {
      setSavingReceiptEdit(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-slate-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="project-load-error">
        <div className="max-w-md text-center bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="text-3xl mb-2">⚠️</div>
          <h1 className="text-lg font-bold text-slate-900 mb-1">Couldn't load this project</h1>
          <p className="text-sm text-slate-600 mb-4">
            We hit a transient error pulling project details. Refresh to try again, or head back to the deal pipeline.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              data-testid="project-load-retry-btn"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/project-hub')}
              className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="project-load-back-btn"
            >
              Back to pipeline
            </button>
          </div>
        </div>
      </div>
    );
  }

  const budgetStatus = calculateBudgetStatus();

  // Calculate timeline status
  const getTimelineStatus = () => {
    if (!project.projected_completion_date) return { status: 'No deadline', daysRemaining: null, isPastDue: false, percentComplete: 0 };
    const today = new Date();
    const completionDate = new Date(project.projected_completion_date);
    const startDate = project.start_date ? new Date(project.start_date) : today;
    
    const totalDuration = completionDate - startDate;
    const elapsed = today - startDate;
    const percentComplete = totalDuration > 0 ? Math.max(0, Math.min(100, (elapsed / totalDuration) * 100)) : 0;
    
    const diffTime = completionDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { status: 'Overdue', daysRemaining: Math.abs(diffDays), isOverdue: true, isPastDue: true, percentComplete: 100 };
    return { status: 'On Track', daysRemaining: diffDays, isOverdue: false, isPastDue: false, percentComplete };
  };

  const timelineStatus = getTimelineStatus();

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile nav drawer overlay (below lg) */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileNavOpen(false)}
          data-testid="project-nav-overlay"
        />
      )}
      {/* Left Sidebar Navigation — fixed on desktop, slide-in hamburger drawer on mobile */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-900 flex flex-col h-full z-50 transform transition-transform duration-300 lg:translate-x-0 lg:z-30 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Close (mobile drawer only) */}
        <button
          onClick={() => setMobileNavOpen(false)}
          className="lg:hidden absolute top-4 right-3 text-slate-400 hover:text-white z-10"
          data-testid="project-nav-close"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Logo/Brand - Same as Dashboard */}
        <div className="p-4 border-b border-slate-700">
          <button onClick={() => navigate(homePath)} className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div className="overflow-hidden">
              <h1 className="font-bold text-lg text-white whitespace-nowrap">DrawReady</h1>
              <p className="text-xs text-slate-400 whitespace-nowrap">Construction Finance</p>
            </div>
          </button>
        </div>

        {/* Project Info Section */}
        <div className="px-4 py-3 border-b border-slate-700">
          <h2 className="font-bold text-white truncate">{project.name}</h2>
          <p className="text-xs text-slate-400 truncate">{project.address || 'No address'}</p>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {/* Dashboard - styled like other nav items */}
            <li>
              <button
                onClick={() => navigate(homePath)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">{isLenderViewer ? 'Portfolio' : 'Dashboard'}</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => setActiveTab('budget')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === 'budget' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid="budget-tab-btn"
              >
                <TrendingUp className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Budget & Progress</span>
              </button>
            </li>
            {!modulesLocked && (<>
            <li>
              <button
                onClick={() => setActiveTab('gantt')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === 'gantt' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Calendar className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Timeline</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => setActiveTab('schedule')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === 'schedule' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid="schedule-tab-btn"
              >
                <Calendar className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Schedule</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => setActiveTab('spec-sheets')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === 'spec-sheets' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <ClipboardList className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Spec Sheets</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => setActiveTab('paint')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === 'paint' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <PaintBucket className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Paint Colors</span>
              </button>
            </li>
            </>)}
            <li>
              <button
                onClick={() => setActiveTab('loan')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === 'loan' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Landmark className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Loan Details</span>
              </button>
            </li>
          </ul>
        </nav>

        {/* Bottom Section - User & Logout (same as Dashboard) */}
        <div className="border-t border-slate-700 py-4">
          <ul className="space-y-1 px-3">
            <li>
              <button
                onClick={() => navigate('/contact')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Help & Support</span>
              </button>
            </li>
          </ul>

          {/* User Info & Logout */}
          {user && (
            <div className="mt-4 px-3">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-medium">
                    {(user.name || user.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                <Button
                  onClick={() => setMobileNavOpen(true)}
                  data-testid="project-mobile-menu-toggle"
                  variant="ghost"
                  size="icon"
                  className="text-slate-600 hover:text-slate-900 flex-shrink-0 lg:hidden"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </Button>
                <div className="min-w-0 flex-1">
                  {allProjects.length > 1 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="group inline-flex items-center gap-2 min-w-0 -ml-1 rounded-xl bg-blue-600 hover:bg-blue-700 px-3 py-1.5 shadow-sm transition-colors" data-testid="project-switcher-trigger" title="Switch project">
                          <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-white truncate">{project.name}</h1>
                          <ChevronDown className="w-5 h-5 text-white flex-shrink-0" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto">
                        <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Switch project</div>
                        {allProjects.map((p) => (
                          <DropdownMenuItem
                            key={p.id}
                            onClick={() => { if (p.id !== projectId) navigate(`/project/${p.id}?tab=${activeTab}`); }}
                            className={`cursor-pointer ${p.id === projectId ? 'bg-blue-50' : ''}`}
                            data-testid={`project-switch-${p.id}`}
                          >
                            <div className="min-w-0">
                              <div className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                                {p.id === projectId && <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                                {p.name}
                              </div>
                              {p.address && <div className="text-xs text-slate-400 truncate">{p.address}</div>}
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-slate-900 truncate">{project.name}</h1>
                  )}
                  <p className="text-xs sm:text-sm text-slate-500 truncate">{project.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {/* Quick Capture - hidden on mobile (FAB covers it) */}
                <div className="hidden md:inline-block">
                  <QuickCaptureTopButton projectId={project?.id} />
                </div>
                {/* Edit Project Button - only for users with edit permission */}
                {can(PERMISSIONS.EDIT_PROJECTS) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setIsEditProjectDialogOpen(true)}
                >
                  <Edit2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
                )}

                {/* Multi-Unit Schedule wizard moved into the Schedule tab (v2.6) */}
                {gates?.bid_sheet_import && !isLenderViewer && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 hidden xl:inline-flex"
                    onClick={() => setBidImportOpen(true)}
                    data-testid="import-bid-sheet-button"
                    title="Import a lender bid sheet and reconcile phase budgets & subcontractors"
                  >
                    <FileSpreadsheet className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Import Bid Sheet</span>
                  </Button>
                )}
                <Button
                  data-testid="export-button"
                  variant="outline"
                  size="sm"
                  className="rounded-full hidden xl:inline-flex"
                  onClick={handleExportPDF}
                >
                  <Download className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              
              {/* Edit Project Dialog */}
              <Dialog open={isEditProjectDialogOpen} onOpenChange={setIsEditProjectDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Project</DialogTitle>
                    <DialogDescription>
                      Update project details and timeline
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUpdateProjectTimeline} className="space-y-6">
                    {/* Basic Info */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Basic Information</h3>
                      <div>
                        <Label htmlFor="edit-address">Address</Label>
                        <div className="flex gap-2">
                          <Input
                            id="edit-address"
                            value={editProjectData.address}
                            onChange={(e) => setEditProjectData({ ...editProjectData, address: e.target.value })}
                            onBlur={(e) => {
                              // Auto-detect timezone when address changes and no timezone is set
                              if (e.target.value && !editProjectData.timezone) {
                                handleDetectTimezone(e.target.value);
                              }
                            }}
                            placeholder="Project address"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDetectTimezone(editProjectData.address)}
                            disabled={!editProjectData.address || isDetectingTimezone}
                            className="whitespace-nowrap"
                            data-testid="detect-timezone-btn"
                          >
                            {isDetectingTimezone ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Globe className="h-4 w-4 mr-1" />
                            )}
                            Detect TZ
                          </Button>
                        </div>
                      </div>

                      {/* Builder company (v2.6.2) */}
                      <BuilderCompanyPicker
                        userId={project?.user_id}
                        value={editProjectData.builder_company_id}
                        onChange={(b) => setEditProjectData({
                          ...editProjectData,
                          builder_company_id: b.id,
                          builder_company_name: b.name,
                          builder_company_logo: b.logo,
                        })}
                      />

                      
                      {/* Timezone Selection */}
                      <div>
                        <Label htmlFor="edit-timezone">Project Timezone</Label>
                        <Select
                          value={editProjectData.timezone || ""}
                          onValueChange={(value) => setEditProjectData({ ...editProjectData, timezone: value })}
                        >
                          <SelectTrigger id="edit-timezone" data-testid="timezone-select">
                            <SelectValue placeholder="Select timezone (or auto-detect from address)" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTimezones.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {editProjectData.timezone && (
                          <p className="text-xs text-slate-500 mt-1">
                            All dates will display with {getTimezoneAbbrev(editProjectData.timezone)} timezone
                          </p>
                        )}
                      </div>
                      
                      {/* City Field */}
                      <div>
                        <Label htmlFor="edit-city">City</Label>
                        <Input
                          id="edit-city"
                          value={editProjectData.city}
                          onChange={(e) => setEditProjectData({ ...editProjectData, city: e.target.value })}
                          placeholder="City name for draw request form"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-start-date">Start Date</Label>
                          <Input
                            id="edit-start-date"
                            type="date"
                            value={editProjectData.start_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, start_date: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-completion-date">Projected Completion</Label>
                          <Input
                            id="edit-completion-date"
                            type="date"
                            value={editProjectData.projected_completion_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, projected_completion_date: e.target.value })}
                          />
                        </div>
                      </div>

                    {/* Key Milestone Dates (Jun 2026) — optional, improve valuation & benchmarking */}
                    <div className="space-y-3 pt-2">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Key Milestone Dates</h3>
                      <p className="text-xs text-slate-500 -mt-1">
                        All optional — but each date you add sharpens your own timeline, valuation, and cost tracking, and keeps your project records complete. Skip any you do not have yet.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-permit-submitted">Permit Submitted</Label>
                          <Input
                            id="edit-permit-submitted"
                            type="date"
                            data-testid="edit-permit-submitted-date"
                            value={editProjectData.permit_submitted_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, permit_submitted_date: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-permit-approved">Permit Approved</Label>
                          <Input
                            id="edit-permit-approved"
                            type="date"
                            data-testid="edit-permit-approved-date"
                            value={editProjectData.permit_approved_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, permit_approved_date: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-survey-completion">Survey Completed</Label>
                          <Input
                            id="edit-survey-completion"
                            type="date"
                            data-testid="edit-survey-completion-date"
                            value={editProjectData.survey_completion_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, survey_completion_date: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-actual-completion">Completion Date (Actual)</Label>
                          <Input
                            id="edit-actual-completion"
                            type="date"
                            data-testid="edit-actual-completion-date"
                            value={editProjectData.actual_completion_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, actual_completion_date: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-listing-date">Listing Date</Label>
                          <Input
                            id="edit-listing-date"
                            type="date"
                            data-testid="edit-listing-date"
                            value={editProjectData.listing_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, listing_date: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-sold-date">Sold Date</Label>
                          <Input
                            id="edit-sold-date"
                            type="date"
                            data-testid="edit-sold-date"
                            value={editProjectData.sold_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, sold_date: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    </div>

                    {/* v2.5.5.11 — Project Layout (buildings & units per building) */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Project Layout</h3>
                      <p className="text-xs text-slate-500 -mt-1">
                        Update the number of buildings, units per building, and total finished square footage. The schedule generator uses these to stagger crews and $/sqft analytics use the total sqft.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="edit-sqft">Total finished sqft</Label>
                          <Input
                            id="edit-sqft"
                            type="number"
                            min="0"
                            step="1"
                            value={editProjectData.sqft ?? ''}
                            onChange={(e) => setEditProjectData(prev => ({ ...prev, sqft: e.target.value }))}
                            placeholder="e.g. 4200"
                            data-testid="edit-sqft-input"
                          />
                          <p className="text-[10px] text-slate-500 mt-1">
                            Powers the Cost-per-sqft card on the project dashboard.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-end gap-3">
                        <div className="w-32">
                          <Label htmlFor="edit-buildings">Buildings</Label>
                          <Input
                            id="edit-buildings"
                            type="number"
                            min="1"
                            step="1"
                            value={editProjectData.buildings ?? 1}
                            onChange={(e) => {
                              const n = Math.max(1, parseInt(e.target.value, 10) || 1);
                              setEditProjectData(prev => {
                                const cur = Array.isArray(prev.units_per_building) ? prev.units_per_building : [prev.units_per_building || 1];
                                const next = [...cur];
                                while (next.length < n) next.push(cur[cur.length - 1] || 1);
                                next.length = n;
                                return { ...prev, buildings: n, units_per_building: next };
                              });
                            }}
                            data-testid="edit-buildings-input"
                          />
                        </div>
                        <div className="text-xs text-slate-500 pb-2">
                          Total units: <span className="font-bold text-slate-800 tabular-nums">
                            {(editProjectData.units_per_building || []).reduce((s, n) => s + (parseInt(n, 10) || 0), 0)}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2 mt-1">
                        {(editProjectData.units_per_building || []).map((u, i) => (
                          <div key={i} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 w-20 flex-shrink-0">
                              Bldg {i + 1}
                            </span>
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              value={u || ''}
                              onChange={(e) => {
                                const v = Math.max(1, parseInt(e.target.value, 10) || 1);
                                setEditProjectData(prev => {
                                  const next = [...(prev.units_per_building || [])];
                                  next[i] = v;
                                  return { ...prev, units_per_building: next };
                                });
                              }}
                              className="w-24 text-center"
                              data-testid={`edit-units-bldg-${i}`}
                              placeholder="units"
                            />
                            <span className="text-xs text-slate-500">unit{u === 1 ? '' : 's'}</span>
                            <button
                              type="button"
                              onClick={() => setEditProjectData(prev => {
                                const next = [...(prev.units_per_building || [])];
                                if (next.length <= 1) return prev;
                                next.splice(i, 1);
                                return { ...prev, units_per_building: next, buildings: next.length };
                              })}
                              className="text-slate-400 hover:text-red-600 ml-auto"
                              title="Remove this building"
                              disabled={(editProjectData.units_per_building || []).length <= 1}
                              data-testid={`remove-bldg-${i}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setEditProjectData(prev => {
                            const next = [...(prev.units_per_building || []), (prev.units_per_building?.[prev.units_per_building.length - 1] || 1)];
                            return { ...prev, units_per_building: next, buildings: next.length };
                          })}
                          className="text-xs font-semibold text-blue-700 hover:text-blue-800"
                          data-testid="add-building-btn"
                        >
                          + Add a building
                        </button>
                      </div>
                    </div>

                    {/* Budget Breakdown */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Budget Breakdown</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-purchase-home">Purchase Price (Home)</Label>
                          <Input
                            id="edit-purchase-home"
                            type="number"
                            step="0.01"
                            value={editProjectData.purchase_price_home}
                            onChange={(e) => setEditProjectData({ ...editProjectData, purchase_price_home: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-purchase-land">Purchase Price (Land)</Label>
                          <Input
                            id="edit-purchase-land"
                            type="number"
                            step="0.01"
                            value={editProjectData.purchase_price_land}
                            onChange={(e) => setEditProjectData({ ...editProjectData, purchase_price_land: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-closing-costs">Acquisition Closing Costs</Label>
                          <Input
                            id="edit-closing-costs"
                            type="number"
                            step="0.01"
                            value={editProjectData.acquisition_closing_costs}
                            onChange={(e) => setEditProjectData({ ...editProjectData, acquisition_closing_costs: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-construction">Construction Budget</Label>
                          <Input
                            id="edit-construction"
                            type="number"
                            step="0.01"
                            value={editProjectData.construction_budget}
                            onChange={(e) => setEditProjectData({ ...editProjectData, construction_budget: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-contingency">Contingency Budget</Label>
                          <Input
                            id="edit-contingency"
                            type="number"
                            step="0.01"
                            value={editProjectData.contingency_budget}
                            onChange={(e) => setEditProjectData({ ...editProjectData, contingency_budget: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-insurance">Insurance</Label>
                          <Input
                            id="edit-insurance"
                            type="number"
                            step="0.01"
                            value={editProjectData.insurance}
                            onChange={(e) => setEditProjectData({ ...editProjectData, insurance: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Total Budget: </span>
                          ${(
                            (parseFloat(editProjectData.purchase_price_home) || 0) +
                            (parseFloat(editProjectData.purchase_price_land) || 0) +
                            (parseFloat(editProjectData.acquisition_closing_costs) || 0) +
                            (parseFloat(editProjectData.construction_budget) || 0) +
                            (parseFloat(editProjectData.contingency_budget) || 0) +
                            (parseFloat(editProjectData.insurance) || 0)
                          ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>

                    {/* Loan Details */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Loan Details</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-bank-name">Bank Name</Label>
                          <Input
                            id="edit-bank-name"
                            value={editProjectData.bank_name}
                            onChange={(e) => setEditProjectData({ ...editProjectData, bank_name: e.target.value })}
                            placeholder="e.g., First National Bank"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-loan-number">Loan Number</Label>
                          <Input
                            id="edit-loan-number"
                            value={editProjectData.loan_number}
                            onChange={(e) => setEditProjectData({ ...editProjectData, loan_number: e.target.value })}
                            placeholder="Loan number"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-draw-account">Draw Account</Label>
                          <Input
                            id="edit-draw-account"
                            value={editProjectData.draw_account}
                            onChange={(e) => setEditProjectData({ ...editProjectData, draw_account: e.target.value })}
                            placeholder="Draw account"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-borrower">Borrower Name</Label>
                          <Input
                            id="edit-borrower"
                            value={editProjectData.borrower}
                            onChange={(e) => setEditProjectData({ ...editProjectData, borrower: e.target.value })}
                            placeholder="Borrower name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-borrower-entity">Borrower Entity</Label>
                          <Input
                            id="edit-borrower-entity"
                            value={editProjectData.borrower_entity}
                            onChange={(e) => setEditProjectData({ ...editProjectData, borrower_entity: e.target.value })}
                            placeholder="Entity name (LLC, etc.)"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-interest-rate">Interest Rate (%)</Label>
                          <Input
                            id="edit-interest-rate"
                            type="number"
                            step="0.00001"
                            value={editProjectData.loan_interest_rate}
                            onChange={(e) => setEditProjectData({ ...editProjectData, loan_interest_rate: e.target.value })}
                            placeholder="6.87500"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-principal">Total Loan Amount</Label>
                          <Input
                            id="edit-principal"
                            type="number"
                            step="0.01"
                            value={editProjectData.loan_principal_amount}
                            onChange={(e) => setEditProjectData({ ...editProjectData, loan_principal_amount: e.target.value })}
                            placeholder="0.00"
                          />
                          <p className="text-[11px] text-slate-400 mt-1">Your full bank loan commitment. Available to Draw = Total Loan − Amount Drawn.</p>
                        </div>
                        <div>
                          <Label htmlFor="edit-period">Loan Period (months)</Label>
                          <Input
                            id="edit-period"
                            type="number"
                            value={editProjectData.loan_period_months}
                            onChange={(e) => setEditProjectData({ ...editProjectData, loan_period_months: e.target.value })}
                            placeholder="12"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-interest-start">Interest Start Date</Label>
                          <Input
                            id="edit-interest-start"
                            type="date"
                            value={editProjectData.interest_start_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, interest_start_date: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-loan-maturity">Loan Maturity Date</Label>
                          <Input
                            id="edit-loan-maturity"
                            type="date"
                            data-testid="edit-loan-maturity-input"
                            value={editProjectData.loan_maturity_date}
                            onChange={(e) => setEditProjectData({ ...editProjectData, loan_maturity_date: e.target.value })}
                          />
                          <p className="text-[11px] text-slate-400 mt-1">Used to project interest owed at payoff.</p>
                        </div>
                        <div>
                          <Label htmlFor="edit-interest-basis">Interest Day-Count</Label>
                          <select
                            id="edit-interest-basis"
                            data-testid="edit-interest-basis-select"
                            value={editProjectData.interest_day_basis || 360}
                            onChange={(e) => setEditProjectData({ ...editProjectData, interest_day_basis: parseInt(e.target.value, 10) })}
                            className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm"
                          >
                            <option value={360}>360 (banker&apos;s year)</option>
                            <option value={365}>365 (actual)</option>
                          </select>
                          <p className="text-[11px] text-slate-400 mt-1">Match your bank (Hills = 360).</p>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="edit-short-legal">Short Legal Description</Label>
                        <Input
                          id="edit-short-legal"
                          value={editProjectData.short_legal}
                          onChange={(e) => setEditProjectData({ ...editProjectData, short_legal: e.target.value })}
                          placeholder="Legal description"
                        />
                      </div>
                    </div>

                    {/* Bank Contacts for Draw Request Form */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Bank Contacts (for Draw Request Form)</h3>
                      <p className="text-sm text-slate-500">
                        Add up to 4 bank contacts to display on your draw request form. These will appear in the "For Bank Use Only" section.
                      </p>
                      {editProjectData.bank_contacts.map((contact, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <span className="text-sm text-slate-500 w-4">{index + 1}.</span>
                          <Input
                            value={contact.name || ''}
                            onChange={(e) => {
                              const newContacts = [...editProjectData.bank_contacts];
                              newContacts[index] = { ...newContacts[index], name: e.target.value };
                              setEditProjectData({ ...editProjectData, bank_contacts: newContacts });
                            }}
                            placeholder="Contact name"
                            className="flex-1"
                          />
                          <Input
                            value={contact.phone || ''}
                            onChange={(e) => {
                              const newContacts = [...editProjectData.bank_contacts];
                              newContacts[index] = { ...newContacts[index], phone: e.target.value };
                              setEditProjectData({ ...editProjectData, bank_contacts: newContacts });
                            }}
                            placeholder="Phone number"
                            className="w-36"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newContacts = editProjectData.bank_contacts.filter((_, i) => i !== index);
                              setEditProjectData({ ...editProjectData, bank_contacts: newContacts });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {editProjectData.bank_contacts.length < 4 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditProjectData({
                              ...editProjectData,
                              bank_contacts: [...editProjectData.bank_contacts, { name: '', phone: '' }]
                            });
                          }}
                          className="text-blue-600"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Bank Contact
                        </Button>
                      )}
                    </div>

                    {/* Custom Lien Waiver Template */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Custom Lien Waiver Template</h3>
                      <p className="text-sm text-slate-500">
                        Upload your bank's required lien waiver format. When contractors sign, the system will auto-fill this template with project and contractor information.
                      </p>
                      
                      {project?.lien_waiver_template_id ? (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-green-800">Custom template uploaded</span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await apiService.deleteProjectLienTemplate(projectId);
                                    toast.success('Custom template removed');
                                    loadProjectData();
                                  } catch (err) {
                                    toast.error('Failed to remove template');
                                  }
                                }}
                              >
                                Remove
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                asChild
                              >
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/lien-waiver-template`} target="_blank" rel="noopener noreferrer">
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </a>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <label
                            htmlFor="lien-template-upload"
                            className="block p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 transition-colors cursor-pointer"
                          >
                            <div className="text-center">
                              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                              <span className="text-sm text-slate-600 hover:text-blue-600 block" data-testid="lien-template-upload-label">
                                {lienWaiverTemplateFile ? lienWaiverTemplateFile.name : 'Click to upload bank lien waiver template (PDF)'}
                              </span>
                            </div>
                            {/* iOS Safari will NOT open the picker for a display:none file input,
                                so keep it rendered but visually hidden (sr-only technique). */}
                            <input
                              id="lien-template-upload"
                              type="file"
                              accept="application/pdf,.pdf"
                              data-testid="lien-template-upload-input"
                              onChange={(e) => setLienWaiverTemplateFile(e.target.files?.[0] || null)}
                              style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }}
                            />
                          </label>
                          
                          {lienWaiverTemplateFile && (
                            <Button
                              type="button"
                              className="w-full"
                              onClick={async () => {
                                setIsUploadingLienTemplate(true);
                                try {
                                  const formData = new FormData();
                                  formData.append('file', lienWaiverTemplateFile);
                                  await apiService.uploadProjectLienTemplate(projectId, formData);
                                  toast.success('Lien waiver template uploaded!');
                                  setLienWaiverTemplateFile(null);
                                  loadProjectData();
                                } catch (err) {
                                  toast.error('Failed to upload template');
                                } finally {
                                  setIsUploadingLienTemplate(false);
                                }
                              }}
                              disabled={isUploadingLienTemplate}
                            >
                              {isUploadingLienTemplate ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                              ) : (
                                <><Upload className="w-4 h-4 mr-2" /> Upload Template</>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Custom Draw Request Form — project-specific (unique per address) */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700 border-b pb-2">Draw Request Form</h3>
                      <p className="text-sm text-slate-500">
                        Upload your bank&apos;s draw request form for this project. Each form is specific to this property&apos;s address, so it lives on the project (just like the lien waiver).
                      </p>

                      {project?.draw_request_form_id ? (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="draw-form-uploaded">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-green-800">Draw request form uploaded</span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                data-testid="draw-form-remove-btn"
                                onClick={async () => {
                                  try {
                                    await apiService.deleteProjectDrawForm(projectId);
                                    toast.success('Draw request form removed');
                                    setCustomFormMapped(false);
                                    loadProjectData();
                                  } catch (err) {
                                    toast.error('Failed to remove draw request form');
                                  }
                                }}
                              >
                                Remove
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                asChild
                              >
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/draw-request-form`} target="_blank" rel="noopener noreferrer">
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </a>
                              </Button>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-green-200 flex items-center justify-between gap-2">
                            <p className="text-xs text-green-700">
                              {customFormMapped
                                ? 'Fields mapped — this form is available as a draw-request format.'
                                : 'Map where your data prints so we can fill draws onto this exact form.'}
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 flex-shrink-0"
                              data-testid="draw-form-map-btn"
                              onClick={() => { setIsEditProjectDialogOpen(false); setActiveTab('loan'); setTimeout(() => setIsDrawMapperOpen(true), 400); }}
                            >
                              <MapPin className="w-4 h-4 mr-1" /> {customFormMapped ? 'Edit field map' : 'Map form fields'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <label
                            htmlFor="draw-form-upload"
                            className="block p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 transition-colors cursor-pointer"
                          >
                            <div className="text-center">
                              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                              <span className="text-sm text-slate-600 hover:text-blue-600 block" data-testid="draw-form-upload-label">
                                {drawFormFile ? drawFormFile.name : 'Click to upload bank draw request form (PDF)'}
                              </span>
                            </div>
                            <input
                              id="draw-form-upload"
                              type="file"
                              accept="application/pdf,.pdf"
                              data-testid="draw-form-upload-input"
                              onChange={(e) => setDrawFormFile(e.target.files?.[0] || null)}
                              style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }}
                            />
                          </label>

                          {drawFormFile && (
                            <Button
                              type="button"
                              className="w-full"
                              data-testid="draw-form-upload-btn"
                              onClick={async () => {
                                setIsUploadingDrawForm(true);
                                try {
                                  const formData = new FormData();
                                  formData.append('file', drawFormFile);
                                  await apiService.uploadProjectDrawForm(projectId, formData);
                                  toast.success('Draw request form uploaded!');
                                  setDrawFormFile(null);
                                  loadProjectData();
                                } catch (err) {
                                  toast.error('Failed to upload draw request form');
                                } finally {
                                  setIsUploadingDrawForm(false);
                                }
                              }}
                              disabled={isUploadingDrawForm}
                            >
                              {isUploadingDrawForm ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                              ) : (
                                <><Upload className="w-4 h-4 mr-2" /> Upload Form</>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsEditProjectDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                        Save Changes
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              
              {/* Guided tour for the current tab */}
              <TourLauncher tourKey={`project-${activeTab}`} userId={user?.id} />

              {/* Overflow Menu for Mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-shrink-0"
                    data-testid="project-actions-menu-trigger"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* Below xl: secondary header actions collapse here so the top bar never overflows */}
                  <DropdownMenuItem className="xl:hidden" onClick={handleExportPDF} data-testid="menu-export">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </DropdownMenuItem>
                  {gates?.bid_sheet_import && !isLenderViewer && (
                    <DropdownMenuItem className="xl:hidden" onClick={() => setBidImportOpen(true)} data-testid="menu-import-bid-sheet">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Import Bid Sheet
                    </DropdownMenuItem>
                  )}
                  {project.status === 'completed' ? (
                    <DropdownMenuItem onClick={handleReopenProject}>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Reopen Project
                    </DropdownMenuItem>
                  ) : (
                    <>
                      {!project.project_started_at && (
                        <DropdownMenuItem
                          onClick={() => setIsStartProjectDialogOpen(true)}
                          data-testid="menu-start-project"
                          className="text-blue-700 font-semibold"
                        >
                          <Rocket className="w-4 h-4 mr-2" />
                          Start Project
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={handleCompleteProject}>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Complete Project
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Profile Icon */}
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => navigate('/profile')}>
                <Users className="w-4 h-4 text-slate-600" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8 pb-8 overflow-y-auto">
        {/* Start Project / Break Ground Banner — only shows when project is in
            planning state (not yet started) and not completed. */}
        {project.status !== 'completed' && !project.project_started_at && !isReadOnlyUser && !isInvestor && (
          <Card className="mb-4 shadow-lg overflow-hidden border-0" data-testid="start-project-banner">
            <div
              className="p-5"
              style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)' }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.18)' }}
                >
                  <Hammer className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-100 mb-1">
                    Ready to break ground?
                  </p>
                  <h2 className="text-white text-xl font-bold leading-tight">
                    Press Start to notify all contractors.
                  </h2>
                  <p className="text-blue-50 text-xs mt-1.5 leading-relaxed">
                    Phase 1 will auto-status to <strong className="text-white">in progress</strong>, your
                    full contractor roster gets a personalized "we broke ground" email with their phase
                    dates highlighted, and the schedule clock starts running.
                  </p>
                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <Button
                      onClick={() => setIsStartProjectDialogOpen(true)}
                      className="bg-white hover:bg-blue-50 text-blue-700 font-bold shadow-md"
                      data-testid="start-project-banner-btn"
                    >
                      <Rocket className="w-4 h-4 mr-1.5" /> Start Project
                    </Button>
                    <Button
                      onClick={() => {
                        const current = project?.start_date ? String(project.start_date).slice(0, 10) : '';
                        setPushStartNewDate(current);
                        setIsPushStartDialogOpen(true);
                      }}
                      variant="outline"
                      className="bg-transparent hover:bg-white/10 text-white font-semibold border-white/60 hover:border-white"
                      data-testid="push-start-banner-btn"
                      title="Push the planned start date — reminders re-arm for the new date"
                    >
                      <Calendar className="w-4 h-4 mr-1.5" /> Push start date
                    </Button>
                    <span className="text-[11px] text-blue-100">
                      {contractorCount > 0
                        ? `${contractorCount} contractor${contractorCount === 1 ? '' : 's'} will be notified`
                        : 'Add contractors to phases first, then press Start'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-2.5 bg-slate-50 text-[11px] text-slate-500 border-t border-slate-200">
              <strong className="text-slate-700">Tip:</strong> Press Start when boots actually hit the
              ground — not just when the permit is approved. This keeps the contractor schedule honest.
            </div>
          </Card>
        )}

        {/* "Project active" pill — shows after Start has been pressed */}
        {project.project_started_at && project.status !== 'completed' && (
          <div className="mb-3 flex items-center gap-2" data-testid="project-active-pill">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
              <Rocket className="w-3 h-3" />
              Active since {formatDate(project.project_started_at)}
            </span>
          </div>
        )}

        {/* Completed Project Banner */}
        {project.status === 'completed' && (
          <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white mb-4 shadow-lg">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8" />
                  <div>
                    <h3 className="text-xl font-bold">Project Completed</h3>
                    <p className="text-sm text-green-100">This project has been marked as completed</p>
                  </div>
                </div>
                <Button
                  onClick={handleReopenProject}
                  variant="outline"
                  className="bg-white text-green-600 hover:bg-green-50 border-white"
                >
                  Reopen Project
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Investor Read-Only Banner */}
        {isInvestor && (
          <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300 mb-4">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-amber-900">Investor View (Read-Only)</h3>
                  <p className="text-xs text-amber-700">You have view-only access to this project's progress and financials.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timezone Banner - Non-blocking notification for projects without timezone */}
        {showTimezoneBanner && (
          <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200 mb-4 shadow-sm" data-testid="timezone-banner">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      This project doesn't have a timezone set
                    </p>
                    <p className="text-xs text-slate-500">
                      Set a timezone to ensure all dates display correctly for your location
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleQuickSetTimezone}
                    variant="default"
                    size="sm"
                    disabled={isDetectingTimezone}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="set-timezone-btn"
                  >
                    {isDetectingTimezone ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Globe className="h-4 w-4 mr-1" />
                    )}
                    Set Timezone
                  </Button>
                  <Button
                    onClick={() => setShowTimezoneBanner(false)}
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Prediction Card */}
        {aiPrediction && (
          <Card className="mb-8 border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Sparkles className="w-5 h-5" />
                AI-Powered Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
                {aiPrediction}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content based on active tab */}
        <div className="pb-24">

          {activeTab === 'overview' && (
            <div className="space-y-4">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900">Construction Phases</h3>
              </div>
              <div className="flex gap-2 flex-wrap w-full sm:w-auto">
                {/* Standalone AI Receipt Scanner Button */}
                <Dialog open={isReceiptScannerOpen} onOpenChange={(open) => {
                  setIsReceiptScannerOpen(open);
                  if (!open) {
                    setScannedReceiptData(null);
                    setScannerMode('new');
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                      <Camera className="w-4 h-4 mr-2" />
                      Scan Receipt
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>📸 AI Receipt Scanner</DialogTitle>
                      <DialogDescription>
                        Upload a receipt image - AI will extract amount, vendor, and date automatically
                      </DialogDescription>
                    </DialogHeader>
                    
                    {!scannedReceiptData ? (
                      <div className="space-y-4">
                        <div className="border-2 border-dashed border-purple-200 rounded-lg p-8 text-center bg-purple-50/50">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            id="standalone-receipt-upload"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              setIsScanningReceipt(true);
                              try {
                                // Create a temporary expense to scan the receipt
                                const tempExpense = await apiService.createProjectExpense({
                                  project_id: projectId,
                                  expense_type: 'other',
                                  description: 'Scanned Receipt (pending)',
                                  amount: 0,
                                  is_paid: false,
                                });
                                
                                // Scan the receipt
                                const res = await apiService.scanExpenseReceipt(tempExpense.data.id, file);
                                const receipt = res.data.receipt;
                                
                                setScannedReceiptData({
                                  ...receipt,
                                  tempExpenseId: tempExpense.data.id
                                });
                                
                                toast.success(`Receipt scanned: $${receipt.amount.toFixed(2)} from ${receipt.vendor}`);
                              } catch (error) {
                                toast.error('Failed to scan receipt');
                                console.error(error);
                              } finally {
                                setIsScanningReceipt(false);
                                e.target.value = '';
                              }
                            }}
                          />
                          <label
                            htmlFor="standalone-receipt-upload"
                            className="cursor-pointer flex flex-col items-center gap-3"
                          >
                            {isScanningReceipt ? (
                              <>
                                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center animate-pulse">
                                  <span className="text-3xl">🔍</span>
                                </div>
                                <span className="text-purple-600 font-medium">Scanning with AI...</span>
                                <span className="text-sm text-slate-500">This may take a few seconds</span>
                              </>
                            ) : (
                              <>
                                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                                  <Camera className="w-8 h-8 text-purple-600" />
                                </div>
                                <span className="text-purple-600 font-medium">Click or tap to upload receipt</span>
                                <span className="text-sm text-slate-500">Supports images and PDFs</span>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Scanned Data Display */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-medium text-green-800 mb-3">✓ Receipt Scanned Successfully</h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-slate-500">Amount:</span>
                              <span className="font-bold text-green-700 ml-2">${scannedReceiptData.amount?.toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Vendor:</span>
                              <span className="font-medium ml-2">{scannedReceiptData.vendor}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Date:</span>
                              <span className="ml-2">{scannedReceiptData.date || 'Not detected'}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-slate-500">Description:</span>
                              <span className="ml-2">{scannedReceiptData.description || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Options */}
                        <div className="space-y-3">
                          <p className="text-sm text-slate-600 font-medium">What would you like to do with this receipt?</p>
                          
                          {/* Create Soft Cost */}
                          <Button
                            type="button"
                            className="w-full bg-amber-600 hover:bg-amber-700 justify-start"
                            onClick={async () => {
                              try {
                                await apiService.updateProjectExpense(scannedReceiptData.tempExpenseId, {
                                  description: scannedReceiptData.vendor || 'Scanned Receipt',
                                  vendor_name: scannedReceiptData.vendor,
                                  amount: scannedReceiptData.amount,
                                  is_paid: true,
                                });
                                toast.success('Soft cost created from receipt!');
                                setIsReceiptScannerOpen(false);
                                setScannedReceiptData(null);
                                loadProjectExpenses();
                                loadProjectData();
                              } catch (e) {
                                toast.error('Failed to save');
                              }
                            }}
                          >
                            💰 Create New Soft Cost
                          </Button>
                          
                          {/* Add to Existing Phase - Only shows phases without contractors */}
                          <div className="border rounded-lg p-3 space-y-2">
                            <p className="text-sm font-medium text-slate-700">📦 Add to Existing Phase</p>
                            <p className="text-xs text-slate-500">Phases with contractors must use the "Pay" button for proper payment tracking</p>
                            <Select
                              value={scannedReceiptData.selectedPhaseId || ''}
                              onValueChange={(value) => setScannedReceiptData({...scannedReceiptData, selectedPhaseId: value})}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a phase..." />
                              </SelectTrigger>
                              <SelectContent>
                                {phases.filter(phase => !phase.contractor_id).map((phase) => (
                                  <SelectItem key={phase.id} value={phase.id}>
                                    {phase.phase_type === 'material' ? '📦' : '🔨'} {phase.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {scannedReceiptData.selectedPhaseId && (
                              <Button
                                type="button"
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                onClick={async () => {
                                  try {
                                    // Delete the temp soft cost
                                    await apiService.deleteProjectExpense(scannedReceiptData.tempExpenseId);
                                    
                                    // Add receipt to the selected phase
                                    const selectedPhase = phases.find(p => p.id === scannedReceiptData.selectedPhaseId);
                                    
                                    // Update phase with the receipt amount (add to actual_cost if exists)
                                    const currentCost = parseFloat(selectedPhase?.actual_cost) || 0;
                                    const newCost = currentCost + scannedReceiptData.amount;
                                    
                                    await apiService.updatePhase(scannedReceiptData.selectedPhaseId, {
                                      actual_cost: newCost,
                                    });
                                    
                                    // Also scan the receipt to attach it to the phase
                                    // (reupload to phase receipts would require the file, so we just update cost)
                                    
                                    toast.success(`Added $${scannedReceiptData.amount.toFixed(2)} to ${selectedPhase?.name}`);
                                    setIsReceiptScannerOpen(false);
                                    setScannedReceiptData(null);
                                    loadProjectData();
                                  } catch (e) {
                                    toast.error('Failed to add to phase');
                                    console.error(e);
                                  }
                                }}
                              >
                                ✓ Add ${scannedReceiptData.amount?.toFixed(2)} to Phase
                              </Button>
                            )}
                          </div>
                          
                          {/* Create New Phase */}
                          <div className="border rounded-lg p-3 space-y-2">
                            <p className="text-sm font-medium text-slate-700">🔨 Create New Phase</p>
                            <Input
                              placeholder="Phase name (e.g., 'Plumbing Materials')"
                              value={scannedReceiptData.newPhaseName || ''}
                              onChange={(e) => setScannedReceiptData({...scannedReceiptData, newPhaseName: e.target.value})}
                            />
                            <div className="flex gap-2">
                              <Select
                                value={scannedReceiptData.newPhaseType || 'material'}
                                onValueChange={(value) => setScannedReceiptData({...scannedReceiptData, newPhaseType: value})}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="material">📦 Material</SelectItem>
                                  <SelectItem value="labor">🔨 Labor</SelectItem>
                                  <SelectItem value="soft_cost">📋 Soft Cost</SelectItem>
                                </SelectContent>
                              </Select>
                              {scannedReceiptData.newPhaseName && (
                                <Button
                                  type="button"
                                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                  onClick={async () => {
                                    try {
                                      // Delete the temp soft cost
                                      await apiService.deleteProjectExpense(scannedReceiptData.tempExpenseId);
                                      
                                      // Create new phase
                                      await apiService.createPhase({
                                        project_id: projectId,
                                        name: scannedReceiptData.newPhaseName,
                                        phase_type: scannedReceiptData.newPhaseType || 'material',
                                        estimated_cost: scannedReceiptData.amount,
                                        actual_cost: scannedReceiptData.amount,
                                        supplier_name: scannedReceiptData.vendor,
                                      });
                                      
                                      toast.success(`Phase "${scannedReceiptData.newPhaseName}" created!`);
                                      setIsReceiptScannerOpen(false);
                                      setScannedReceiptData(null);
                                      loadProjectData();
                                    } catch (e) {
                                      toast.error('Failed to create phase');
                                      console.error(e);
                                    }
                                  }}
                                >
                                  ✓ Create Phase
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Scan Another */}
                          <Button
                            type="button"
                            variant="ghost"
                            className="w-full text-slate-500"
                            onClick={async () => {
                              try {
                                await apiService.deleteProjectExpense(scannedReceiptData.tempExpenseId);
                              } catch (e) {}
                              setScannedReceiptData(null);
                            }}
                          >
                            ← Scan Another Receipt
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                
                
                {/* Edit Expense Dialog */}
                <Dialog open={isEditExpenseDialogOpen} onOpenChange={setIsEditExpenseDialogOpen}>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Edit Soft Cost</DialogTitle>
                    </DialogHeader>
                    {editExpense && (
                      <form onSubmit={handleEditExpense} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select
                            value={editExpense.expense_type}
                            onValueChange={(value) => setEditExpense({ ...editExpense, expense_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="permit">🏛️ Permit</SelectItem>
                              <SelectItem value="utility">⚡ Utilities</SelectItem>
                              <SelectItem value="disposal">🗑️ Dumpster/Disposal</SelectItem>
                              <SelectItem value="porta_potty">🚽 Porta-Potty</SelectItem>
                              <SelectItem value="rental">🚛 Equipment Rental</SelectItem>
                              <SelectItem value="insurance">🏠 Insurance</SelectItem>
                              <SelectItem value="other">📋 Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Input
                            value={editExpense.description || ''}
                            onChange={(e) => setEditExpense({ ...editExpense, description: e.target.value })}
                            placeholder="Description"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Vendor Name</Label>
                          <Input
                            value={editExpense.vendor_name || ''}
                            onChange={(e) => setEditExpense({ ...editExpense, vendor_name: e.target.value })}
                            placeholder="Vendor name"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Estimated Amount</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={editExpense.estimated_amount || ''}
                              onChange={(e) => setEditExpense({ ...editExpense, estimated_amount: e.target.value })}
                              placeholder="0.00"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Actual Amount</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={editExpense.amount || ''}
                              onChange={(e) => setEditExpense({ ...editExpense, amount: e.target.value })}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editExpense.is_paid || false}
                              onChange={(e) => setEditExpense({ ...editExpense, is_paid: e.target.checked })}
                              className="rounded"
                            />
                            <span className="text-sm">Marked as Paid</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editExpense.is_running_tab || false}
                              onChange={(e) => setEditExpense({ ...editExpense, is_running_tab: e.target.checked })}
                              className="rounded"
                            />
                            <span className="text-sm">Running Tab (multiple payments)</span>
                          </label>
                        </div>
                        
                        {/* Receipt Upload Section */}
                        <div className="border-t pt-4 mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-slate-700 font-medium">📄 Receipt / Invoice</Label>
                            {editExpense.receipt_file_id && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const response = await apiService.getProjectExpenseReceipt(editExpense.id);
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    window.open(url, '_blank');
                                  } catch (e) {
                                    toast.error('Failed to open receipt');
                                  }
                                }}
                                className="text-blue-600"
                              >
                                View Receipt
                              </Button>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              id="expense-receipt-upload"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                setIsUploadingExpenseReceipt(true);
                                try {
                                  const res = await apiService.scanExpenseReceipt(editExpense.id, file);
                                  const receipt = res.data.receipt;
                                  
                                  // Update the form with scanned data
                                  if (receipt.amount > 0 && (!editExpense.amount || editExpense.amount === 0)) {
                                    setEditExpense(prev => ({ 
                                      ...prev, 
                                      amount: receipt.amount,
                                      vendor_name: prev.vendor_name || receipt.vendor,
                                      receipt_file_id: receipt.file_id,
                                      is_paid: true
                                    }));
                                    toast.success(`Receipt scanned: $${receipt.amount.toFixed(2)} from ${receipt.vendor}`);
                                  } else {
                                    setEditExpense(prev => ({ 
                                      ...prev, 
                                      receipt_file_id: receipt.file_id 
                                    }));
                                    toast.success('Receipt uploaded successfully!');
                                  }
                                  
                                  loadProjectExpenses();
                                } catch (error) {
                                  toast.error('Failed to upload receipt');
                                  console.error(error);
                                } finally {
                                  setIsUploadingExpenseReceipt(false);
                                  e.target.value = '';
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => document.getElementById('expense-receipt-upload').click()}
                              disabled={isUploadingExpenseReceipt}
                              className="flex-1"
                            >
                              {isUploadingExpenseReceipt ? (
                                <>
                                  <span className="animate-spin mr-2">⏳</span>
                                  Scanning...
                                </>
                              ) : (
                                <>
                                  📤 {editExpense.receipt_file_id ? 'Replace Receipt' : 'Upload Receipt'}
                                </>
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Upload a receipt or invoice image. AI will auto-extract the amount and vendor.
                          </p>
                        </div>
                        
                        {/* Amount Already Drawn - for tracking reimbursement */}
                        <div className="space-y-2 border-t pt-4">
                          <Label>Amount Already Drawn (Reimbursed)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={editExpense.amount_drawn || ''}
                            onChange={(e) => setEditExpense({ ...editExpense, amount_drawn: parseFloat(e.target.value) || 0 })}
                            placeholder="0.00"
                          />
                          <p className="text-xs text-slate-500">Enter the amount already reimbursed from bank draws. This expense will be hidden from new draw requests once fully drawn.</p>
                        </div>
                        
                        {/* Running Tab Payment Section */}
                        {(editExpense.is_running_tab || ['utility', 'disposal', 'porta_potty'].includes(editExpense.expense_type)) && (
                          <div className="border-t pt-4 mt-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-slate-700">Payment Entries</h4>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                  setSelectedExpenseForPayment(editExpense);
                                  setIsExpensePaymentDialogOpen(true);
                                }}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                + Add Payment
                              </Button>
                            </div>
                            {editExpense.payment_entries && editExpense.payment_entries.length > 0 ? (
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {editExpense.payment_entries.map((payment, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded text-sm">
                                    <span>{payment.date ? formatDate(payment.date) : '-'}</span>
                                    <span className="text-emerald-600 font-medium">${parseFloat(payment.amount || 0).toLocaleString()}</span>
                                    <span className="text-slate-500 text-xs truncate max-w-[120px]">{payment.description || '-'}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteExpensePayment(editExpense.id, payment.id)}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                                <div className="text-right font-medium text-emerald-600 pt-2 border-t">
                                  Total: ${editExpense.payment_entries.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString()}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 italic">No payments recorded. Click "+ Add Payment" to add.</p>
                            )}
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button type="submit" className="flex-1">Save Changes</Button>
                          <Button type="button" variant="outline" onClick={() => setIsEditExpenseDialogOpen(false)}>Cancel</Button>
                        </div>
                      </form>
                    )}
                  </DialogContent>
                </Dialog>
                
                {/* Add Payment to Running Tab Dialog */}
                <Dialog open={isExpensePaymentDialogOpen} onOpenChange={setIsExpensePaymentDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Payment</DialogTitle>
                      <DialogDescription>
                        Adding payment to: {selectedExpenseForPayment?.description || selectedExpenseForPayment?.expense_type?.replace('_', ' ')}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddExpensePayment} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            step="0.01"
                            required
                            value={newExpensePayment.amount}
                            onChange={(e) => setNewExpensePayment({ ...newExpensePayment, amount: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input
                            type="date"
                            required
                            value={newExpensePayment.date}
                            onChange={(e) => setNewExpensePayment({ ...newExpensePayment, date: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Description (optional)</Label>
                        <Input
                          value={newExpensePayment.description}
                          onChange={(e) => setNewExpensePayment({ ...newExpensePayment, description: e.target.value })}
                          placeholder="e.g., Monthly rental - January"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1">Add Payment</Button>
                        <Button type="button" variant="outline" onClick={() => setIsExpensePaymentDialogOpen(false)}>Cancel</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                
                {/* Add Payment to Soft Cost Phase Dialog */}
                <Dialog open={isAddPaymentDialogOpen} onOpenChange={setIsAddPaymentDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Payment</DialogTitle>
                      <DialogDescription>
                        Adding payment to: {currentPhaseForPayment?.name || 'Soft Cost'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddPhasePayment} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            step="0.01"
                            required
                            value={newPayment.amount}
                            onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input
                            type="date"
                            required
                            value={newPayment.payment_date}
                            onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Description (optional)</Label>
                        <Input
                          value={newPayment.description}
                          onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
                          placeholder="e.g., Partial payment"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Payment Method</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={newPayment.payment_method}
                          onChange={(e) => setNewPayment({ ...newPayment, payment_method: e.target.value })}
                        >
                          <option value="check">Check</option>
                          <option value="ach">ACH</option>
                          <option value="wire">Wire</option>
                          <option value="cash">Cash</option>
                          <option value="card">Card</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1">Add Payment</Button>
                        <Button type="button" variant="outline" onClick={() => setIsAddPaymentDialogOpen(false)}>Cancel</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                
                {gates?.bid_sheet_import && !isLenderViewer && (
                  <Button
                    onClick={() => setBidImportOpen(true)}
                    data-testid="import-bid-sheet-button-inline"
                    variant="outline"
                    className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Import Bid Sheet
                  </Button>
                )}

                <Dialog open={isPhaseDialogOpen} onOpenChange={setIsPhaseDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-phase-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Phase
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                  <DialogHeader className="flex-shrink-0">
                    <DialogTitle>Add New Phase</DialogTitle>
                    <DialogDescription>Create a new construction phase for this project</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreatePhase} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Phase Type Selector */}
                      <div className="col-span-2 space-y-2">
                        <Label>Phase Type</Label>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => setNewPhase({ ...newPhase, phase_type: 'labor' })}
                            className={`flex-1 min-w-[120px] py-3 px-3 rounded-lg border-2 transition-all ${
                              newPhase.phase_type === 'labor'
                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xl">🔨</span>
                              <div className="text-left">
                                <div className="font-semibold text-sm">Labor</div>
                                <div className="text-xs text-slate-500">Work & contractors</div>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewPhase({ ...newPhase, phase_type: 'material' })}
                            className={`flex-1 min-w-[120px] py-3 px-3 rounded-lg border-2 transition-all ${
                              newPhase.phase_type === 'material'
                                ? 'border-green-600 bg-green-50 text-green-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xl">📦</span>
                              <div className="text-left">
                                <div className="font-semibold text-sm">Material</div>
                                <div className="text-xs text-slate-500">Orders & delivery</div>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewPhase({ ...newPhase, phase_type: 'labor_and_materials' })}
                            className={`flex-1 min-w-[120px] py-3 px-3 rounded-lg border-2 transition-all ${
                              newPhase.phase_type === 'labor_and_materials'
                                ? 'border-orange-600 bg-orange-50 text-orange-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xl">🔧</span>
                              <div className="text-left">
                                <div className="font-semibold text-sm">Labor & Materials</div>
                                <div className="text-xs text-slate-500">Combined bid</div>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewPhase({ ...newPhase, phase_type: 'soft_cost' })}
                            className={`flex-1 min-w-[120px] py-3 px-3 rounded-lg border-2 transition-all ${
                              newPhase.phase_type === 'soft_cost'
                                ? 'border-purple-600 bg-purple-50 text-purple-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xl">📋</span>
                              <div className="text-left">
                                <div className="font-semibold text-sm">Soft Cost</div>
                                <div className="text-xs text-slate-500">Permits, fees, etc.</div>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="phase-name">{newPhase.phase_type === 'labor' ? 'Phase' : newPhase.phase_type === 'material' ? 'Material' : newPhase.phase_type === 'labor_and_materials' ? 'Phase' : 'Expense'} Name</Label>
                        <Input
                          id="phase-name"
                          data-testid="phase-name-input"
                          placeholder={newPhase.phase_type === 'labor' ? 'Foundation Work' : newPhase.phase_type === 'material' ? 'Windows' : newPhase.phase_type === 'labor_and_materials' ? 'Electrical (Labor & Materials)' : 'Building Permit'}
                          value={newPhase.name}
                          onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="phase-category">Category</Label>
                        <Select
                          value={newPhase.category || "custom"}
                          onValueChange={(value) => {
                            if (value === "custom") {
                              setNewPhase({ ...newPhase, category: '' });
                            } else {
                              setNewPhase({ ...newPhase, category: value });
                            }
                          }}
                        >
                          <SelectTrigger id="phase-category" data-testid="phase-category-input">
                            <SelectValue placeholder="Select or type custom category" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            <SelectItem value="custom">+ Add Custom Category</SelectItem>
                            <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">Standard Categories</div>
                            {getAllCategories(phases).map((cat) => (
                              <SelectItem key={cat} value={cat}>
                                {cat}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {newPhase.category === '' && (
                          <Input
                            placeholder="Enter custom category name"
                            value={newPhase.category}
                            onChange={(e) => setNewPhase({ ...newPhase, category: normalizeCategory(e.target.value) })}
                            className="mt-2"
                            autoFocus
                          />
                        )}
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="phase-desc">Description</Label>
                        <Input
                          id="phase-desc"
                          placeholder="Excavation and concrete pour"
                          value={newPhase.description}
                          onChange={(e) => setNewPhase({ ...newPhase, description: e.target.value })}
                        />
                      </div>

                      {/* Hills Bank Category Selector */}
                      <div className="col-span-2 space-y-2 border-t pt-4">
                        <Label htmlFor="bank-category">Hills Bank Category (Optional)</Label>
                        <p className="text-xs text-slate-500 mb-2">
                          Select a category for bank draw requests, or leave blank for custom
                        </p>
                        <Select
                          value={newPhase.bank_category || "none"}
                          onValueChange={(value) => setNewPhase({ ...newPhase, bank_category: value === "none" ? "" : value })}
                        >
                          <SelectTrigger id="bank-category">
                            <SelectValue placeholder="Select Hills Bank category (optional)" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            <SelectItem value="none">None (Custom Category)</SelectItem>
                            {HILLS_BANK_CATEGORIES.map((cat) => (
                              <SelectItem key={cat.code} value={cat.fullName}>
                                {cat.fullName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {newPhase.bank_category && newPhase.bank_category !== "none" && (
                          <div className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Category mapped for bank draw requests
                          </div>
                        )}
                      </div>

                      {/* Conditional fields based on phase type */}
                      {(newPhase.phase_type === 'labor' || newPhase.phase_type === 'labor_and_materials') ? (
                        <>
                          {/* Labor Phase Fields */}
                          <div className="space-y-2">
                            <Label htmlFor="phase-cost">Estimated Cost ($)</Label>
                            <Input
                              id="phase-cost"
                              data-testid="phase-cost-input"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="50000"
                              value={newPhase.estimated_cost}
                              onChange={(e) => setNewPhase({ ...newPhase, estimated_cost: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phase-start-date">Start Date</Label>
                            <Input
                              id="phase-start-date"
                              type="date"
                              value={newPhase.start_date}
                              onChange={(e) => setNewPhase({ ...newPhase, start_date: e.target.value })}
                            />
                          </div>
                          {(!newPhase.phase_type || newPhase.phase_type === 'labor' || newPhase.phase_type === 'labor_and_materials') && (
                            <div className="col-span-2 space-y-2">
                              <Label htmlFor="phase-contractor">Assign Contractor (Optional)</Label>
                              <Select
                                value={newPhase.contractor_id || 'none'}
                                onValueChange={(value) => setNewPhase({ ...newPhase, contractor_id: value === 'none' ? '' : value })}
                              >
                                <SelectTrigger id="phase-contractor">
                                  <SelectValue placeholder="Select contractor" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No contractor assigned</SelectItem>
                                  {contractors.map((contractor) => (
                                    <SelectItem key={contractor.id} value={contractor.id}>
                                      {contractor.name} {contractor.company && `(${contractor.company})`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <div className="col-span-2 space-y-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newPhase.exclude_from_sqft}
                                onChange={(e) => setNewPhase({ ...newPhase, exclude_from_sqft: e.target.checked })}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <span className="text-sm">Exclude from $/sqft</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newPhase.all_inclusive_bid}
                                onChange={(e) => setNewPhase({ ...newPhase, all_inclusive_bid: e.target.checked })}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <span className="text-sm font-medium text-slate-700">
                                All-inclusive bid (labor + materials combined)
                              </span>
                            </label>
                          </div>
                          <div className="col-span-2 space-y-2">
                            <Label htmlFor="inspection-status">Inspection Status</Label>
                            <Select
                              value={newPhase.inspection_status}
                              onValueChange={(value) => setNewPhase({ ...newPhase, inspection_status: value })}
                            >
                              <SelectTrigger id="inspection-status" data-testid="inspection-status-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="n/a">N/A</SelectItem>
                                <SelectItem value="passed">Passed</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Material Phase Fields */}
                          <div className="col-span-2 space-y-2">
                            <Label htmlFor="supplier-vendor">Supplier/Vendor</Label>
                            <Input
                              id="supplier-vendor"
                              placeholder="ABC Supply Co."
                              value={newPhase.supplier_vendor}
                              onChange={(e) => setNewPhase({ ...newPhase, supplier_vendor: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="material-cost">Cost ($)</Label>
                            <Input
                              id="material-cost"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="15000"
                              value={newPhase.estimated_cost}
                              onChange={(e) => setNewPhase({ ...newPhase, estimated_cost: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="order-date">Order Date</Label>
                            <Input
                              id="order-date"
                              type="date"
                              value={newPhase.order_date}
                              onChange={(e) => setNewPhase({ ...newPhase, order_date: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="est-delivery-date">Estimated Delivery</Label>
                            <Input
                              id="est-delivery-date"
                              type="date"
                              value={newPhase.estimated_delivery_date}
                              onChange={(e) => setNewPhase({ ...newPhase, estimated_delivery_date: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="est-install-date">Estimated Install</Label>
                            <Input
                              id="est-install-date"
                              type="date"
                              value={newPhase.estimated_install_date}
                              onChange={(e) => setNewPhase({ ...newPhase, estimated_install_date: e.target.value })}
                            />
                          </div>
                          <div className="col-span-2 space-y-2">
                            <Label htmlFor="linked-phase">Linked to Labor Phase</Label>
                            <Select
                              value={newPhase.linked_phase_id}
                              onValueChange={(value) => setNewPhase({ ...newPhase, linked_phase_id: value === 'none' ? '' : value })}
                            >
                              <SelectTrigger id="linked-phase">
                                <SelectValue placeholder="Select related labor phase (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No linked phase</SelectItem>
                                {phases && phases.filter(p => p.phase_type === 'labor' || !p.phase_type).map((phase) => (
                                  <SelectItem key={phase.id} value={phase.id}>
                                    {phase.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                    </div>
                    </div>
                    
                    {/* Submit Button - Fixed at bottom */}
                    <div className="flex-shrink-0 pt-4 border-t mt-4">
                      <Button type="submit" data-testid="submit-phase-button" className="w-full">
                        Create {newPhase.phase_type === 'labor' ? 'Labor Phase' : newPhase.phase_type === 'material' ? 'Material Phase' : newPhase.phase_type === 'labor_and_materials' ? 'Labor & Materials Phase' : 'Soft Cost'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            </div>

            {/* Edit Phase Dialog */}
            <Dialog open={isEditPhaseDialogOpen} onOpenChange={setIsEditPhaseDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Phase Details</DialogTitle>
                  <DialogDescription>
                    Update any field for this phase
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSavePhaseEdit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="edit-phase-name">Phase Name</Label>
                      <Input
                        id="edit-phase-name"
                        type="text"
                        value={editPhase.name}
                        onChange={(e) => setEditPhase({ ...editPhase, name: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="edit-phase-category">Category</Label>
                      <Select
                        value={editPhase.category || "custom"}
                        onValueChange={(value) => {
                          if (value === "custom") {
                            setEditPhase({ ...editPhase, category: '' });
                          } else {
                            setEditPhase({ ...editPhase, category: value });
                          }
                        }}
                      >
                        <SelectTrigger id="edit-phase-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="custom">+ Add Custom Category</SelectItem>
                          <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">Standard Categories</div>
                          {getAllCategories(phases).map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(editPhase.category === '' || !getAllCategories(phases).includes(editPhase.category)) && (
                        <Input
                          placeholder="Enter custom category name"
                          value={editPhase.category === 'Uncategorized' ? '' : editPhase.category}
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            // Don't normalize while typing - just store the raw value
                            setEditPhase({ ...editPhase, category: value });
                          }}
                          onBlur={(e) => {
                            // Normalize on blur only if there's a value
                            if (e.target.value.trim()) {
                              setEditPhase({ ...editPhase, category: normalizeCategory(e.target.value) });
                            }
                          }}
                          className="mt-2"
                          autoFocus
                        />
                      )}
                    </div>
                    
                    {/* Status is auto-calculated based on dates - no manual selection needed */}
                    
                    {/* Date fields - different for labor vs material */}
                    {(!editPhase.phase_type || editPhase.phase_type === 'labor' || editPhase.phase_type === 'labor_and_materials') ? (
                      <>
                        {/* Labor Phase Status Controls */}
                        <div className="col-span-2 bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-slate-700">{editPhase.phase_type === 'labor_and_materials' ? 'Labor & Materials' : 'Labor'} Status</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              editPhase.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              editPhase.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              editPhase.status === 'delayed' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {editPhase.status === 'completed' ? '✓ Completed' :
                               editPhase.status === 'in_progress' ? '🔨 In Progress' :
                               editPhase.status === 'delayed' ? '⚠️ Delayed' :
                               '⏳ Pending'}
                            </span>
                          </div>
                          <div className="flex gap-2 flex-wrap min-h-[40px]">
                            {(!editPhase.status || editPhase.status === 'pending') && (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => {
                                    const today = new Date().toISOString().split('T')[0];
                                    setEditPhase({ 
                                      ...editPhase, 
                                      status: 'in_progress',
                                      actual_start_date: today
                                    });
                                  }}
                                >
                                  ▶️ Start Work
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-red-300 text-red-600 hover:bg-red-50"
                                  onClick={() => setEditPhase({ ...editPhase, status: 'delayed' })}
                                >
                                  ⚠️ Mark Delayed
                                </Button>
                              </>
                            )}
                            {editPhase.status === 'in_progress' && (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => {
                                    const today = new Date().toISOString().split('T')[0];
                                    setEditPhase({ 
                                      ...editPhase, 
                                      status: 'completed',
                                      actual_end_date: today
                                    });
                                  }}
                                >
                                  ✓ Mark Complete
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-red-300 text-red-600 hover:bg-red-50"
                                  onClick={() => setEditPhase({ ...editPhase, status: 'delayed' })}
                                >
                                  ⚠️ Mark Delayed
                                </Button>
                              </>
                            )}
                            {editPhase.status === 'delayed' && (
                              <Button
                                type="button"
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => {
                                  const today = new Date().toISOString().split('T')[0];
                                  setEditPhase({ 
                                    ...editPhase, 
                                    status: 'in_progress',
                                    actual_start_date: editPhase.actual_start_date || today
                                  });
                                }}
                              >
                                ▶️ Resume Work
                              </Button>
                            )}
                            {editPhase.status === 'completed' && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setEditPhase({ ...editPhase, status: 'in_progress', actual_end_date: '' })}
                              >
                                ↩️ Reopen
                              </Button>
                            )}
                          </div>
                          {editPhase.actual_start_date && (
                            <p className="text-xs text-slate-500 mt-2">
                              Started: {formatDate(editPhase.actual_start_date)}
                              {editPhase.start_date && new Date(editPhase.actual_start_date) > new Date(editPhase.start_date) && (
                                <span className="text-red-500 ml-2">
                                  (Started {Math.ceil((new Date(editPhase.actual_start_date) - new Date(editPhase.start_date)) / (1000*60*60*24))} days late)
                                </span>
                              )}
                              {editPhase.start_date && new Date(editPhase.actual_start_date) < new Date(editPhase.start_date) && (
                                <span className="text-green-500 ml-2">
                                  (Started {Math.ceil((new Date(editPhase.start_date) - new Date(editPhase.actual_start_date)) / (1000*60*60*24))} days early)
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="edit-phase-start-date" className="text-slate-600">
                            <Calendar className="w-4 h-4 inline mr-2" />
                            Scheduled Start <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <Input
                            id="edit-phase-start-date"
                            type="date"
                            value={editPhase.start_date}
                            onChange={(e) => setEditPhase({ ...editPhase, start_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-phase-end-date" className="text-slate-600">
                            <Calendar className="w-4 h-4 inline mr-2" />
                            Scheduled End <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <Input
                            id="edit-phase-end-date"
                            type="date"
                            value={editPhase.end_date}
                            onChange={(e) => setEditPhase({ ...editPhase, end_date: e.target.value })}
                          />
                        </div>
                        
                        {/* Actual End Date - for completed phases */}
                        {(editPhase.status === 'completed' || editPhase.actual_end_date) && (
                          <div className="space-y-2">
                            <Label htmlFor="edit-actual-end-date" className="text-slate-600">
                              <CheckCircle2 className="w-4 h-4 inline mr-2" />
                              Actual Completion Date
                            </Label>
                            <Input
                              id="edit-actual-end-date"
                              type="date"
                              value={editPhase.actual_end_date}
                              onChange={(e) => setEditPhase({ ...editPhase, actual_end_date: e.target.value })}
                            />
                            {editPhase.actual_end_date && editPhase.end_date && (
                              <p className="text-xs mt-1">
                                {new Date(editPhase.actual_end_date) < new Date(editPhase.end_date) ? (
                                  <span className="text-green-600 flex items-center gap-1">
                                    🚀 Completed {Math.ceil((new Date(editPhase.end_date) - new Date(editPhase.actual_end_date)) / (1000*60*60*24))} days ahead of schedule!
                                  </span>
                                ) : new Date(editPhase.actual_end_date) > new Date(editPhase.end_date) ? (
                                  <span className="text-red-500 flex items-center gap-1">
                                    ⚠️ Completed {Math.ceil((new Date(editPhase.actual_end_date) - new Date(editPhase.end_date)) / (1000*60*60*24))} days late
                                  </span>
                                ) : (
                                  <span className="text-slate-500">Completed on schedule</span>
                                )}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="edit-order-date" className="text-slate-600">
                            Order Date <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <Input
                            id="edit-order-date"
                            type="date"
                            value={editPhase.order_date}
                            onChange={(e) => setEditPhase({ ...editPhase, order_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-est-delivery" className="text-slate-600">
                            Est. Delivery <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <Input
                            id="edit-est-delivery"
                            type="date"
                            value={editPhase.estimated_delivery_date}
                            onChange={(e) => setEditPhase({ ...editPhase, estimated_delivery_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-actual-delivery" className="text-slate-600">
                            Actual Delivery <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <Input
                            id="edit-actual-delivery"
                            type="date"
                            value={editPhase.actual_delivery_date}
                            onChange={(e) => setEditPhase({ ...editPhase, actual_delivery_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-est-install" className="text-slate-600">
                            Est. Install <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <Input
                            id="edit-est-install"
                            type="date"
                            value={editPhase.estimated_install_date}
                            onChange={(e) => setEditPhase({ ...editPhase, estimated_install_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-actual-install" className="text-slate-600">
                            Actual Install <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              id="edit-actual-install"
                              type="date"
                              value={editPhase.actual_install_date}
                              onChange={(e) => setEditPhase({ ...editPhase, actual_install_date: e.target.value })}
                              className="flex-1"
                            />
                            {editPhase.actual_install_date && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setEditPhase({ ...editPhase, actual_install_date: '' })}
                                className="text-red-500 hover:text-red-700"
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-supplier">Supplier/Vendor</Label>
                          <Input
                            id="edit-supplier"
                            value={editPhase.supplier_vendor}
                            onChange={(e) => setEditPhase({ ...editPhase, supplier_vendor: e.target.value })}
                            placeholder="ABC Supply Co."
                          />
                        </div>
                      </>
                    )}
                    
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="edit-phase-contractor">
                        <Users className="w-4 h-4 inline mr-2" />
                        Assigned Contractor
                      </Label>
                      <div className="flex items-center gap-2">
                        <Select
                          value={editPhase.contractor_id || 'none'}
                          onValueChange={(value) => {
                            setEditPhase({ ...editPhase, contractor_id: value === 'none' ? null : value });
                          }}
                        >
                          <SelectTrigger id="edit-phase-contractor" className="flex-1">
                            <SelectValue placeholder="Select contractor (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No contractor</SelectItem>
                            {contractors.map((contractor) => (
                              <SelectItem key={contractor.id} value={contractor.id}>
                                {contractor.name} {contractor.company ? `- ${contractor.company}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setReturnToEditPhase(true);
                            setIsEditPhaseDialogOpen(false);
                            setTimeout(() => setIsContractorDialogOpen(true), 100);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="edit-phase-estimated">
                        <DollarSign className="w-4 h-4 inline mr-2" />
                        Estimated Cost
                      </Label>
                      <Input
                        id="edit-phase-estimated"
                        type="number"
                        step="0.01"
                        value={editPhase.estimated_cost}
                        onChange={(e) => setEditPhase({ ...editPhase, estimated_cost: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="edit-phase-actual">
                        <DollarSign className="w-4 h-4 inline mr-2" />
                        Actual Cost
                        {editPhase.contractor_id && (
                          <span className="ml-2 text-xs font-normal text-slate-500">(Invoice/actual amount)</span>
                        )}
                      </Label>
                      <Input
                        id="edit-phase-actual"
                        type="number"
                        step="0.01"
                        value={editPhase.actual_cost}
                        onChange={(e) => setEditPhase({ ...editPhase, actual_cost: e.target.value })}
                        placeholder="Enter actual cost"
                      />
                      <p className="text-xs text-slate-500">
                        The actual/invoice amount for this phase (can differ from estimate)
                      </p>
                    </div>
                    
                    {/* Amount Already Drawn - for tracking past reimbursements */}
                    <div className="space-y-2">
                      <Label htmlFor="edit-phase-amount-drawn" className="text-orange-700">
                        <DollarSign className="w-4 h-4 inline mr-2" />
                        Amount Already Drawn
                      </Label>
                      <Input
                        id="edit-phase-amount-drawn"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="border-orange-200 focus:border-orange-400"
                        value={editPhase.amount_drawn || ''}
                        onChange={(e) => setEditPhase({ ...editPhase, amount_drawn: e.target.value })}
                      />
                      <p className="text-xs text-orange-600">Amount already reimbursed by bank (won't show in new draw requests)</p>
                    </div>
                  </div>
                  
                  {/* Compliance Settings Section - For labor phases with contractors */}
                  {(editPhase.phase_type === 'labor' || editPhase.phase_type === 'labor_and_materials') && (
                    <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4" />
                        Compliance Requirements
                      </h4>
                      <p className="text-xs text-blue-600 mb-3">Configure what's required before payment can be processed</p>
                      
                      <div className="space-y-3">
                        {/* Requires City Inspection */}
                        <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center gap-3">
                            <ClipboardCheck className="w-5 h-5 text-amber-500" />
                            <div>
                              <p className="font-medium text-sm text-slate-700">Requires City Inspection</p>
                              <p className="text-xs text-slate-500">Contractor must upload passed inspection report</p>
                            </div>
                          </div>
                          <Switch
                            checked={editPhase.requires_inspection || false}
                            onCheckedChange={(checked) => setEditPhase({ ...editPhase, requires_inspection: checked })}
                          />
                        </div>
                        
                        {/* Lien Waiver Required */}
                        <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center gap-3">
                            <FileCheck className="w-5 h-5 text-green-500" />
                            <div>
                              <p className="font-medium text-sm text-slate-700">Lien Waiver Required</p>
                              <p className="text-xs text-slate-500">Contractor must sign lien waiver before payment</p>
                            </div>
                          </div>
                          <Switch
                            checked={editPhase.lien_waiver_required !== false}
                            onCheckedChange={(checked) => setEditPhase({ ...editPhase, lien_waiver_required: checked })}
                          />
                        </div>
                        
                        {/* Completion Photos Required */}
                        <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center gap-3">
                            <Camera className="w-5 h-5 text-purple-500" />
                            <div>
                              <p className="font-medium text-sm text-slate-700">Completion Photos Required</p>
                              <p className="text-xs text-slate-500">Contractor must upload proof of completed work</p>
                            </div>
                          </div>
                          <Switch
                            checked={editPhase.completion_photos_required !== false}
                            onCheckedChange={(checked) => setEditPhase({ ...editPhase, completion_photos_required: checked })}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Payment Summary Section - For contractor phases, directs to Pay button */}
                  {editPhase.contractor_id ? (
                    <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-green-800 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Payment Summary
                        </h4>
                        {editPhase.payment_status === 'final' && (
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full">
                            Final Payment Made
                          </span>
                        )}
                      </div>
                      
                      {/* Payment Stats */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                          <p className="text-xs text-slate-500">Total Paid</p>
                          <p className="font-bold text-green-700">
                            ${(phasePayments[editPhase.id] || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                          <p className="text-xs text-slate-500">Payments</p>
                          <p className="font-bold text-green-700">
                            {(phasePayments[editPhase.id] || []).length}
                          </p>
                        </div>
                      </div>
                      
                      {/* Recent Payments */}
                      {phasePayments[editPhase.id] && phasePayments[editPhase.id].length > 0 && (
                        <div className="space-y-1 mb-3 max-h-24 overflow-y-auto">
                          {phasePayments[editPhase.id].slice(0, 3).map((payment, idx) => (
                            <div key={payment.id || idx} className="flex items-center justify-between bg-white rounded px-2 py-1.5 text-xs border border-slate-100">
                              <span className="font-medium">${parseFloat(payment.amount).toLocaleString()}</span>
                              <span className="text-slate-500">{formatDate(payment.payment_date)}</span>
                              {payment.is_final_payment && (
                                <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px]">Final</span>
                              )}
                            </div>
                          ))}
                          {phasePayments[editPhase.id].length > 3 && (
                            <p className="text-xs text-slate-500 text-center">+{phasePayments[editPhase.id].length - 3} more payments</p>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-green-700 text-center">
                        Use the <strong>"Pay"</strong> button in the budget table to log payments
                      </p>
                    </div>
                  ) : (
                    /* For non-contractor phases, show simple actual cost info */
                    <div className="border border-slate-200 bg-slate-50 rounded-lg p-4">
                      <p className="text-sm text-slate-600 text-center">
                        No contractor assigned. Edit actual cost directly above.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Shipping and Tax Fields - Material Phases */}
                    {editPhase.phase_type === 'material' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="edit-phase-shipping">
                            <DollarSign className="w-4 h-4 inline mr-2" />
                            Shipping Fees
                          </Label>
                          <Input
                            id="edit-phase-shipping"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={editPhase.shipping_fees || ''}
                            onChange={(e) => setEditPhase({ ...editPhase, shipping_fees: e.target.value })}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="edit-phase-tax">
                            <DollarSign className="w-4 h-4 inline mr-2" />
                            Sales Tax
                          </Label>
                          <Input
                            id="edit-phase-tax"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={editPhase.sales_tax || ''}
                            onChange={(e) => setEditPhase({ ...editPhase, sales_tax: e.target.value })}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="edit-phase-cc-fees">
                            <DollarSign className="w-4 h-4 inline mr-2" />
                            Credit Card Fees
                          </Label>
                          <Input
                            id="edit-phase-cc-fees"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={editPhase.credit_card_fees || ''}
                            onChange={(e) => setEditPhase({ ...editPhase, credit_card_fees: e.target.value })}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="edit-phase-wire-fees">
                            <DollarSign className="w-4 h-4 inline mr-2" />
                            Wire Fees
                          </Label>
                          <Input
                            id="edit-phase-wire-fees"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={editPhase.wire_fees || ''}
                            onChange={(e) => setEditPhase({ ...editPhase, wire_fees: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                    
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="edit-bank-category">Hills Bank Category (Optional)</Label>
                      <p className="text-xs text-slate-500 mb-2">
                        Select a category for bank draw requests
                      </p>
                      <Select
                        value={editPhase.bank_category || 'none'}
                        onValueChange={(value) => setEditPhase({ ...editPhase, bank_category: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger id="edit-bank-category">
                          <SelectValue placeholder="Select Hills Bank category" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="none">None (Custom Category)</SelectItem>
                          {HILLS_BANK_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.code} value={cat.fullName}>
                              {cat.fullName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {editPhase.bank_category && editPhase.bank_category !== 'none' && (
                        <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Category mapped for bank draw requests
                        </div>
                      )}
                    </div>
                    
                    {/* Notes Section */}
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="edit-phase-notes">
                        Phase Notes
                        <span className="text-slate-400 font-normal ml-2">(Track changes, delays, price differences)</span>
                      </Label>
                      <textarea
                        id="edit-phase-notes"
                        className="w-full min-h-[100px] p-3 border border-slate-200 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Add notes about this phase... (e.g., price increased due to material shortage, delay caused by weather, etc.)"
                        value={editPhase.notes || ''}
                        onChange={(e) => setEditPhase({ ...editPhase, notes: e.target.value })}
                      />
                      <p className="text-xs text-slate-500">
                        Notes will be used for AI project summaries and lessons learned
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="destructive" 
                      onClick={handleDeletePhase}
                      className="flex-1"
                    >
                      Delete Phase
                    </Button>
                    <Button type="submit" className="flex-1">
                      Save Changes
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Phase Detail Modal - Modern Design */}
            <Dialog open={isPhaseDetailModalOpen} onOpenChange={setIsPhaseDetailModalOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
                {selectedPhaseForModal && (() => {
                  const phase = selectedPhaseForModal;
                  const isMaterial = phase.phase_type === 'material';
                  const isSoftCost = phase.phase_type === 'soft_cost';
                  const contractor = contractors.find(c => c.id === phase.contractor_id);
                  const autoStatus = getPhaseStatus(phase);
                  const estimated = parseFloat(phase.estimated_cost || 0);
                  const actual = parseFloat(phase.actual_cost || 0);
                  const remaining = estimated - actual;
                  
                  // Determine icon and badge based on phase type
                  const phaseIcon = isSoftCost ? '💰' : isMaterial ? '📦' : '🔨';
                  const phaseBadge = isSoftCost ? 'Soft Cost' : isMaterial ? 'Material' : 'Labor';
                  const badgeColor = isSoftCost ? 'bg-amber-500' : isMaterial ? 'bg-green-500' : 'bg-blue-500';
                  
                  return (
                    <>
                      {/* Header with gradient */}
                      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">{phaseIcon}</span>
                            <div>
                              <h2 className="text-xl font-bold text-white">{phase.name}</h2>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 text-white text-xs font-medium rounded ${badgeColor}`}>
                                  {phaseBadge}
                                </span>
                                <span className={`px-2 py-0.5 text-white text-xs font-medium rounded flex items-center gap-1 ${
                                  phase.is_paid ? 'bg-emerald-500' : 
                                  autoStatus === 'in_progress' ? 'bg-amber-500' : 'bg-slate-500'
                                }`}>
                                  {phase.is_paid ? (
                                    <><CheckCircle2 className="w-3 h-3" /> Paid</>
                                  ) : autoStatus === 'in_progress' ? (
                                    <><Clock className="w-3 h-3" /> Under Review</>
                                  ) : (
                                    'Unpaid'
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Modal Body */}
                      <div className="p-6 space-y-5 overflow-y-auto max-h-[60vh]">
                        {/* Cost Summary Grid */}
                        <div className="grid grid-cols-4 gap-3">
                          <div className="bg-slate-50 rounded-xl p-4 text-center">
                            <p className="text-slate-500 text-xs font-medium mb-1">Budget</p>
                            <p className="text-xl font-bold text-slate-800">${estimated.toLocaleString()}</p>
                          </div>
                          <div className="bg-slate-50 rounded-xl p-4 text-center">
                            <p className="text-slate-500 text-xs font-medium mb-1">Total Paid</p>
                            <p className="text-xl font-bold text-blue-600">${actual.toLocaleString()}</p>
                          </div>
                          <div className="bg-slate-50 rounded-xl p-4 text-center">
                            <p className="text-slate-500 text-xs font-medium mb-1">Remaining</p>
                            <p className={`text-xl font-bold ${remaining >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              ${Math.abs(remaining).toLocaleString()}
                            </p>
                          </div>
                          <div className="bg-slate-50 rounded-xl p-4 text-center">
                            <p className="text-slate-500 text-xs font-medium mb-1">Variance</p>
                            <p className={`text-xl font-bold ${remaining >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                              {remaining >= 0 ? '+' : '-'}${Math.abs(remaining).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        
                        {/* Info Cards Grid */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Timeline Card */}
                          <div className="bg-blue-50 rounded-xl p-4">
                            <h4 className="text-blue-700 text-sm font-semibold mb-3 flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Timeline
                            </h4>
                            <div className="space-y-2 text-sm">
                              {isMaterial ? (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Order Date</span>
                                    <span className="text-slate-800 font-medium">{phase.order_date ? formatDate(phase.order_date) : 'Not set'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Delivery</span>
                                    <span className="text-slate-800 font-medium">
                                      {phase.actual_delivery_date ? formatDate(phase.actual_delivery_date) :
                                       phase.estimated_delivery_date ? `${formatDate(phase.estimated_delivery_date)} (est)` : 'Not set'}
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Start Date</span>
                                    <span className="text-slate-800 font-medium">{phase.start_date ? formatDate(phase.start_date) : 'Not set'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">End Date</span>
                                    <span className="text-slate-800 font-medium">{phase.end_date ? formatDate(phase.end_date) : 'In Progress'}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Contractor Card */}
                          <div className="bg-purple-50 rounded-xl p-4">
                            <h4 className="text-purple-700 text-sm font-semibold mb-3 flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              {isMaterial ? 'Supplier' : 'Contractor'}
                            </h4>
                            <div className="space-y-2 text-sm">
                              {isMaterial ? (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Supplier</span>
                                    <span className="text-slate-800 font-medium">{phase.supplier_vendor || 'Not set'}</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Company</span>
                                    <span className="text-slate-800 font-medium">{contractor?.company || contractor?.name || 'Not assigned'}</span>
                                  </div>
                                  {contractor?.email && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Email</span>
                                      <span className="text-blue-600 font-medium truncate max-w-[150px]">{contractor.email}</span>
                                    </div>
                                  )}
                                  {contractor?.id && (
                                    <div className="pt-2 mt-2 border-t border-purple-200">
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="w-full text-xs"
                                        onClick={() => handleResendPortalEmail(contractor.id, contractor.email)}
                                        data-testid={`resend-portal-${phase.id}`}
                                      >
                                        <Mail className="w-3 h-3 mr-1" /> Resend Portal Link
                                      </Button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Category & Bank Info */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-slate-50 rounded-xl p-4">
                            <h4 className="text-slate-700 text-sm font-semibold mb-3">Category Info</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Category</span>
                                <span className="text-slate-800 font-medium">{phase.category || 'Uncategorized'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Bank Category</span>
                                <span className="text-slate-800 font-medium">{phase.bank_category || 'Not set'}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-slate-50 rounded-xl p-4">
                            <h4 className="text-slate-700 text-sm font-semibold mb-3">Payment Info</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between items-center">
                                <span className="text-slate-500">Status</span>
                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                  phase.is_paid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {phase.is_paid ? 'Paid' : 'Pending'}
                                </span>
                              </div>
                              {(phase.payment_proof_file_id || phase.supplier_payment_proof_file_id) && (
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500">Receipt</span>
                                  <button
                                    onClick={() => {
                                      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                                      const paymentType = isMaterial ? 'supplier' : 'contractor';
                                      const receiptUrl = `${apiUrl}/api/phases/${phase.id}/payment-proof/${paymentType}`;
                                      window.open(receiptUrl, '_blank');
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                                  >
                                    <FileText className="w-3 h-3" />
                                    View Receipt
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Phase Notes Section */}
                        {phase.notes && (
                          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                            <h4 className="text-amber-700 text-sm font-semibold mb-2 flex items-center gap-2">
                              <Pencil className="w-4 h-4" />
                              Phase Notes
                            </h4>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{phase.notes}</p>
                          </div>
                        )}
                        
                        {/* AI Receipt Scanning Section */}
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-slate-700 text-sm font-semibold flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-purple-600" />
                              Receipts & Documentation
                            </h4>
                            <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                              {phaseReceipts.length} receipt{phaseReceipts.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          
                          {/* Upload Button */}
                          <div className="mb-3">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              id="receipt-upload"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                
                                setIsUploadingReceipt(true);
                                try {
                                  const res = await apiService.scanReceipt(phase.id, projectId, file);
                                  toast.success(`Receipt scanned: $${res.data.receipt.amount?.toFixed(2) || '0.00'} from ${res.data.receipt.vendor || 'Unknown'}`);
                                  setPhaseReceipts(prev => [...prev, res.data.receipt]);
                                  setReceiptsTotalAmount(res.data.total_receipts_amount || 0);
                                } catch (error) {
                                  toast.error('Failed to scan receipt');
                                  console.error(error);
                                } finally {
                                  setIsUploadingReceipt(false);
                                  e.target.value = '';
                                }
                              }}
                            />
                            <label
                              htmlFor="receipt-upload"
                              data-testid="upload-receipt-btn"
                              className={`flex items-center justify-center gap-2 w-full py-2.5 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors ${isUploadingReceipt ? 'opacity-50 pointer-events-none' : ''}`}
                            >
                              {isUploadingReceipt ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                  <span className="text-sm text-purple-600 font-medium">Scanning with AI...</span>
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 text-purple-600" />
                                  <span className="text-sm text-purple-600 font-medium">Upload Receipt / Invoice</span>
                                </>
                              )}
                            </label>
                            <p className="text-xs text-slate-500 mt-1 text-center">AI will auto-extract amount, vendor, and date from photos</p>
                          </div>
                          
                          {/* Receipts List */}
                          {phaseReceipts.length > 0 && (
                            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                              {phaseReceipts.map((receipt) => (
                                <div key={receipt.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2.5 text-sm border border-slate-200 hover:border-purple-300 transition-colors" data-testid={`receipt-item-${receipt.id}`}>
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                      <FileText className="w-4 h-4 text-purple-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-800">${(receipt.amount || 0).toFixed(2)}</span>
                                        <span className="text-slate-400">•</span>
                                        <span className="text-slate-600 truncate">{receipt.vendor || 'Unknown Vendor'}</span>
                                      </div>
                                      <div className="text-xs text-slate-400 mt-0.5">
                                        {receipt.date ? formatDate(receipt.date) : 'No date'}
                                        {receipt.description && <span className="ml-2 text-slate-500">- {receipt.description}</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 ml-2">
                                    {/* View Receipt Button */}
                                    <button
                                      onClick={async () => {
                                        try {
                                          toast.info('Loading receipt...');
                                          const response = await apiService.getReceiptImage(receipt.id);
                                          
                                          // Check if we got valid data
                                          if (!response.data) {
                                            throw new Error('No data received');
                                          }
                                          
                                          // Get content type from response headers
                                          const contentType = response.headers?.['content-type'] || 'image/jpeg';
                                          
                                          // Create blob from response
                                          const blob = response.data instanceof Blob 
                                            ? response.data 
                                            : new Blob([response.data], { type: contentType });
                                          
                                          // Check blob size
                                          if (blob.size < 100) {
                                            throw new Error('Receipt file appears to be empty or corrupted');
                                          }
                                          
                                          const url = window.URL.createObjectURL(blob);
                                          
                                          // Open in new tab
                                          const newTab = window.open(url, '_blank');
                                          if (!newTab) {
                                            // Fallback: create download link
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `receipt-${receipt.id}.${contentType.split('/')[1] || 'jpg'}`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                          }
                                          
                                          // Clean up URL after delay
                                          setTimeout(() => window.URL.revokeObjectURL(url), 5000);
                                        } catch (e) {
                                          console.error('Receipt download error:', e);
                                          toast.error(`Failed to open receipt: ${e.message || 'Unknown error'}`);
                                        }
                                      }}
                                      className="p-1.5 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                                      title="View Receipt"
                                      data-testid={`view-receipt-${receipt.id}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </button>
                                    {/* Edit Button — fix AI mis-parsed total/vendor */}
                                    <button
                                      onClick={() => openEditReceipt(receipt)}
                                      className="p-1.5 rounded-lg text-amber-500 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                                      title="Edit receipt total"
                                      data-testid={`edit-receipt-${receipt.id}`}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    {/* Delete Button */}
                                    <button
                                      onClick={async () => {
                                        if (window.confirm('Delete this receipt?')) {
                                          try {
                                            await apiService.deleteReceipt(receipt.id);
                                            setPhaseReceipts(prev => prev.filter(r => r.id !== receipt.id));
                                            setReceiptsTotalAmount(prev => prev - (receipt.amount || 0));
                                            toast.success('Receipt deleted');
                                          } catch (e) {
                                            toast.error('Failed to delete receipt');
                                          }
                                        }
                                      }}
                                      className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                      title="Delete Receipt"
                                      data-testid={`delete-receipt-${receipt.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Edit Receipt Dialog — fix AI mis-parsed total/vendor without deleting */}
                          <Dialog open={!!editingReceipt} onOpenChange={(o) => { if (!o) setEditingReceipt(null); }}>
                            <DialogContent className="max-w-sm" data-testid="edit-receipt-dialog">
                              <DialogHeader>
                                <DialogTitle>Edit receipt</DialogTitle>
                                <DialogDescription>Correct the total or vendor if the AI scan got it wrong.</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-3 py-1">
                                <div>
                                  <label className="text-xs font-medium text-slate-600">Total amount</label>
                                  <div className="flex items-center gap-1 mt-1">
                                    <span className="text-slate-500">$</span>
                                    <Input
                                      type="number" step="0.01" min="0"
                                      value={editReceiptAmount}
                                      onChange={(e) => setEditReceiptAmount(e.target.value)}
                                      data-testid="edit-receipt-amount-input"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-slate-600">Vendor</label>
                                  <Input
                                    className="mt-1"
                                    value={editReceiptVendor}
                                    onChange={(e) => setEditReceiptVendor(e.target.value)}
                                    placeholder="Vendor name"
                                    data-testid="edit-receipt-vendor-input"
                                  />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button variant="ghost" onClick={() => setEditingReceipt(null)} disabled={savingReceiptEdit}>Cancel</Button>
                                <Button onClick={handleSaveReceiptEdit} disabled={savingReceiptEdit} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="edit-receipt-save-btn">
                                  {savingReceiptEdit ? 'Saving…' : 'Save'}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>

                            <div className="text-center py-4 text-slate-400 text-sm">
                              <Camera className="w-6 h-6 mx-auto mb-2 opacity-50" />
                              No receipts uploaded yet
                            </div>
                          )}
                          
                          {/* Total & Apply Button - Only for phases without contractors */}
                          {phaseReceipts.length > 0 && (
                            <div className="flex items-center justify-between pt-3 border-t border-purple-200">
                              <div>
                                <span className="text-xs text-slate-500">Total from receipts:</span>
                                <span className="ml-2 font-bold text-purple-700">${receiptsTotalAmount.toFixed(2)}</span>
                              </div>
                              {phase.contractor_id ? (
                                <div className="text-xs text-amber-600 flex items-center gap-1">
                                  <Receipt className="w-3 h-3" />
                                  Use "Pay" button to add costs
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const currentCost = parseFloat(phase.actual_cost) || 0;
                                      const result = await apiService.applyReceiptsToPhase(phase.id);
                                      const newTotal = currentCost + receiptsTotalAmount;
                                      toast.success(`Added $${receiptsTotalAmount.toFixed(2)} to actual cost (new total: $${newTotal.toFixed(2)})`);
                                      loadProjectData();
                                    } catch (e) {
                                      toast.error('Failed to apply receipts total');
                                    }
                                  }}
                                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-1.5 h-auto"
                                  data-testid="apply-receipts-btn"
                                >
                                  Add to Actual Cost
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Modal Footer */}
                      <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200">
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this phase?')) {
                              try {
                                await apiService.deletePhase(phase.id);
                                toast.success('Phase deleted');
                                setIsPhaseDetailModalOpen(false);
                                loadProjectData();
                              } catch (error) {
                                toast.error('Failed to delete phase');
                              }
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Phase
                        </button>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            onClick={() => setIsPhaseDetailModalOpen(false)}
                          >
                            Close
                          </Button>
                          <Button
                            onClick={() => {
                              handleEditPhase(phase);
                              setIsPhaseDetailModalOpen(false);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit Phase
                          </Button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </DialogContent>
            </Dialog>

            {/* Modern Phases Table */}
            <div className="space-y-4">
              {/* Main Funds Available Card - Most Prominent */}
              <Card className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-green-100 text-sm font-medium">Funds Available for Draw</p>
                      </div>
                      <p className="text-4xl md:text-5xl font-bold text-white">
                        ${Math.max(0, (project?.loan_available_for_draw || ((project?.loan_principal_amount || 0) - (project?.amount_drawn || 0)))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-green-100 text-sm mt-2">Based on verified expenses from Loan Details</p>
                    </div>
                    {can(PERMISSIONS.REQUEST_DRAWS) && (
                      <Button 
                        onClick={openDrawRequest}
                        className="text-white hover:opacity-90 font-semibold rounded-full px-8 py-3 text-lg shadow-md"
                        style={{ backgroundColor: '#1E4ED8' }}
                        data-testid="request-draw-btn-banner"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        New Draw Request
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Budget Summary Card */}
              <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                <CardContent className="p-5">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left: Financial Summary */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-slate-700">Budget Status:</h2>
                        <div className="flex gap-2">
                          <select 
                            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                            value={budgetStatusFilter}
                            onChange={(e) => setBudgetStatusFilter(e.target.value)}
                          >
                            <option value="all">All Budget Statuses</option>
                            <option value="under_budget">Under Budget</option>
                            <option value="on_budget">On Budget</option>
                            <option value="over_budget">Over Budget</option>
                          </select>
                          <select 
                            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                            value={paymentStatusFilter}
                            onChange={(e) => setPaymentStatusFilter(e.target.value)}
                          >
                            <option value="all">All Payment Statuses</option>
                            <option value="paid">Paid</option>
                            <option value="unpaid">Unpaid</option>
                            <option value="partial">Partial Payment</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Key Financial Numbers - 3 cards */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-amber-50 rounded-xl p-4">
                          <p className="text-amber-600 text-xs font-medium mb-1">Funds Drawn</p>
                          <p className="text-2xl font-bold text-amber-700">
                            ${(project?.loan_principal_amount ? Math.min(project?.amount_drawn || 0, project.loan_principal_amount) : (project?.amount_drawn || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-amber-600 mt-1">Bank loan drawn</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <div className="flex items-center gap-1 mb-1">
                            <p className="text-slate-500 text-xs font-medium">Actual Spend</p>
                            <div className="relative group">
                              <HelpCircle className="w-3 h-3 text-slate-400 cursor-help" />
                              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 shadow-lg">
                                Does not include cost of land/purchase price or closing costs
                                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900"></div>
                              </div>
                            </div>
                          </div>
                          <p className="text-2xl font-bold text-slate-800">
                            ${(budgetStatus.actualSpend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">Paid to contractors</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4">
                          <p className="text-blue-600 text-xs font-medium mb-1">Projected Final</p>
                          <p className="text-2xl font-bold text-blue-700">${budgetStatus.projectedSpend.toLocaleString()}</p>
                          <p className={`text-xs ${budgetStatus.projectedVsInitial <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {budgetStatus.projectedVsInitial <= 0 
                              ? `✓ $${Math.abs(budgetStatus.projectedVsInitial).toLocaleString()} under budget`
                              : `⚠ $${budgetStatus.projectedVsInitial.toLocaleString()} over budget`}
                          </p>
                        </div>
                      </div>
                      
                      {/* Progress Bars */}
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">Total Budget</span>
                            <span className="text-sm font-bold text-slate-800">${budgetStatus.initialBudget.toLocaleString()}</span>
                          </div>
                          <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full transition-all duration-500" 
                              style={{ width: `${Math.min(100, (budgetStatus.actualSpend / Math.max(1, budgetStatus.initialBudget)) * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">Total Spent</span>
                            <span className="text-sm font-bold text-slate-800">${budgetStatus.actualSpend.toLocaleString()}</span>
                          </div>
                          <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden flex">
                            <div 
                              className="h-full bg-emerald-500 transition-all duration-500" 
                              style={{ width: `${Math.min(100, (phases.filter(p => p.is_paid).reduce((sum, p) => sum + parseFloat(p.actual_cost || 0), 0) / Math.max(1, budgetStatus.actualSpend)) * 100)}%` }}
                            ></div>
                            <div 
                              className="h-full bg-amber-500 transition-all duration-500" 
                              style={{ width: `${Math.min(100, (phases.filter(p => !p.is_paid && parseFloat(p.actual_cost || 0) > 0).reduce((sum, p) => sum + parseFloat(p.actual_cost || 0), 0) / Math.max(1, budgetStatus.actualSpend)) * 100)}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between mt-1 text-xs">
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                              <span className="text-slate-500">Paid</span>
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                              <span className="text-slate-500">Pending</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right: Timeline Status + Contingency */}
                    <div className="space-y-3">
                      {/* Timeline Status Card */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          <h3 className="text-sm font-semibold text-blue-800">Timeline Status</h3>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-2xl font-bold text-blue-700">
                              {timelineStatus.daysRemaining > 0 ? timelineStatus.daysRemaining : 0} days
                            </p>
                            <p className="text-sm text-blue-600">
                              {timelineStatus.isPastDue ? 'past due' : 'remaining'}
                            </p>
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-slate-600">Start: <span className="font-medium">
                              {project?.start_date ? formatDateWithTimezone(project.start_date, project?.timezone) : 'Not set'}
                            </span></p>
                            <p className="text-slate-600">End: <span className="font-medium">
                              {project?.projected_completion_date ? formatDateWithTimezone(project.projected_completion_date, project?.timezone) : 'Not set'}
                            </span></p>
                            {project?.timezone && (
                              <p className="text-xs text-blue-500 mt-0.5 flex items-center justify-end gap-1">
                                <Globe className="h-3 w-3" />
                                {getTimezoneAbbrev(project.timezone)}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Timeline Progress */}
                        <div className="mt-3">
                          <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${timelineStatus.isPastDue ? 'bg-red-500' : 'bg-blue-600'}`}
                              style={{ width: `${Math.min(100, timelineStatus.percentComplete || 0)}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-blue-500 mt-1">{Math.round(timelineStatus.percentComplete || 0)}% of timeline elapsed</p>
                        </div>
                      </div>
                      
                      {/* Contingency Status Card */}
                      {budgetStatus.contingencyBudget > 0 && (
                        <div className={`rounded-xl p-4 border ${budgetStatus.overBudget ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' : 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className={`w-5 h-5 ${budgetStatus.overBudget ? 'text-red-600' : 'text-emerald-600'}`} />
                            <h3 className={`text-sm font-semibold ${budgetStatus.overBudget ? 'text-red-800' : 'text-emerald-800'}`}>
                              Contingency Reserve
                            </h3>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-2xl font-bold ${budgetStatus.overBudget ? 'text-red-700' : 'text-emerald-700'}`}>
                                ${budgetStatus.contingencyRemaining.toLocaleString()}
                              </p>
                              <p className={`text-sm ${budgetStatus.overBudget ? 'text-red-600' : 'text-emerald-600'}`}>
                                remaining
                              </p>
                            </div>
                            <div className="text-right text-sm">
                              <p className="text-slate-600">Budget: <span className="font-medium">${budgetStatus.contingencyBudget.toLocaleString()}</span></p>
                              {budgetStatus.contingencyUsed > 0 && (
                                <p className="text-red-600">Used: <span className="font-medium">-${budgetStatus.contingencyUsed.toLocaleString()}</span></p>
                              )}
                            </div>
                          </div>
                          {/* Contingency Progress */}
                          <div className="mt-3">
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${budgetStatus.contingencyUsed > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}
                                style={{ width: `${Math.min(100, (budgetStatus.contingencyUsed / budgetStatus.contingencyBudget) * 100)}%` }}
                              ></div>
                            </div>
                            <p className={`text-xs mt-1 ${budgetStatus.overBudget ? 'text-red-500' : 'text-emerald-500'}`}>
                              {Math.round((budgetStatus.contingencyUsed / budgetStatus.contingencyBudget) * 100)}% of contingency used
                            </p>
                          </div>
                          {budgetStatus.overBudget && (
                            <div className="mt-3 p-2 bg-red-100 rounded-lg border border-red-200">
                              <p className="text-xs text-red-700 font-medium flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Project is ${budgetStatus.overAmount.toLocaleString()} over budget
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* v2.5.5.16 — Cost-per-square-foot snapshot. Excludes
                          phases flagged exclude_from_sqft (permits, fees,
                          insurance, etc.) so the number reflects pure build
                          cost — the metric developers benchmark next bids
                          against. */}
                      {(() => {
                        const sqft = parseFloat(project?.sqft) || 0;
                        if (sqft <= 0) {
                          return (
                            <div className="rounded-xl p-4 border border-slate-200 bg-slate-50">
                              <div className="flex items-center gap-2 mb-1">
                                <Building2 className="w-5 h-5 text-slate-500" />
                                <h3 className="text-sm font-semibold text-slate-700">Cost per sqft</h3>
                              </div>
                              <p className="text-xs text-slate-500">
                                Add a square-footage to this project (Edit Project → Basic Info) to start tracking $/sqft for benchmarking future builds.
                              </p>
                            </div>
                          );
                        }
                        const NON_BUILD_SQFT = ['land purchase','land acquisition','purchase price','financ','loan interest','interest reserve','closing cost','bank fee','origination','lender fee','landscap','swimming pool','retaining wall','tree plant','furnish','furniture','curtain','blind'];
                        const isBuildPhase = (p) => { if (p.exclude_from_sqft) return false; const t = `${p.name || ''} ${p.category || ''}`.toLowerCase(); return !NON_BUILD_SQFT.some(k => t.includes(k)); };
                        const buildPhases = (phases || []).filter(isBuildPhase);
                        const buildEstimated = buildPhases.reduce((s, p) => s + (parseFloat(p.estimated_cost) || 0), 0);
                        const buildActual = buildPhases.reduce((s, p) => s + (parseFloat(p.actual_cost) || 0), 0);
                        const estPerSqft = buildEstimated / sqft;
                        const actPerSqft = buildActual / sqft;
                        const variance = estPerSqft > 0 ? ((actPerSqft - estPerSqft) / estPerSqft) * 100 : 0;
                        const isUnder = variance < 0;
                        return (
                          <div className="rounded-xl p-4 border bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200" data-testid="cost-per-sqft-card">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-violet-600" />
                                <h3 className="text-sm font-semibold text-violet-800">Cost per sqft</h3>
                              </div>
                              <span className="text-[10px] uppercase tracking-wider font-bold bg-white text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded">
                                {sqft.toLocaleString()} sqft
                              </span>
                            </div>
                            <div className="flex items-end justify-between gap-3">
                              <div>
                                <p className="text-2xl font-bold text-violet-700 tabular-nums" data-testid="actual-per-sqft">
                                  ${actPerSqft.toFixed(2)}
                                </p>
                                <p className="text-xs text-violet-600">actual per sqft</p>
                              </div>
                              <div className="text-right text-xs">
                                <p className="text-slate-600">Estimated: <span className="font-semibold tabular-nums">${estPerSqft.toFixed(2)}</span></p>
                                {estPerSqft > 0 && (
                                  <p className={isUnder ? 'text-emerald-700' : 'text-red-600'} data-testid="cost-per-sqft-variance">
                                    {isUnder ? '▼' : '▲'} {Math.abs(variance).toFixed(1)}% {isUnder ? 'under' : 'over'}
                                  </p>
                                )}
                              </div>
                            </div>
                            <p className="text-[10px] text-violet-600/80 mt-2 leading-snug">
                              Hard construction cost only — land, financing, major landscaping, furnishings, permits, insurance and other fees are excluded. Toggle "Exclude from $/sqft" on any phase to keep it out too.
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Modern Phases Table */}
              <Card className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-tour="phases-table">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-4 px-4 font-semibold text-slate-600 w-12">#</th>
                          <th className="text-left py-4 px-4 font-semibold text-slate-600">Phase</th>
                          <th className="text-left py-4 px-4 font-semibold text-slate-600">Budget</th>
                          <th className="text-left py-4 px-4 font-semibold text-slate-600">Total Paid</th>
                          <th className="text-center py-4 px-4 font-semibold text-slate-600">
                            <div className="flex items-center justify-center gap-1 group relative">
                              Drawn
                              <div className="relative">
                                <svg className="w-4 h-4 text-blue-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 shadow-lg">
                                  Check when fully reimbursed from bank
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800"></div>
                                </div>
                              </div>
                            </div>
                          </th>
                          <th className="text-center py-4 px-4 font-semibold text-slate-600">
                            <div className="flex items-center justify-center gap-1 group relative">
                              Details
                              <div className="relative">
                                <svg className="w-4 h-4 text-blue-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 shadow-lg">
                                  Click to view full phase info and edit
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800"></div>
                                </div>
                              </div>
                            </div>
                          </th>
                          <th className="text-center py-4 px-4 font-semibold text-slate-600">Payment Status</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Soft Costs Rows from Expenses Collection */}
                        {projectExpenses.map((expense, index) => {
                          const expenseIcons = {
                            permit: '🏛️',
                            utility: '⚡',
                            rental: '🚛',
                            disposal: '🗑️',
                            porta_potty: '🚽',
                            insurance: '🏠',
                            other: '📋',
                          };
                          const estimated = expense.estimated_amount || 0;
                          const actual = parseFloat(expense.amount || 0);
                          const isRunningTab = expense.is_running_tab || ['utility', 'disposal', 'porta_potty'].includes(expense.expense_type);
                          const paymentCount = expense.payment_entries?.length || 0;
                          const isExpanded = expandedExpenseId === expense.id;
                          
                          return (
                            <React.Fragment key={`expense-${expense.id}`}>
                              <tr className="border-b border-slate-100 hover:bg-amber-50/50 transition-colors bg-amber-50/30">
                                <td className="py-4 px-4 text-slate-500">{index + 1}</td>
                                <td className="py-4 px-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{expenseIcons[expense.expense_type] || '💰'}</span>
                                    <span className="text-slate-800 font-medium">{expense.description || expense.expense_type?.replace('_', ' ')}</span>
                                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Soft Cost</span>
                                    {isRunningTab && (
                                      <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">Running Tab</span>
                                    )}
                                  </div>
                                  {expense.vendor_name && (
                                    <p className="text-xs text-slate-500 mt-1">{expense.vendor_name}</p>
                                  )}
                                </td>
                                <td className="py-4 px-4 text-blue-600 font-medium">${estimated.toLocaleString()}</td>
                                <td className="py-4 px-4 text-slate-700">
                                  ${actual.toLocaleString()}
                                  {paymentCount > 0 && (
                                    <button
                                      onClick={() => setExpandedExpenseId(isExpanded ? null : expense.id)}
                                      className="ml-2 text-xs text-orange-600 hover:text-orange-800"
                                    >
                                      ({paymentCount} payment{paymentCount !== 1 ? 's' : ''} {isExpanded ? '▲' : '▼'})
                                    </button>
                                  )}
                                </td>
                                <td className="py-4 px-4 text-center">
                                  <label className="flex items-center justify-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={expense.is_drawn || false}
                                      onChange={(e) => handleMarkExpenseAsFullyDrawn(expense, e.target.checked)}
                                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                    />
                                  </label>
                                </td>
                                <td className="py-4 px-4 text-center">
                                  <span className="text-xs text-slate-400">-</span>
                                </td>
                                <td className="py-4 px-4 text-center">
                                  {expense.is_paid ? (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-sm font-medium">
                                      <CheckCircle2 className="w-4 h-4" />
                                      Paid
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-400 text-white rounded-lg text-sm font-medium">
                                      <Clock className="w-4 h-4" />
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="py-4 px-4">
                                  <div className="flex items-center gap-2">
                                    {/* Partial Payment button - available for ALL soft costs */}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 text-xs"
                                      onClick={() => {
                                        setSelectedExpenseForPayment(expense);
                                        setIsExpensePaymentDialogOpen(true);
                                      }}
                                      data-testid={`add-payment-expense-${expense.id}`}
                                    >
                                      + Add Payment
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 text-xs"
                                      onClick={() => {
                                        setEditExpense({
                                          ...expense,
                                          estimated_amount: expense.estimated_amount || '',
                                          amount: expense.amount || 0,
                                        });
                                        setIsEditExpenseDialogOpen(true);
                                      }}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-600 hover:text-red-800 hover:bg-red-50 text-xs"
                                      onClick={() => handleDeleteExpense(expense.id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                              {/* Expanded payment history for running tabs */}
                              {isExpanded && isRunningTab && expense.payment_entries?.length > 0 && (
                                <tr className="bg-orange-50/50">
                                  <td colSpan="7" className="py-4 px-8">
                                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                                      <h4 className="text-slate-800 font-medium mb-3">Payment History</h4>
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-slate-500 border-b">
                                            <th className="text-left py-2">Date</th>
                                            <th className="text-left py-2">Amount</th>
                                            <th className="text-left py-2">Description</th>
                                            <th className="text-left py-2"></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {expense.payment_entries.map((payment, pIdx) => (
                                            <tr key={pIdx} className="border-b border-slate-100">
                                              <td className="py-2">{payment.date ? formatDate(payment.date) : '-'}</td>
                                              <td className="py-2 text-emerald-600 font-medium">${parseFloat(payment.amount || 0).toLocaleString()}</td>
                                              <td className="py-2 text-slate-600">{payment.description || '-'}</td>
                                              <td className="py-2">
                                                <button
                                                  onClick={() => handleDeleteExpensePayment(expense.id, payment.id)}
                                                  className="text-red-500 hover:text-red-700 text-xs"
                                                >
                                                  Remove
                                                </button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                        <tfoot className="border-t">
                                          <tr className="font-medium">
                                            <td className="py-2">Total</td>
                                            <td className="py-2 text-emerald-600">${actual.toLocaleString()}</td>
                                            <td colSpan="2"></td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        
                        {/* Soft Cost Phases - Show with expenses */}
                        {phases.filter(p => p.phase_type === 'soft_cost').map((phase, index) => {
                          const estimated = parseFloat(phase.estimated_cost || 0);
                          const actual = parseFloat(phase.actual_cost || 0);
                          
                          return (
                            <tr key={`soft-cost-phase-${phase.id}`} className="border-b border-slate-100 hover:bg-amber-50/50 transition-colors bg-amber-50/30">
                              <td className="py-4 px-4 text-slate-500">{projectExpenses.length + index + 1}</td>
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">💰</span>
                                  <span className="text-slate-800 font-medium">{phase.name}</span>
                                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Soft Cost</span>
                                </div>
                                {phase.category && (
                                  <p className="text-xs text-slate-500 mt-1">{phase.category}</p>
                                )}
                              </td>
                              <td className="py-4 px-4 text-blue-600 font-medium">${estimated.toLocaleString()}</td>
                              <td className="py-4 px-4 text-slate-700">${actual.toLocaleString()}</td>
                              {/* Drawn column - checkbox */}
                              <td className="py-4 px-4 text-center">
                                {actual > 0 ? (
                                  <label className="flex items-center justify-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={phase.is_drawn || false}
                                      onChange={(e) => handleMarkPhaseDrawn(phase.id, e.target.checked)}
                                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                    />
                                  </label>
                                ) : (
                                  <label className="flex items-center justify-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={phase.is_drawn || false}
                                      onChange={(e) => handleMarkPhaseDrawn(phase.id, e.target.checked)}
                                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                    />
                                  </label>
                                )}
                              </td>
                              {/* Details column - shows "-" for soft costs */}
                              <td className="py-4 px-4 text-center">
                                <span className="text-xs text-slate-400">-</span>
                              </td>
                              {/* Payment Status column */}
                              <td className="py-4 px-4 text-center">
                                {phase.is_paid ? (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-sm font-medium">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Paid
                                  </span>
                                ) : actual > 0 ? (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-400 text-white rounded-lg text-sm font-medium">
                                    <Clock className="w-4 h-4" />
                                    Pending
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-400 text-white rounded-lg text-sm font-medium">
                                    <Clock className="w-4 h-4" />
                                    Pending
                                  </span>
                                )}
                              </td>
                              {/* Actions column */}
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 text-xs"
                                    onClick={() => {
                                      setCurrentPhaseForPayment(phase);
                                      setIsAddPaymentDialogOpen(true);
                                    }}
                                  >
                                    + Add Payment
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 text-xs"
                                    onClick={() => handleEditPhase(phase)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-800 hover:bg-red-50 text-xs"
                                    onClick={async () => {
                                      if (window.confirm(`Delete "${phase.name}"?`)) {
                                        try {
                                          await apiService.deletePhase(phase.id);
                                          toast.success('Soft cost deleted');
                                          loadProjectData();
                                        } catch (error) {
                                          toast.error('Failed to delete');
                                        }
                                      }
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {/* Phase Rows - Apply Filters (excluding soft costs) */}
                        {phases.filter((phase) => {
                          // Filter out soft costs - they should be shown in expenses section
                          if (phase.phase_type === 'soft_cost') return false;
                          
                          const estimated = parseFloat(phase.estimated_cost || 0);
                          const actual = parseFloat(phase.actual_cost || 0);
                          const partialPaymentsTotal = phasePayments[phase.id] 
                            ? phasePayments[phase.id].reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) 
                            : 0;
                          const hasPartialPayment = partialPaymentsTotal > 0 && !phase.is_paid;
                          
                          // Budget Status Filter
                          if (budgetStatusFilter !== 'all') {
                            if (budgetStatusFilter === 'under_budget' && !(actual > 0 && estimated > 0 && actual < estimated)) return false;
                            if (budgetStatusFilter === 'on_budget' && !(actual > 0 && estimated > 0 && Math.abs(actual - estimated) < 0.01)) return false;
                            if (budgetStatusFilter === 'over_budget' && !(actual > 0 && estimated > 0 && actual > estimated)) return false;
                          }
                          
                          // Payment Status Filter
                          if (paymentStatusFilter !== 'all') {
                            if (paymentStatusFilter === 'paid' && !phase.is_paid) return false;
                            if (paymentStatusFilter === 'unpaid' && (phase.is_paid || hasPartialPayment)) return false;
                            if (paymentStatusFilter === 'partial' && !hasPartialPayment) return false;
                          }
                          
                          return true;
                        }).map((phase, index) => {
                          const estimated = parseFloat(phase.estimated_cost || 0);
                          const actual = parseFloat(phase.actual_cost || 0);
                          // Calculate total paid including partial payments
                          const partialPaymentsTotal = phasePayments[phase.id] 
                            ? phasePayments[phase.id].reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) 
                            : 0;
                          // Total paid: partial payments if any; else only count
                          // actual_cost as paid if is_paid/supplier_paid is true.
                          // Otherwise actual_cost is the work cost, not paid out.
                          let totalPaid = 0;
                          if (partialPaymentsTotal > 0) totalPaid = partialPaymentsTotal;
                          else if (phase.is_paid || phase.supplier_paid) totalPaid = actual;
                          const autoStatus = getPhaseStatus(phase);
                          const isMaterial = phase.phase_type === 'material';
                          
                          return (
                            <tr key={phase.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="py-4 px-4 text-slate-500">{projectExpenses.length + index + 1}</td>
                              <td className="py-4 px-4">
                                <button
                                  onClick={async () => {
                                    setSelectedPhaseForModal(phase);
                                    setIsPhaseDetailModalOpen(true);
                                    // Load receipts for this phase
                                    try {
                                      const res = await apiService.getPhaseReceipts(phase.id);
                                      setPhaseReceipts(res.data.receipts || []);
                                      setReceiptsTotalAmount(res.data.total_amount || 0);
                                    } catch (e) {
                                      setPhaseReceipts([]);
                                      setReceiptsTotalAmount(0);
                                    }
                                  }}
                                  className="text-blue-600 hover:text-blue-800 font-medium text-left flex items-center gap-2"
                                >
                                  <span className="text-lg">{isMaterial ? '📦' : '🔨'}</span>
                                  {phase.name}
                                </button>
                              </td>
                              <td className="py-4 px-4 text-blue-600 font-medium">${estimated.toLocaleString()}</td>
                              <td className="py-4 px-4 text-slate-700">
                                ${totalPaid.toLocaleString()}
                                {partialPaymentsTotal > 0 && partialPaymentsTotal < estimated && (
                                  <span className="text-xs text-amber-600 ml-1">(partial)</span>
                                )}
                              </td>
                              <td className="py-4 px-4 text-center">
                                {totalPaid > 0 ? (
                                  <label className="flex items-center justify-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={(parseFloat(phase.amount_drawn) || 0) >= totalPaid && totalPaid > 0}
                                      onChange={(e) => handleMarkAsFullyDrawn(phase, e.target.checked)}
                                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                    />
                                  </label>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </td>
                              <td className="py-4 px-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {can(PERMISSIONS.RECORD_PAYMENTS) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        // Add contractor name to phase for display
                                        const contractor = contractors.find(c => c.id === phase.contractor_id);
                                        setSelectedPhaseForPayment({
                                          ...phase,
                                          contractor_name: contractor?.company || contractor?.name || null
                                        });
                                        setIsPhasePaymentDialogOpen(true);
                                      }}
                                      className="px-3 py-1.5 bg-green-100 hover:bg-green-200 rounded-lg text-sm text-green-700 font-medium h-auto"
                                      data-testid="pay-phase-btn"
                                    >
                                      <Receipt className="w-3 h-3 mr-1" />
                                      Pay
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      setSelectedPhaseForModal(phase);
                                      setIsPhaseDetailModalOpen(true);
                                      // Load receipts for this phase
                                      try {
                                        const res = await apiService.getPhaseReceipts(phase.id);
                                        setPhaseReceipts(res.data.receipts || []);
                                        setReceiptsTotalAmount(res.data.total_amount || 0);
                                      } catch (e) {
                                        setPhaseReceipts([]);
                                        setReceiptsTotalAmount(0);
                                      }
                                    }}
                                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 font-medium h-auto"
                                  >
                                    Details
                                  </Button>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-center">
                                {renderStatusBadge(phase)}
                              </td>
                              <td className="py-4 px-4">
                                {phase.is_paid ? (
                                  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-6 h-6 text-red-300 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200">
                        <tr>
                          <td className="py-4 px-4 text-slate-500">=</td>
                          <td className="py-4 px-4 font-semibold text-slate-800">Totals</td>
                          <td className="py-4 px-4 font-bold text-blue-600">
                            ${(phases.reduce((sum, p) => sum + parseFloat(p.estimated_cost || 0), 0) + projectExpenses.reduce((sum, e) => sum + (e.estimated_amount || 0), 0)).toLocaleString()}
                          </td>
                          <td className="py-4 px-4 font-bold text-slate-800">
                            ${(phases.reduce((sum, p) => {
                              // Include partial payments in total
                              const partialTotal = phasePayments[p.id] 
                                ? phasePayments[p.id].reduce((s, pay) => s + parseFloat(pay.amount || 0), 0) 
                                : 0;
                              return sum + (partialTotal > 0 ? partialTotal : parseFloat(p.actual_cost || 0));
                            }, 0) + projectExpenses.filter(e => e.is_paid).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0)).toLocaleString()}
                          </td>
                          <td className="py-4 px-4 text-center text-green-600 font-medium">
                            ${(phases.reduce((sum, p) => sum + parseFloat(p.amount_drawn || 0), 0) + projectExpenses.filter(e => e.is_drawn).reduce((sum, e) => sum + parseFloat(e.amount_drawn || 0), 0)).toLocaleString()}
                          </td>
                          <td colSpan="2"></td>
                          <td className="py-4 px-4 text-right text-sm text-slate-500">
                            {phases.filter(p => p.phase_type !== 'soft_cost').length} phases, {projectExpenses.length + phases.filter(p => p.phase_type === 'soft_cost').length} soft costs
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {phases.length === 0 && (
                <Card className="bg-white">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <p className="text-slate-500 mb-4">No phases yet</p>
                    <p className="text-sm text-slate-400">Add phases to see your budget breakdown</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
          )}

          {activeTab === 'budget' && (
            <div className="space-y-4" data-testid="budget-tab-content">
              <ConstructionPhasesV2 
                projectId={projectId}
                project={project}
                contractors={contractors}
                userId={user?.id}
                onRequestCOI={!isLenderViewer && can(PERMISSIONS.EDIT_PROJECTS) ? () => setIsCOIRequestOpen(true) : null}
                onRefresh={loadProjectData}
                onRequestDraw={can(PERMISSIONS.REQUEST_DRAWS) ? openDrawRequest : null}
                onPayContractor={(phase, contractor) => {
                  setSelectedPhaseForPayment(phase);
                  setIsPhasePaymentDialogOpen(true);
                }}
                isReadOnly={isReadOnlyUser}
                useBatchB={useBatchB}
                inspectionGateEnabled={inspectionGateEnabled}
              />
              <UnitCostBreakdown projectId={projectId} />
            </div>
          )}

          {modulesLocked && ['gantt', 'schedule', 'spec-sheets', 'paint'].includes(activeTab) && (
            <AddOnUpgrade inline moduleId={activeTab === 'gantt' ? 'timeline' : activeTab} />
          )}

          {activeTab === 'gantt' && !modulesLocked && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-white">Project Timeline</h2>
                  <p className="text-slate-400 text-sm">Read-only Gantt chart view of your construction schedule</p>
                </div>
              </div>
              <GanttChart 
                phases={phases.map(phase => {
                  const contractor = contractors.find(c => c.id === phase.contractor_id);
                  return {
                    ...phase,
                    contractor_name: contractor ? (contractor.company || contractor.name) : '-'
                  };
                })} 
                contractors={contractors}
                project={project}
                readOnly={true}
              />
            </div>
          )}

          {activeTab === 'schedule' && !modulesLocked && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              {can(PERMISSIONS.EDIT_PROJECTS) && (
                <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => setShowScheduleQModal(true)}
                    data-testid="schedule-tab-timing-q-btn"
                    title="Ask contractors how many days each stage takes — feeds into the schedule"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send timing questionnaire
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => setIsScheduleWizardOpen(true)}
                    data-testid="schedule-tab-wizard-btn"
                    title="Generate the multi-unit staggered schedule"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule wizard
                  </Button>
                </div>
              )}
              <ProjectScheduleTab project={project} user={user} />
            </div>
          )}

          {activeTab === 'spec-sheets' && !modulesLocked && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-900">Spec Sheets & Materials</h3>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Spec Sheet
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Spec Sheet</DialogTitle>
                      <DialogDescription>
                        Upload specification sheets for appliances, materials, fixtures, etc.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Category</Label>
                        <Select 
                          value={newSpecSheet.category} 
                          onValueChange={(v) => setNewSpecSheet({...newSpecSheet, category: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="appliances">Appliances</SelectItem>
                            <SelectItem value="plumbing">Plumbing Fixtures</SelectItem>
                            <SelectItem value="tile">Tile & Stone</SelectItem>
                            <SelectItem value="flooring">Flooring</SelectItem>
                            <SelectItem value="lighting">Light Fixtures</SelectItem>
                            <SelectItem value="cabinetry">Cabinetry</SelectItem>
                            <SelectItem value="countertops">Countertops</SelectItem>
                            <SelectItem value="hardware">Hardware</SelectItem>
                            <SelectItem value="windows">Windows & Doors</SelectItem>
                            <SelectItem value="hvac">HVAC</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Item Name</Label>
                        <Input 
                          placeholder="e.g., Kitchen Range Hood, Master Bath Tub"
                          value={newSpecSheet.name}
                          onChange={(e) => setNewSpecSheet({...newSpecSheet, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Description / Notes</Label>
                        <Textarea 
                          placeholder="Model number, dimensions, color, etc."
                          value={newSpecSheet.description}
                          onChange={(e) => setNewSpecSheet({...newSpecSheet, description: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Spec Sheet File (PDF or Image)</Label>
                        <Input 
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => setNewSpecSheet({...newSpecSheet, file: e.target.files[0]})}
                        />
                      </div>
                      <Button 
                        className="w-full"
                        disabled={specSheetUploadLoading || !newSpecSheet.name}
                        onClick={async () => {
                          setSpecSheetUploadLoading(true);
                          try {
                            const formData = new FormData();
                            formData.append('project_id', projectId);
                            formData.append('category', newSpecSheet.category);
                            formData.append('name', newSpecSheet.name);
                            formData.append('description', newSpecSheet.description);
                            if (newSpecSheet.file) {
                              formData.append('file', newSpecSheet.file);
                            }
                            
                            await apiService.uploadSpecSheet(projectId, formData);
                            toast.success('Spec sheet added successfully!');
                            
                            // Reload spec sheets
                            const res = await apiService.getSpecSheets(projectId);
                            setSpecSheets(res.data || []);
                            
                            // Reset form
                            setNewSpecSheet({ category: 'appliances', name: '', description: '', file: null });
                          } catch (err) {
                            toast.error('Failed to add spec sheet');
                          } finally {
                            setSpecSheetUploadLoading(false);
                          }
                        }}
                      >
                        {specSheetUploadLoading ? 'Uploading...' : 'Add Spec Sheet'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              <p className="text-sm text-slate-500">
                Store specification sheets for appliances, tubs, shower pans, tile, flooring, light fixtures, and more.
              </p>
              
              {/* Spec Sheets by Category */}
              {['appliances', 'plumbing', 'tile', 'flooring', 'lighting', 'cabinetry', 'countertops', 'hardware', 'windows', 'hvac', 'other'].map(category => {
                const categorySheets = specSheets.filter(s => s.category === category);
                if (categorySheets.length === 0) return null;
                
                const categoryLabels = {
                  appliances: 'Appliances',
                  plumbing: 'Plumbing Fixtures',
                  tile: 'Tile & Stone',
                  flooring: 'Flooring',
                  lighting: 'Light Fixtures',
                  cabinetry: 'Cabinetry',
                  countertops: 'Countertops',
                  hardware: 'Hardware',
                  windows: 'Windows & Doors',
                  hvac: 'HVAC',
                  other: 'Other'
                };
                
                return (
                  <Card key={category}>
                    <CardHeader>
                      <CardTitle className="text-lg">{categoryLabels[category]}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {categorySheets.map((sheet, idx) => (
                          <div key={sheet.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <ClipboardList className="w-5 h-5 text-blue-600" />
                              <div>
                                <p className="font-medium">{sheet.name}</p>
                                {sheet.description && (
                                  <p className="text-sm text-slate-500">{sheet.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {sheet.file_id && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${sheet.file_id}`, '_blank')}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = `${process.env.REACT_APP_BACKEND_URL}/api/files/${sheet.file_id}`;
                                      link.download = sheet.filename || 'spec-sheet.pdf';
                                      link.click();
                                    }}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    Download
                                  </Button>
                                </>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={async () => {
                                  if (window.confirm('Delete this spec sheet?')) {
                                    try {
                                      await apiService.deleteSpecSheet(projectId, sheet.id);
                                      setSpecSheets(specSheets.filter(s => s.id !== sheet.id));
                                      toast.success('Spec sheet deleted');
                                    } catch (err) {
                                      toast.error('Failed to delete');
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {specSheets.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No spec sheets added yet.</p>
                    <p className="text-sm text-slate-400 mt-1">Click "Add Spec Sheet" to get started.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'paint' && !modulesLocked && (
            <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-900">Paint Colors</h3>
              <Dialog open={isPaintDialogOpen} onOpenChange={setIsPaintDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-paint-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Color
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Paint Color</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePaintColor} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="paint-room">Room</Label>
                      <Input
                        id="paint-room"
                        data-testid="paint-room-input"
                        placeholder="Living Room"
                        value={newPaint.room}
                        onChange={(e) => setNewPaint({ ...newPaint, room: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="paint-name">Color Name</Label>
                      <Input
                        id="paint-name"
                        data-testid="paint-name-input"
                        placeholder="Swiss Coffee"
                        value={newPaint.color_name}
                        onChange={(e) => setNewPaint({ ...newPaint, color_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="paint-code">Color Code</Label>
                      <div className="flex gap-2">
                        <Input
                          id="paint-code"
                          type="color"
                          value={newPaint.color_code}
                          onChange={(e) => setNewPaint({ ...newPaint, color_code: e.target.value })}
                          className="w-20"
                        />
                        <Input
                          value={newPaint.color_code}
                          onChange={(e) => setNewPaint({ ...newPaint, color_code: e.target.value })}
                          placeholder="#000000"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="paint-type">Paint Type</Label>
                      <Select
                        value={newPaint.type}
                        onValueChange={(value) => setNewPaint({ ...newPaint, type: value })}
                      >
                        <SelectTrigger id="paint-type" data-testid="paint-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">Flat</SelectItem>
                          <SelectItem value="eggshell">Eggshell</SelectItem>
                          <SelectItem value="satin">Satin</SelectItem>
                          <SelectItem value="semi-gloss">Semi-Gloss</SelectItem>
                          <SelectItem value="gloss">Gloss</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="paint-purchased-from">Purchased From</Label>
                      <Input
                        id="paint-purchased-from"
                        data-testid="paint-purchased-from-input"
                        placeholder="Home Depot, Sherwin-Williams, etc."
                        value={newPaint.purchased_from}
                        onChange={(e) => setNewPaint({ ...newPaint, purchased_from: e.target.value })}
                      />
                    </div>
                    <Button type="submit" data-testid="submit-paint-button" className="w-full">
                      Save Color
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {paintColors.map((color) => (
                <Card key={color.id} data-testid={`paint-card-${color.id}`} className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-16 h-16 rounded-lg border-2 border-slate-200 flex-shrink-0"
                        style={{ backgroundColor: color.color_code }}
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{color.room}</h4>
                        <p className="text-sm text-slate-600">{color.color_name}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {color.color_code} • {color.type}
                          {color.purchased_from && ` • ${color.purchased_from}`}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 h-8 px-2"
                          onClick={() => {
                            setEditPaint(color);
                            setIsEditPaintDialogOpen(true);
                          }}
                          data-testid={`edit-paint-${color.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                          onClick={() => handleDeletePaintColor(color.id)}
                          data-testid={`delete-paint-${color.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {paintColors.length === 0 && (
                <Card className="bg-white col-span-2">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <PaintBucket className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-4">No paint colors saved yet</p>
                    <Button onClick={() => setIsPaintDialogOpen(true)} data-testid="empty-paint-add-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Color
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Edit Paint Color Dialog */}
            <Dialog open={isEditPaintDialogOpen} onOpenChange={setIsEditPaintDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Paint Color</DialogTitle>
                </DialogHeader>
                {editPaint && (
                  <form onSubmit={handleUpdatePaintColor} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-paint-room">Room</Label>
                      <Input
                        id="edit-paint-room"
                        value={editPaint.room}
                        onChange={(e) => setEditPaint({ ...editPaint, room: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-paint-name">Color Name</Label>
                      <Input
                        id="edit-paint-name"
                        value={editPaint.color_name}
                        onChange={(e) => setEditPaint({ ...editPaint, color_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-paint-code">Color Code</Label>
                      <div className="flex gap-2">
                        <Input
                          id="edit-paint-code"
                          type="color"
                          value={editPaint.color_code}
                          onChange={(e) => setEditPaint({ ...editPaint, color_code: e.target.value })}
                          className="w-20"
                        />
                        <Input
                          value={editPaint.color_code}
                          onChange={(e) => setEditPaint({ ...editPaint, color_code: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-paint-type">Paint Type</Label>
                      <Select
                        value={editPaint.type}
                        onValueChange={(value) => setEditPaint({ ...editPaint, type: value })}
                      >
                        <SelectTrigger id="edit-paint-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">Flat</SelectItem>
                          <SelectItem value="eggshell">Eggshell</SelectItem>
                          <SelectItem value="satin">Satin</SelectItem>
                          <SelectItem value="semi-gloss">Semi-Gloss</SelectItem>
                          <SelectItem value="gloss">Gloss</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-paint-purchased-from">Purchased From</Label>
                      <Input
                        id="edit-paint-purchased-from"
                        value={editPaint.purchased_from || ''}
                        onChange={(e) => setEditPaint({ ...editPaint, purchased_from: e.target.value })}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsEditPaintDialogOpen(false)} className="flex-1">
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1">
                        Save Changes
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
            </div>
          )}

          {/* Loan Details Tab */}
          {activeTab === 'loan' && (
            <div className="space-y-6">
              {/* Bank draw form mapping card — lives outside any dialog so the
                  mapper opens cleanly (no nested-Radix-dialog race). */}
              {project?.draw_request_form_id && (
                <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4 flex items-center justify-between gap-3" data-testid="loan-bank-form-card">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-purple-600/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm">Your bank&apos;s draw form</p>
                      <p className="text-xs text-slate-500 truncate">
                        {customFormMapped
                          ? 'Fields mapped — available as a draw-request format.'
                          : 'Map where your data prints to fill draws onto this exact form.'}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 flex-shrink-0"
                    data-testid="loan-draw-form-map-btn"
                    onClick={() => setIsDrawMapperOpen(true)}
                  >
                    <MapPin className="w-4 h-4 mr-1" /> {customFormMapped ? 'Edit field map' : 'Map form fields'}
                  </Button>
                </div>
              )}
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-900">Loan Details</h3>
                {/* New Draw Request Button - Moved from Phases tab */}
                <Dialog open={isReimbursementDialogOpen} onOpenChange={(open) => {
                  setIsReimbursementDialogOpen(open);
                  if (open) {
                    prepareDrawRequestData();
                  } else {
                    clearDrawWip();
                    setSelectedPhasesForReimbursement([]);
                    setSelectedExpensesForReimbursement([]);
                    setSelectedHoldingCostsForReimbursement([]);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="text-white hover:opacity-90 rounded-full shadow-lg" style={{ backgroundColor: '#1E4ED8' }}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Draw Request
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[900px] w-[95vw] max-h-[92vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0 pb-2">
                      <DialogTitle className="text-lg">Request Bank Reimbursement</DialogTitle>
                      <DialogDescription className="text-sm">
                        Select completed phases to include in your draw request.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                      {phases.filter(p => {
                        // v2.5.15 — outer empty-state predicate mirrors the
                        // inner selector eligibility (see L7559+). A phase
                        // qualifies if it has ANY of: reimbursement gap,
                        // unpaid balance, or is completed/paid with a draw
                        // history. Previously this outer check only allowed
                        // completed/labor-with-partial-payments, which hid
                        // in-progress phases that the inner filter was
                        // designed to include.
                        if (!p) return false;
                        if (p.status === 'completed' || p.is_paid) return true;
                        const partialPaymentsTotal = phasePayments[p.id]
                          ? phasePayments[p.id].reduce((sum, pay) => sum + parseFloat(pay.amount || 0), 0)
                          : 0;
                        let paid = 0;
                        if (p.phase_type === 'material') {
                          paid = Math.max(partialPaymentsTotal, parseFloat(p.supplier_amount_paid) || 0, parseFloat(p.actual_cost) || 0);
                        } else {
                          paid = Math.max(partialPaymentsTotal, parseFloat(p.actual_cost) || 0);
                        }
                        const totalCost = Math.max(
                          parseFloat(p.invoice_amount) || 0,
                          parseFloat(p.actual_cost) || 0,
                          parseFloat(p.estimated_cost) || 0,
                        );
                        const drawn = parseFloat(p.amount_drawn) || 0;
                        return (paid - drawn) > 0.01 || (totalCost - paid) > 0.01;
                      }).length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p className="font-medium">No phases with payments</p>
                          <p className="text-sm mt-1">Complete phases or make payments to request reimbursement</p>
                        </div>
                      ) : (
                        <>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                            <p className="text-xs text-blue-800">
                              <strong>Note:</strong> Select paid phases and expenses to include in your draw request.
                            </p>
                          </div>
                          
                          {drawHistory.length > 0 && (
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 mb-3">
                              <p className="text-xs text-slate-800 font-semibold mb-2">
                                📋 Draw History ({drawHistory.length} draws)
                              </p>
                              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                {drawHistory
                                  .sort((a, b) => new Date(b.draw_date) - new Date(a.draw_date))
                                  .map((draw, idx) => (
                                  <div 
                                    key={draw.id} 
                                    className="bg-white p-2 rounded border"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <span className="text-xs font-semibold text-blue-600">
                                          {draw.description || `Draw #${drawHistory.length - idx}`}
                                        </span>
                                        <span className="text-xs text-slate-500 ml-2">
                                          {formatDateString(draw.draw_date?.split('T')[0])}
                                        </span>
                                      </div>
                                      <span className="text-xs font-bold text-green-600">
                                        ${parseFloat(draw.draw_amount || 0).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                                        ✓ Deposited
                                      </span>
                                      <span className="text-xs text-slate-400 ml-auto">
                                        Cumulative: ${parseFloat(draw.cumulative_balance || 0).toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {submittedDraws.length > 0 && (
                            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 mb-3" data-testid="submitted-to-bank-panel">
                              <p className="text-xs text-indigo-900 font-semibold mb-2">📧 Submitted to bank ({submittedDraws.length})</p>
                              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                                {submittedDraws.map((d) => {
                                  const s = d.delivery_status || 'sent';
                                  const badge = {
                                    sent: ['Accepted by mail server', 'bg-slate-100 text-slate-600'],
                                    delivered: ['Delivered ✓', 'bg-green-100 text-green-700'],
                                    opened: ['Opened 👁', 'bg-blue-100 text-blue-700'],
                                    clicked: ['Opened 👁', 'bg-blue-100 text-blue-700'],
                                    delivery_delayed: ['Delayed — retrying', 'bg-amber-100 text-amber-700'],
                                    bounced: ['Bounced ✗', 'bg-red-100 text-red-700'],
                                    complained: ['Spam-flagged', 'bg-red-100 text-red-700'],
                                  }[s] || ['Accepted by mail server', 'bg-slate-100 text-slate-600'];
                                  return (
                                    <div key={d.id} className="bg-white p-2 rounded border" data-testid={`submitted-draw-${d.draw_number}`}>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-indigo-700">Draw #{d.draw_number}</span>
                                        <span className="text-xs font-bold text-green-600">${parseFloat(d.amount || 0).toLocaleString()}</span>
                                      </div>
                                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">To {d.bank_email} · {d.emailed_at ? formatDateString(String(d.emailed_at).split('T')[0]) : ''}</div>
                                      <div className="text-[11px] text-slate-400 truncate">Replies → {d.reply_to || '—'}</div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badge[1]}`} data-testid={`submitted-draw-status-${d.draw_number}`}>{badge[0]}</span>
                                        {d.pdf_file_id && (
                                          <button onClick={() => viewSentDrawPdf(d.pdf_file_id)} className="text-[11px] text-blue-600 hover:underline ml-auto" data-testid={`submitted-draw-viewpdf-${d.draw_number}`}>View PDF sent</button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                            {(phases || [])
                              .filter(p => {
                                if (!p) return false;
                                // Calculate what's been paid (total payments made)
                                let paidAmount = 0;
                                
                                // Check for partial payments first (applies to both labor and material)
                                const partialPaymentsTotal = phasePayments[p.id] 
                                  ? phasePayments[p.id].reduce((sum, pay) => sum + parseFloat(pay.amount || 0), 0) 
                                  : 0;
                                
                                if (p.phase_type === 'material') {
                                  // For materials: use partial payments, or supplier_amount_paid, or actual_cost
                                  const supplierPaid = parseFloat(p.supplier_amount_paid) || 0;
                                  const actualCost = parseFloat(p.actual_cost) || 0;
                                  // Use the highest of: partial payments, supplier_amount_paid, or actual_cost
                                  paidAmount = Math.max(partialPaymentsTotal, supplierPaid, actualCost);
                                } else {
                                  // For labor: use partial payments or actual_cost
                                  const actualCost = parseFloat(p.actual_cost) || 0;
                                  paidAmount = Math.max(partialPaymentsTotal, actualCost);
                                }
                                
                                // Calculate what's already been drawn/reimbursed
                                const alreadyDrawn = parseFloat(p.amount_drawn) || 0;
                                
                                // Remaining drawable amount (what's paid but not yet reimbursed)
                                const remainingDrawable = paidAmount - alreadyDrawn;

                                // v2.5.15 — Two-mode draw eligibility.
                                // The "Request Draw" flow funds either:
                                //   (a) REIMBURSEMENT: owner already paid out of pocket; the
                                //       bank needs to refill them → remainingDrawable > 0.
                                //   (b) PAY-THE-SUB: there's still an unpaid balance the
                                //       bank should fund directly → unpaidBalance > 0.
                                // The old filter only counted (a), so any phase whose
                                // reimbursement was fully complete (paid === drawn)
                                // disappeared from the list even when the sub still
                                // had money owed.
                                //
                                // We take the MAX of invoice_amount / actual_cost /
                                // estimated_cost so partial-payment phases where the
                                // backend Phase model strips invoice_amount from the
                                // response still see the full contract value as the
                                // ceiling (otherwise totalCost collapses to actual_cost
                                // === drawn and the phase is incorrectly filtered).
                                const totalCost = Math.max(
                                  parseFloat(p.invoice_amount) || 0,
                                  parseFloat(p.actual_cost) || 0,
                                  parseFloat(p.estimated_cost) || 0,
                                );
                                const unpaidBalance = Math.max(0, totalCost - paidAmount);

                                // Show phase if EITHER:
                                //  • there's still money to draw (reimbursement mode), OR
                                //  • there's still an unpaid balance to the sub (pay-the-sub mode), OR
                                //  • phase is completed/paid and the user might want to
                                //    review a $0 line as a sanity check.
                                const isCompletedOrPaid = p.status === 'completed' || p.is_paid;
                                return (
                                  remainingDrawable > 0.01
                                  || unpaidBalance > 0.01
                                  || (isCompletedOrPaid && alreadyDrawn > 0)
                                );
                              })
                              .map((phase) => {
                                const contractor = contractors.find(c => c.id === phase.contractor_id);
                                const isSelected = selectedPhasesForReimbursement.includes(phase.id);
                                
                                // Calculate paid amount (same logic as filter)
                                const partialPaymentsTotal = phasePayments[phase.id] 
                                  ? phasePayments[phase.id].reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) 
                                  : 0;
                                
                                let paidAmount = 0;
                                if (phase.phase_type === 'material') {
                                  const supplierPaid = parseFloat(phase.supplier_amount_paid) || 0;
                                  const actualCost = parseFloat(phase.actual_cost) || 0;
                                  paidAmount = Math.max(partialPaymentsTotal, supplierPaid, actualCost);
                                } else {
                                  const actualCost = parseFloat(phase.actual_cost) || 0;
                                  paidAmount = Math.max(partialPaymentsTotal, actualCost);
                                }
                                
                                // v2.5.15 — totalCost lets us cover both reimbursement
                                // and pay-the-sub draw modes. drawableAmount is the
                                // total still owed for this phase across both modes:
                                //   = (paid - alreadyDrawn)    ← reimbursement gap
                                //   + (totalCost - paid)        ← unpaid balance to sub
                                //   = totalCost - alreadyDrawn
                                // Use Math.max across all three fields so partial-pay
                                // phases without an invoice_amount still see the
                                // contract ceiling (the Phase model strips this field
                                // from the API response — see filter rationale above).
                                const alreadyDrawn = parseFloat(phase.amount_drawn) || 0;
                                const totalCost = Math.max(
                                  parseFloat(phase.invoice_amount) || 0,
                                  parseFloat(phase.actual_cost) || 0,
                                  parseFloat(phase.estimated_cost) || 0,
                                ) || paidAmount;
                                const drawableAmount = Math.max(0, totalCost - alreadyDrawn);
                                const unpaidBalance = Math.max(0, totalCost - paidAmount);
                                
                                return (
                                  <div
                                    key={phase.id}
                                    className={`p-3 border-2 rounded-lg transition-all ${
                                      isSelected
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                  >
                                    <div 
                                      className="flex items-center justify-between cursor-pointer"
                                      onClick={() => {
                                        if (isSelected) {
                                          setSelectedPhasesForReimbursement(selectedPhasesForReimbursement.filter(id => id !== phase.id));
                                          // Clear any custom draw amount for this phase
                                          setCustomDrawAmounts(prev => {
                                            const updated = {...prev};
                                            delete updated[phase.id];
                                            return updated;
                                          });
                                          // Clear selected receipts for this phase
                                          setSelectedReceiptsForDraw(prev => {
                                            const updated = {...prev};
                                            delete updated[phase.id];
                                            return updated;
                                          });
                                        } else {
                                          setSelectedPhasesForReimbursement([...selectedPhasesForReimbursement, phase.id]);
                                          // Load receipts for this phase if not loaded
                                          if (!phaseReceiptsForDraw[phase.id]) {
                                            apiService.getPhaseReceipts(phase.id).then(res => {
                                              setPhaseReceiptsForDraw(prev => ({
                                                ...prev,
                                                [phase.id]: res.data.receipts || []
                                              }));
                                            }).catch(() => {});
                                          }
                                        }
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => {}}
                                          className="w-4 h-4"
                                        />
                                        <div>
                                          <h4 className="font-semibold text-slate-900">{phase.name}</h4>
                                          {contractor && <p className="text-xs text-slate-500">{contractor.name}</p>}
                                          {phase.bank_category && (
                                            <p className="text-xs text-blue-600">{phase.bank_category}</p>
                                          )}
                                          {alreadyDrawn > 0 && (
                                            <p className="text-xs text-orange-600">
                                              Previously drawn: ${alreadyDrawn.toLocaleString()} | Remaining: ${drawableAmount.toLocaleString()}
                                            </p>
                                          )}
                                          {/* v2.5.15 — surface the two draw modes so the
                                              owner knows what this line item covers. */}
                                          {drawableAmount > 0 && unpaidBalance > 0 && (
                                            <p
                                              className="text-[11px] text-slate-500 mt-0.5"
                                              data-testid={`draw-line-breakdown-${phase.id}`}
                                            >
                                              {(drawableAmount - unpaidBalance) > 0.01
                                                ? `Reimburse $${(drawableAmount - unpaidBalance).toLocaleString()} + Pay sub $${unpaidBalance.toLocaleString()}`
                                                : `Pay sub $${unpaidBalance.toLocaleString()}`}
                                            </p>
                                          )}
                                          {drawableAmount <= 0 && alreadyDrawn > 0 && (
                                            <p className="text-xs text-red-500 flex items-center gap-1">
                                              <AlertCircle className="w-3 h-3" />
                                              Shows fully drawn - click Edit to correct if needed
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right flex items-center gap-2">
                                        <span className={`font-bold ${drawableAmount > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                                          ${drawableAmount.toLocaleString()}
                                        </span>
                                        {/* Edit button - ALWAYS show so user can set/correct the Previously Drawn amount */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Open a prompt to correct the amount_drawn
                                            const newDrawnAmount = prompt(
                                              `Set "Previously Drawn" amount for ${phase.name}:\n\nCurrent value: $${alreadyDrawn.toLocaleString()}\nActual paid: $${paidAmount.toLocaleString()}\n\nEnter the amount already drawn/reimbursed from bank:`,
                                              alreadyDrawn.toString()
                                            );
                                            if (newDrawnAmount !== null && !isNaN(parseFloat(newDrawnAmount))) {
                                              apiService.updatePhase(phase.id, { amount_drawn: parseFloat(newDrawnAmount) })
                                                .then(() => {
                                                  toast.success('Draw amount updated');
                                                  loadProjectData();
                                                })
                                                .catch(() => toast.error('Failed to update'));
                                            }
                                          }}
                                          className="text-xs text-blue-600 hover:underline font-medium"
                                        >
                                          Edit
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* Partial draw — let the owner request less than the full drawable amount */}
                                    {isSelected && drawableAmount > 0 && (
                                      <div
                                        className="mt-3 pt-3 border-t border-green-200 flex flex-wrap items-center gap-2"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <label className="text-xs font-medium text-slate-600" htmlFor={`draw-amount-${phase.id}`}>
                                          Draw amount for this line:
                                        </label>
                                        <div className="relative">
                                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                                          <input
                                            id={`draw-amount-${phase.id}`}
                                            type="number"
                                            min="0"
                                            max={drawableAmount}
                                            step="0.01"
                                            value={customDrawAmounts[phase.id] ?? drawableAmount}
                                            onChange={(e) => setLineDrawAmount(phase.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="pl-5 pr-2 py-1 w-32 border border-slate-300 rounded text-sm focus:border-green-500 focus:outline-none"
                                            data-testid={`draw-amount-input-${phase.id}`}
                                          />
                                        </div>
                                        <span className="text-xs text-slate-400">
                                          of ${drawableAmount.toLocaleString()} available
                                        </span>
                                        {getLineDrawAmount(phase.id, drawableAmount) < drawableAmount - 0.01 && (
                                          <span
                                            className="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"
                                            data-testid={`partial-draw-badge-${phase.id}`}
                                          >
                                            Partial draw · ${(drawableAmount - getLineDrawAmount(phase.id, drawableAmount)).toLocaleString()} left for later
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Receipt attachment section - shown when phase is selected */}
                                    {isSelected && phaseReceiptsForDraw[phase.id] && phaseReceiptsForDraw[phase.id].length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-green-200">
                                        <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                                          <FileText className="w-3 h-3" />
                                          Attach receipts to draw request (optional):
                                        </p>
                                        <div className="space-y-1 max-h-24 overflow-y-auto">
                                          {phaseReceiptsForDraw[phase.id].map(receipt => {
                                            const isReceiptSelected = selectedReceiptsForDraw[phase.id]?.includes(receipt.id);
                                            return (
                                              <label 
                                                key={receipt.id}
                                                className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs ${
                                                  isReceiptSelected ? 'bg-purple-100 border border-purple-300' : 'bg-slate-50 hover:bg-slate-100'
                                                }`}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={isReceiptSelected}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedReceiptsForDraw(prev => {
                                                      const current = prev[phase.id] || [];
                                                      if (isReceiptSelected) {
                                                        return {...prev, [phase.id]: current.filter(id => id !== receipt.id)};
                                                      } else {
                                                        return {...prev, [phase.id]: [...current, receipt.id]};
                                                      }
                                                    });
                                                  }}
                                                  className="w-3 h-3"
                                                />
                                                <span className="font-medium text-slate-700">${(receipt.amount || 0).toFixed(2)}</span>
                                                <span className="text-slate-400">•</span>
                                                <span className="text-slate-600 truncate">{receipt.vendor || 'Unknown'}</span>
                                                {receipt.date && (
                                                  <>
                                                    <span className="text-slate-400">•</span>
                                                    <span className="text-slate-500">{receipt.date}</span>
                                                  </>
                                                )}
                                              </label>
                                            );
                                          })}
                                        </div>
                                        {selectedReceiptsForDraw[phase.id]?.length > 0 && (
                                          <p className="text-xs text-purple-600 mt-1">
                                            📎 {selectedReceiptsForDraw[phase.id].length} receipt(s) will be included
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </>
                      )}
                      
                      {/* Holding Costs Section */}
                      {holdingCostsForDraw.filter(hc => 
                        hc.bank_category && 
                        (hc.is_paid || (hc.actual_cost && hc.actual_cost > 0))
                      ).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-sm font-semibold text-purple-700 mb-2 flex items-center gap-2">
                            Holding Costs Available for Draw
                          </p>
                          <div className="space-y-2">
                            {holdingCostsForDraw
                              .filter(hc => hc.bank_category && (hc.is_paid || (hc.actual_cost && hc.actual_cost > 0)))
                              .map(hc => {
                                const isSelected = selectedHoldingCostsForReimbursement.includes(hc.id);
                                const drawableAmount = hc.actual_cost || 0;
                                
                                return (
                                  <div
                                    key={hc.id}
                                    className={`p-3 border-2 rounded-lg transition-all ${
                                      isSelected
                                        ? 'border-purple-500 bg-purple-50'
                                        : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                  >
                                    <div 
                                      className="flex items-center justify-between cursor-pointer"
                                      onClick={() => {
                                        if (isSelected) {
                                          setSelectedHoldingCostsForReimbursement(
                                            selectedHoldingCostsForReimbursement.filter(id => id !== hc.id)
                                          );
                                        } else {
                                          setSelectedHoldingCostsForReimbursement([
                                            ...selectedHoldingCostsForReimbursement, 
                                            hc.id
                                          ]);
                                        }
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => {}}
                                          className="w-4 h-4"
                                        />
                                        <div>
                                          <h4 className="font-semibold text-slate-900">{hc.name}</h4>
                                          <p className="text-xs text-slate-500">{hc.vendor || 'Holding Cost'}</p>
                                          <p className="text-xs text-blue-600">{hc.bank_category}</p>
                                        </div>
                                      </div>
                                      <span className="text-purple-600 font-bold">${drawableAmount.toLocaleString()}</span>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 pt-3 border-t">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-slate-600">Total Selected:</span>
                        <span className="text-lg font-bold text-green-600">
                          ${(() => {
                            const phasesTotal = phases
                              .filter(p => selectedPhasesForReimbursement.includes(p.id))
                              .reduce((sum, p) => {
                                // Calculate paid amount (consistent with filter logic)
                                const partialPaymentsTotal = phasePayments[p.id] 
                                  ? phasePayments[p.id].reduce((pSum, payment) => pSum + parseFloat(payment.amount || 0), 0) 
                                  : 0;
                                
                                let paidAmount = 0;
                                if (p.phase_type === 'material') {
                                  const supplierPaid = parseFloat(p.supplier_amount_paid) || 0;
                                  const actualCost = parseFloat(p.actual_cost) || 0;
                                  paidAmount = Math.max(partialPaymentsTotal, supplierPaid, actualCost);
                                } else {
                                  const actualCost = parseFloat(p.actual_cost) || 0;
                                  paidAmount = Math.max(partialPaymentsTotal, actualCost);
                                }
                                // Calculate drawable amount (paid - already drawn)
                                const alreadyDrawn = parseFloat(p.amount_drawn) || 0;
                                const drawableAmount = paidAmount - alreadyDrawn;
                                return sum + getLineDrawAmount(p.id, Math.max(0, drawableAmount));
                              }, 0);
                            
                            // Add holding costs total
                            const holdingCostsTotal = holdingCostsForDraw
                              .filter(hc => selectedHoldingCostsForReimbursement.includes(hc.id))
                              .reduce((sum, hc) => sum + (hc.actual_cost || 0), 0);
                            
                            return (phasesTotal + holdingCostsTotal).toLocaleString();
                          })()}
                        </span>
                      </div>
                      <Button
                        onClick={handleReviewDrawScore}
                        disabled={selectedPhasesForReimbursement.length === 0 && selectedHoldingCostsForReimbursement.length === 0}
                        className="w-full h-10 bg-green-600 hover:bg-green-700"
                        data-testid="preview-generate-draw-btn"
                      >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Check Draw Score & Continue
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* v3.0 — Draw-scoped readiness gate (between selection and preview) */}
                <DrawScoreGate
                  isOpen={isDrawScoreGateOpen}
                  phases={phases.filter(p => selectedPhasesForReimbursement.includes(p.id))}
                  contractors={contractors}
                  contingencyRemaining={budgetStatus?.contingencyRemaining || 0}
                  onClose={() => { setIsDrawScoreGateOpen(false); setIsReimbursementDialogOpen(true); }}
                  onContinue={() => { setIsDrawScoreGateOpen(false); handleOpenDrawPreview(); }}
                />

                {/* Draw Request Preview Modal */}
                <DrawRequestPreviewModal
                  isOpen={isDrawPreviewOpen}
                  onClose={() => { clearDrawWip(); setIsDrawPreviewOpen(false); }}
                  project={project}
                  selectedItems={getDrawPreviewData().selectedItems}
                  holdingCosts={getDrawPreviewData().holdingCosts}
                  onEditProject={() => {
                    setIsDrawPreviewOpen(false);
                    setIsEditProjectDialogOpen(true);
                  }}
                  onGenerate={handleGenerateReimbursementRequest}
                  onSubmitToBank={handleSubmitDrawToBank}
                  defaultReplyTo={user?.reply_to_email || user?.email || ''}
                  isSubmittingToBank={isSubmittingToBank}
                  isGenerating={isGeneratingDraw}
                  signDocument={signDrawDocument}
                  setSignDocument={setSignDrawDocument}
                  userSignature={userSignature}
                  setUserSignature={setUserSignature}
                  userId={user?.id}
                  excelOnly={downloadExcelOnly}
                  setExcelOnly={setDownloadExcelOnly}
                  formStyle={drawFormStyle}
                  setFormStyle={setDrawFormStyle}
                  onCompare={handleCompareFormats}
                  isComparing={isComparing}
                  customAvailable={customFormMapped}
                  bankName={project?.bank_name}
                />
              </div>

              {/* Custom bank-form field mapper */}
              <DrawFormMapper
                isOpen={isDrawMapperOpen}
                onClose={() => setIsDrawMapperOpen(false)}
                projectId={projectId}
                onSaved={() => { setCustomFormMapped(true); }}
              />

              {/* Side-by-side format comparison — its OWN Dialog so it renders in
                  its own portal ON TOP of the preview modal (never treated as an
                  outside-click that would close/reset the in-progress draw). */}
              <Dialog
                open={!!compareUrls}
                onOpenChange={(o) => {
                  if (!o && compareUrls) {
                    try { window.URL.revokeObjectURL(compareUrls.hills); window.URL.revokeObjectURL(compareUrls.drawready); } catch (e) { /* noop */ }
                    setCompareUrls(null);
                  }
                }}
              >
                {compareUrls && (
                  <DialogContent
                    className="max-w-none w-screen h-screen left-0 top-0 translate-x-0 translate-y-0 rounded-none border-0 p-4 flex flex-col gap-0 bg-slate-900"
                    data-testid="format-compare-overlay"
                  >
                    <DialogHeader className="flex-row items-center justify-between space-y-0 mb-1">
                      <DialogTitle className="text-white text-lg">Compare draw request formats</DialogTitle>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid="format-compare-close"
                        onClick={() => {
                          try { window.URL.revokeObjectURL(compareUrls.hills); window.URL.revokeObjectURL(compareUrls.drawready); } catch (e) { /* noop */ }
                          setCompareUrls(null);
                        }}
                      >
                        Close
                      </Button>
                    </DialogHeader>
                    <p className="text-white/70 text-sm mb-3">The same draw in both formats, side by side. Your draw stays exactly as it is.</p>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0 overflow-hidden">
                      <div className="bg-white rounded-lg overflow-hidden flex flex-col min-h-0">
                        <div className="px-3 py-2 bg-green-600 text-white text-sm font-semibold flex items-center justify-between flex-shrink-0">
                          <span>{project.bank_name || 'Hills Bank'} format</span>
                          <a href={compareUrls.hills} target="_blank" rel="noopener noreferrer" data-testid="compare-open-hills" className="text-xs underline hover:no-underline">Open in new tab</a>
                        </div>
                        <object data={compareUrls.hills} type="application/pdf" title="bank-format-preview" className="flex-1 w-full border-0">
                          <div className="p-6 text-center text-sm text-slate-600">Can&apos;t preview here — <a href={compareUrls.hills} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">open the {project.bank_name || 'Hills Bank'} PDF</a></div>
                        </object>
                      </div>
                      <div className="bg-white rounded-lg overflow-hidden flex flex-col min-h-0">
                        <div className="px-3 py-2 text-white text-sm font-semibold flex items-center justify-between flex-shrink-0" style={{ backgroundColor: '#1E4ED8' }}>
                          <span>DrawReady Standard</span>
                          <a href={compareUrls.drawready} target="_blank" rel="noopener noreferrer" data-testid="compare-open-drawready" className="text-xs underline hover:no-underline">Open in new tab</a>
                        </div>
                        <object data={compareUrls.drawready} type="application/pdf" title="drawready-format-preview" className="flex-1 w-full border-0">
                          <div className="p-6 text-center text-sm text-slate-600">Can&apos;t preview here — <a href={compareUrls.drawready} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">open the DrawReady PDF</a></div>
                        </object>
                      </div>
                    </div>
                  </DialogContent>
                )}
              </Dialog>

              {/* Real-time Interest Summary Card */}
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-testid="interest-accrued-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Clock className="w-5 h-5" />
                    Interest Accrued (Real-Time)
                  </CardTitle>
                  <p className="text-sm text-blue-600">Interest is calculated only on the amount drawn, not the total loan.</p>
                </CardHeader>
                <CardContent>
                  {interestCalc ? (
                    <div className="space-y-4">
                      {/* Headline: Interest Due (Accrued − Paid) */}
                      <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-red-200 flex flex-wrap items-center justify-between gap-3" data-testid="interest-due-banner">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Interest Due (Balance)</p>
                          <p className="text-3xl font-extrabold text-red-600" data-testid="interest-due-amount">
                            ${(interestCalc.interest_balance_due ?? interestCalc.total_interest ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">Accrued minus interest payments recorded below</p>
                        </div>
                        <div className="text-right text-sm space-y-1">
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-slate-500">Total accrued</span>
                            <span className="font-semibold text-slate-700">${(interestCalc.total_interest ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-slate-500">Interest paid</span>
                            <span className="font-semibold text-green-600">−${(interestCalc.interest_paid ?? interestPaidTotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <p className="text-xs text-slate-500 uppercase">Daily Interest</p>
                          <p className="text-2xl font-bold text-slate-700">${interestCalc.daily_interest_current?.toFixed(2)}/day</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <p className="text-xs text-slate-500 uppercase">Current Rate</p>
                          <p className="text-2xl font-bold text-blue-600">{interestCalc.current_rate}%</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <p className="text-xs text-slate-500 uppercase">Days Accrued</p>
                          <p className="text-2xl font-bold text-slate-700">{interestCalc.days_accrued}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <p className="text-xs text-slate-500 uppercase">Day Basis</p>
                          <p className="text-2xl font-bold text-slate-700">{interestCalc.day_basis || 360}<span className="text-sm font-normal text-slate-400">/yr</span></p>
                        </div>
                      </div>

                      {/* Projected payoff at maturity */}
                      {interestCalc.projected_payoff && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="projected-payoff">
                          <p className="text-xs text-amber-700 uppercase tracking-wide font-semibold">Projected payoff at maturity ({interestCalc.projected_payoff.maturity_date})</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2 text-sm">
                            <div>
                              <p className="text-slate-500">Interest due at maturity</p>
                              <p className="text-xl font-bold text-amber-700" data-testid="projected-interest-due">${(interestCalc.projected_payoff.projected_interest_due ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Additional interest ({interestCalc.projected_payoff.days_to_maturity} days)</p>
                              <p className="font-semibold text-slate-700">${(interestCalc.projected_payoff.projected_additional_interest ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Total interest at maturity</p>
                              <p className="font-semibold text-slate-700">${(interestCalc.projected_payoff.projected_total_interest ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Prompt to set a maturity date when the projection can't render */}
                      {!interestCalc.projected_payoff && interestCalc.needs_maturity_date && (
                        <div className="bg-amber-50 border border-dashed border-amber-300 rounded-lg p-4" data-testid="set-maturity-cta">
                          <p className="text-sm font-semibold text-amber-800">See your projected payoff at maturity</p>
                          <p className="text-xs text-amber-700 mt-0.5 mb-3">Add your loan maturity date and we'll project the total interest you'll owe if you hold to maturity.</p>
                          <div className="flex flex-wrap items-center gap-2">
                            <Input
                              type="date"
                              value={maturityInput}
                              onChange={(e) => setMaturityInput(e.target.value)}
                              className="w-44 bg-white"
                              data-testid="set-maturity-input"
                            />
                            <Button size="sm" onClick={handleSaveMaturityDate} disabled={savingMaturity} data-testid="set-maturity-save">
                              {savingMaturity ? 'Saving…' : 'Save maturity date'}
                            </Button>
                          </div>
                        </div>
                      )}

                      {interestCalc.amount_drawn > 0 && (
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Amount Drawn</p>
                              <p className="font-bold text-lg text-blue-600" data-testid="loan-amount-drawn">${(interestCalc.current_balance ?? interestCalc.amount_drawn)?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Total Loan</p>
                              <p className="font-semibold" data-testid="loan-total">${interestCalc.total_loan?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Available to Draw</p>
                              <p className="font-semibold text-green-600" data-testid="loan-available">${interestCalc.available_for_draw?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Owner Cash / Equity</p>
                              <p className="font-semibold text-slate-700" data-testid="loan-owner-equity">${(interestCalc.owner_cash_equity ?? 0)?.toLocaleString()}</p>
                            </div>
                          </div>
                          {(interestCalc.owner_cash_equity ?? 0) > 0 && (
                            <p className="text-[11px] text-slate-400 mt-2">Owner Cash / Equity = Total Budget − Total Loan (budget funded above the loan).</p>
                          )}
                        </div>
                      )}
                      {interestCalc.message && (
                        <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">{interestCalc.message}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-500">Set your amount drawn and interest start date to calculate interest.</p>
                  )}
                </CardContent>
              </Card>

              {/* Interest Payment Schedule (Jun 2026) — frequency-driven billing periods */}
              <Card className="mt-6 border-blue-200" data-testid="interest-schedule-card">
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-slate-800">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        Interest Payment Schedule
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Choose how you pay interest — the schedule below breaks accrued interest into billing periods.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Payment frequency</Label>
                        <Select
                          value={project?.interest_payment_frequency || 'monthly'}
                          onValueChange={(v) => handleUpdateInterestFrequency({ interest_payment_frequency: v })}
                          disabled={savingFrequency}
                        >
                          <SelectTrigger className="w-40 h-10" data-testid="interest-frequency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="annually">Annually</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">First payment due</Label>
                        <Input
                          type="date"
                          className="w-44 h-10"
                          value={project?.interest_first_payment_date || ''}
                          onChange={(e) => handleUpdateInterestFrequency({ interest_first_payment_date: e.target.value || null })}
                          data-testid="interest-first-payment-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {interestSchedule?.next_payment && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="interest-next-payment">
                      <div>
                        <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold">Next payment due</p>
                        <p className="text-lg font-bold text-slate-800">
                          {(() => { const d = new Date(interestSchedule.next_payment.due_date + 'T00:00:00'); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`; })()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Projected amount</p>
                        <p className="text-2xl font-extrabold text-blue-700">
                          ${(interestSchedule.next_payment.amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  )}
                  {interestSchedule?.schedule?.length ? (
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full text-sm" data-testid="interest-schedule-table">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                          <tr>
                            <th className="text-left px-3 py-2">Billing Period</th>
                            <th className="text-right px-3 py-2">Rate</th>
                            <th className="text-right px-3 py-2">Interest</th>
                            <th className="text-right px-3 py-2">Paid</th>
                            <th className="text-right px-3 py-2">Amount Due</th>
                            <th className="text-center px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {interestSchedule.schedule.map((row) => {
                            const badge = {
                              paid: 'bg-emerald-100 text-emerald-700',
                              partial: 'bg-amber-100 text-amber-700',
                              due: 'bg-red-100 text-red-700',
                              upcoming: 'bg-slate-100 text-slate-500',
                            }[row.status] || 'bg-slate-100 text-slate-500';
                            return (
                              <tr key={row.period} className="border-t border-slate-100" data-testid={`interest-schedule-row-${row.period}`}>
                                <td className="px-3 py-2 text-slate-700">{row.label}</td>
                                <td className="px-3 py-2 text-right text-slate-600">{row.rate}%</td>
                                <td className="px-3 py-2 text-right font-mono text-slate-700">${row.interest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-3 py-2 text-right font-mono text-green-600">${row.paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">${row.amount_due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${badge}`}>{row.status}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                      {interestSchedule?.message || 'Draw funds and set the interest start date to build your payment schedule.'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Monthly Loan Statement (interest + draw/activity, PDF export) */}
              <div className="mt-6">
                <MonthlyStatement mode="project" projectId={projectId} projectName={project?.name} />
              </div>


              {/* Interest Payments Log (v2.6.3) */}
              <Card data-testid="interest-payments-card">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      Interest Paid
                    </CardTitle>
                    <div className="text-right">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">Total interest paid</p>
                      <p className="text-xl font-bold text-green-700" data-testid="interest-paid-total">
                        ${interestPaidTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">Record each interest payment as it's collected (quarterly, annually, etc.). The total updates automatically.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add form */}
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end bg-slate-50 rounded-lg p-3">
                    <div className="w-full sm:flex-1 sm:min-w-[130px]">
                      <Label className="text-xs">Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={newInterestPayment.amount}
                        onChange={(e) => setNewInterestPayment({ ...newInterestPayment, amount: e.target.value })}
                        data-testid="interest-payment-amount-input"
                      />
                    </div>
                    <div className="w-full sm:flex-1 sm:min-w-[150px]">
                      <Label className="text-xs">Date paid</Label>
                      <Input
                        type="date"
                        value={newInterestPayment.date_paid}
                        onChange={(e) => setNewInterestPayment({ ...newInterestPayment, date_paid: e.target.value })}
                        data-testid="interest-payment-date-input"
                      />
                    </div>
                    <div className="w-full sm:flex-[1.5] sm:min-w-[170px]">
                      <Label className="text-xs">Note (optional)</Label>
                      <Input
                        placeholder="e.g. Q1 interest"
                        value={newInterestPayment.notes}
                        onChange={(e) => setNewInterestPayment({ ...newInterestPayment, notes: e.target.value })}
                        data-testid="interest-payment-notes-input"
                      />
                    </div>
                    <Button
                      onClick={handleAddInterestPayment}
                      disabled={savingInterestPayment}
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                      data-testid="interest-payment-add-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>

                  {/* List */}
                  {interestPayments.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No interest payments recorded yet.</p>
                  ) : (
                    <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                      {interestPayments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between px-3 py-2.5" data-testid={`interest-payment-row-${p.id}`}>
                          <div>
                            <p className="font-semibold text-slate-800">${Number(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p className="text-xs text-slate-400">{formatDate(p.date_paid)}{p.notes ? ` · ${p.notes}` : ''}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-red-600"
                            onClick={() => handleDeleteInterestPayment(p.id)}
                            data-testid={`interest-payment-delete-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>


              {/* Rate History Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Percent className="w-5 h-5 text-blue-600" />
                      Interest Rate History
                    </CardTitle>
                    <Dialog open={isAddRateDialogOpen} onOpenChange={setIsAddRateDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Rate Change
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Record Rate Change</DialogTitle>
                          <DialogDescription>
                            Add a new interest rate period for your adjustable rate loan. Leave End Date empty if this is the current rate.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAddRateEntry} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Start Date</Label>
                              <Input
                                type="date"
                                value={newRateEntry.effective_date}
                                onChange={(e) => setNewRateEntry({ ...newRateEntry, effective_date: e.target.value })}
                                required
                              />
                              <p className="text-xs text-slate-500">When this rate took effect</p>
                            </div>
                            <div className="space-y-2">
                              <Label>End Date (Optional)</Label>
                              <Input
                                type="date"
                                value={newRateEntry.end_date}
                                onChange={(e) => setNewRateEntry({ ...newRateEntry, end_date: e.target.value })}
                              />
                              <p className="text-xs text-slate-500">Leave empty for current rate</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Interest Rate (%)</Label>
                            <Input
                              type="number"
                              step="0.00001"
                              placeholder="6.87500"
                              value={newRateEntry.interest_rate}
                              onChange={(e) => setNewRateEntry({ ...newRateEntry, interest_rate: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Notes (Optional)</Label>
                            <Input
                              placeholder="e.g., Initial rate, Fed rate cut, etc."
                              value={newRateEntry.notes}
                              onChange={(e) => setNewRateEntry({ ...newRateEntry, notes: e.target.value })}
                            />
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-700">
                              <strong>Tip:</strong> For historical rates, enter both start and end dates. For the current rate that continues until the next rate change, leave the end date empty.
                            </p>
                          </div>
                          <Button type="submit" className="w-full">Add Rate Entry</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <CardDescription>
                    Track rate changes for your adjustable rate loan. Interest is calculated based on each rate period.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {rateHistory.length > 0 ? (
                    <div className="space-y-3">
                      {rateHistory.map((entry, index) => {
                        // Determine if this is the current/ongoing rate (no end_date)
                        const isCurrentRate = !entry.end_date;
                        const startDate = formatDateString(entry.effective_date);
                        const endDate = entry.end_date ? formatDateString(entry.end_date) : 'Present';
                        
                        return (
                          <div
                            key={entry.id}
                            className={`flex items-center justify-between p-4 rounded-lg border ${
                              isCurrentRate ? 'bg-green-50 border-green-200' : 'bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                isCurrentRate ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-600'
                              }`}>
                                <Percent className="w-6 h-6" />
                              </div>
                              <div>
                                <p className="font-semibold text-lg">{entry.interest_rate}%</p>
                                <p className="text-sm text-slate-500">
                                  {startDate} - {endDate}
                                  {isCurrentRate && <span className="ml-2 text-green-600 font-medium">(Current)</span>}
                                </p>
                                {entry.notes && <p className="text-xs text-slate-400 mt-1">{entry.notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-500 hover:text-blue-700"
                                onClick={() => handleEditRateEntry(entry)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteRateEntry(entry.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Percent className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 mb-2">No rate history recorded</p>
                      <p className="text-sm text-slate-400">Add your initial rate and any subsequent changes</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Edit Rate Dialog */}
              <Dialog open={isEditRateDialogOpen} onOpenChange={setIsEditRateDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Rate Period</DialogTitle>
                    <DialogDescription>
                      Update the rate details for this period. Leave End Date empty if this is the current/ongoing rate.
                    </DialogDescription>
                  </DialogHeader>
                  {editingRateEntry && (
                    <form onSubmit={handleUpdateRateEntry} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={editingRateEntry.effective_date}
                            onChange={(e) => setEditingRateEntry({ ...editingRateEntry, effective_date: e.target.value })}
                            required
                          />
                          <p className="text-xs text-slate-500">When this rate took effect</p>
                        </div>
                        <div className="space-y-2">
                          <Label>End Date (Optional)</Label>
                          <Input
                            type="date"
                            value={editingRateEntry.end_date}
                            onChange={(e) => setEditingRateEntry({ ...editingRateEntry, end_date: e.target.value })}
                          />
                          <p className="text-xs text-slate-500">Leave empty for current rate</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Interest Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.00001"
                          value={editingRateEntry.interest_rate}
                          onChange={(e) => setEditingRateEntry({ ...editingRateEntry, interest_rate: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Notes (Optional)</Label>
                        <Input
                          placeholder="e.g., Initial rate, Fed rate cut, etc."
                          value={editingRateEntry.notes}
                          onChange={(e) => setEditingRateEntry({ ...editingRateEntry, notes: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditRateDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="flex-1">Save Changes</Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              {/* Interest Breakdown by Period */}
              {interestCalc?.periods?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                      Interest Breakdown by Period
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      💡 This breakdown is automatically calculated from your Draw History and Rate History. 
                      To adjust periods, edit draws or rates in the sections below.
                    </p>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-slate-600">Period</th>
                          <th className="px-4 py-2 text-right text-sm font-medium text-slate-600">Balance</th>
                          <th className="px-4 py-2 text-center text-sm font-medium text-slate-600">Rate</th>
                          <th className="px-4 py-2 text-right text-sm font-medium text-slate-600">Per Diem</th>
                          <th className="px-4 py-2 text-center text-sm font-medium text-slate-600">Days</th>
                          <th className="px-4 py-2 text-right text-sm font-medium text-slate-600">Interest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {interestCalc.periods.map((period, index) => {
                          // Show "Present" for ongoing/current rate periods
                          const endDateDisplay = period.is_current || period.display_end_date === 'present' 
                            ? 'Present' 
                            : formatDateString(period.end_date);
                          
                          // Calculate per diem: Balance × Rate ÷ 360 ÷ 100
                          const perDiem = period.balance && period.rate 
                            ? (period.balance * (period.rate / 100) / 360)
                            : 0;
                          
                          return (
                            <tr key={index} className={`border-b ${period.is_current ? 'bg-green-50' : ''}`}>
                              <td className="px-4 py-3 text-sm">
                                {formatDateString(period.start_date)} - {endDateDisplay}
                                {period.is_current && <span className="ml-2 text-green-600 text-xs font-medium">(Current)</span>}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-medium">
                                ${period.balance?.toLocaleString() || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-center font-medium">{period.rate}%</td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-amber-600">
                                ${perDiem.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/day
                              </td>
                              <td className="px-4 py-3 text-sm text-center">{period.days}</td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-red-600">
                                ${period.interest?.toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-100">
                        <tr>
                          <td className="px-4 py-3 font-medium" colSpan={3}>Total</td>
                          <td className="px-4 py-3 text-right font-medium text-amber-600">
                            ${interestCalc.daily_interest_current?.toFixed(2)}/day
                          </td>
                          <td className="px-4 py-3 text-center font-medium">{interestCalc.days_accrued}</td>
                          <td className="px-4 py-3 text-right font-bold text-red-600">
                            ${interestCalc.total_interest?.toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Draw & Interest Projection (pro-forma) */}
              <DrawInterestProjection projectId={projectId} />

              {/* Submitted Draw Requests — funded draws + downloadable records */}
              <Card data-testid="submitted-draw-requests-card">
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Submitted Draw Requests
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Draws submitted to the bank &mdash; awaiting confirmation and funded. Download the exact bank form, a full audit package, or a summary for your records.
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setIsAddDrawDialogOpen(true)} data-testid="record-manual-draw-btn">
                    <Plus className="w-4 h-4 mr-1" /> Record Draw
                  </Button>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const FUNDED = ['deposited', 'confirmed', 'completed', 'funded', 'approved'];
                    const REJECTED = ['rejected', 'denied', 'cancelled', 'canceled', 'void', 'voided'];
                    // Awaiting = submitted to the bank but not yet funded or rejected.
                    const awaiting = (reimbursementHistory || [])
                      .filter((r) => {
                        const s = (r.status || '').toLowerCase();
                        return !FUNDED.includes(s) && !REJECTED.includes(s);
                      })
                      .map((r) => ({
                        key: `pend-${r.id}`, id: r.id, raw: r,
                        draw_number: r.draw_number, date: r.request_date,
                        amount: Number(r.total_amount || 0),
                        items: (r.phase_details?.length || 0) + (r.expense_details?.length || 0),
                      }))
                      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
                    const funded = (reimbursementHistory || [])
                      .filter((r) => FUNDED.includes((r.status || '').toLowerCase()))
                      .map((r) => ({
                        key: `rr-${r.id}`, type: 'request', id: r.id,
                        draw_number: r.draw_number, date: r.request_date,
                        amount: Number(r.total_amount || 0),
                        items: (r.phase_details?.length || 0) + (r.expense_details?.length || 0),
                      }));
                    const manual = (drawHistory || [])
                      .filter((d) => !d.reimbursement_request_id)
                      .map((d) => ({
                        key: `dh-${d.id}`, type: 'manual', id: d.id, raw: d,
                        draw_number: null, date: d.draw_date,
                        amount: Number(d.draw_amount || 0), description: d.description,
                      }));
                    const rows = [...funded, ...manual].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
                    if (rows.length === 0 && awaiting.length === 0) {
                      return (
                        <div className="text-center py-6 text-slate-500">
                          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p>No draws yet</p>
                          <p className="text-sm mt-1">Draws you submit to the bank show here &mdash; first as "awaiting confirmation", then as funded records once the money lands</p>
                        </div>
                      );
                    }
                    return (
                      <div
                        className="space-y-3 overflow-y-auto pr-1"
                        style={{ maxHeight: '470px' }}
                        data-testid="submitted-draws-scroll"
                      >
                        {awaiting.length > 0 && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="awaiting-confirmation-section">
                            <div className="flex items-center gap-2 mb-1">
                              <Clock className="w-4 h-4 text-amber-600" />
                              <span className="text-sm font-semibold text-amber-900">Awaiting bank confirmation ({awaiting.length})</span>
                            </div>
                            <p className="text-xs text-amber-700/90 mb-2">Submitted to the bank, not yet funded. Download the record, or mark it funded once the money hits your account.</p>
                            <div className="space-y-2">
                              {awaiting.map((row) => (
                                <div
                                  key={row.key}
                                  data-testid={`awaiting-draw-${row.draw_number}`}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-amber-200 rounded-lg p-3"
                                >
                                  <div className="min-w-0">
                                    <div className="font-semibold text-slate-900">
                                      Draw #{row.draw_number} &middot; ${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                    <div className="text-sm text-slate-500 mt-0.5">
                                      Submitted {formatDateString(row.date?.split('T')[0])} &middot; {row.items} line {row.items === 1 ? 'item' : 'items'}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                                    <Button size="sm" variant="outline" data-testid={`awaiting-download-bank-form-${row.draw_number}`} onClick={() => handleDownloadDrawRecord(row.id, 'bankform', row.draw_number)}>
                                      <Download className="w-4 h-4 mr-1" /> Bank Form
                                    </Button>
                                    <Button size="sm" variant="outline" data-testid={`awaiting-download-package-${row.draw_number}`} onClick={() => handleDownloadDrawRecord(row.id, 'package', row.draw_number)}>
                                      <Download className="w-4 h-4 mr-1" /> Package
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-white"
                                      data-testid={`awaiting-mark-funded-${row.draw_number}`}
                                      onClick={() => {
                                        setSelectedReimbursementForDeposit(row.raw);
                                        setDepositDate(new Date().toISOString().split('T')[0]);
                                        setIsConfirmDepositDialogOpen(true);
                                      }}
                                    >
                                      <CheckCircle2 className="w-4 h-4 mr-1" /> Mark as funded
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {rows.map((row) => (
                          <div
                            key={row.key}
                            data-testid={row.type === 'request' ? `submitted-draw-${row.draw_number}` : `manual-draw-${row.id}`}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border rounded-lg p-4"
                          >
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-900">
                                {row.type === 'request' ? `Draw #${row.draw_number} · ` : ''}${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <div className="text-sm text-slate-500 mt-0.5">
                                {row.type === 'request'
                                  ? `Funded ${formatDateString(row.date?.split('T')[0])} · ${row.items} line ${row.items === 1 ? 'item' : 'items'}`
                                  : `${row.description || 'Manual draw'} · ${formatDateString(row.date?.split('T')[0])} · recorded manually`}
                              </div>
                            </div>
                            {row.type === 'request' ? (
                              <div className="flex flex-wrap items-center gap-2 shrink-0">
                                <Button size="sm" variant="outline" data-testid={`download-bank-form-${row.draw_number}`} onClick={() => handleDownloadDrawRecord(row.id, 'bankform', row.draw_number)}>
                                  <Download className="w-4 h-4 mr-1" /> Bank Form
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`download-package-${row.draw_number}`} onClick={() => handleDownloadDrawRecord(row.id, 'package', row.draw_number)}>
                                  <Download className="w-4 h-4 mr-1" /> Full Package
                                </Button>
                                <Button size="sm" variant="ghost" title="Edit draw info" data-testid={`edit-funded-draw-${row.id}`} onClick={() => handleEditFundedDraw(row)} className="h-7 w-7 p-0"><Edit2 className="w-3 h-3" /></Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 shrink-0">
                                <span className="text-xs text-slate-400 mr-1">No documents</span>
                                <Button size="sm" variant="ghost" onClick={() => handleEditDrawEntry(row.raw)} className="h-7 w-7 p-0"><Edit2 className="w-3 h-3" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteDrawEntry(row.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Edit Funded Draw Dialog */}
              <Dialog open={isEditFundedDrawOpen} onOpenChange={setIsEditFundedDrawOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Edit Draw</DialogTitle>
                    <DialogDescription>Update the draw number and funded date shown on this submitted draw.</DialogDescription>
                  </DialogHeader>
                  {editingFundedDraw && (
                    <form onSubmit={handleUpdateFundedDraw} className="space-y-4">
                      <div>
                        <Label>Draw Number</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={editingFundedDraw.draw_number}
                          onChange={(e) => setEditingFundedDraw({ ...editingFundedDraw, draw_number: e.target.value })}
                          placeholder="e.g. 2"
                          data-testid="edit-funded-draw-number-input"
                        />
                      </div>
                      <div>
                        <Label>Funded Date</Label>
                        <Input
                          type="date"
                          value={editingFundedDraw.request_date}
                          onChange={(e) => setEditingFundedDraw({ ...editingFundedDraw, request_date: e.target.value })}
                          data-testid="edit-funded-draw-date-input"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditFundedDrawOpen(false)}>Cancel</Button>
                        <Button type="submit" className="flex-1" data-testid="edit-funded-draw-save-btn">Save</Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>



              {/* Add Draw Dialog */}
              <Dialog open={isAddDrawDialogOpen} onOpenChange={setIsAddDrawDialogOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Record New Draw</DialogTitle>
                    <DialogDescription>Add a draw to track interest on changing balances</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddDrawEntry} className="space-y-4">
                    <div>
                      <Label>Draw Date</Label>
                      <Input 
                        type="date" 
                        value={newDrawEntry.draw_date}
                        onChange={(e) => setNewDrawEntry({ ...newDrawEntry, draw_date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Draw Amount ($)</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        placeholder="47199.04"
                        value={newDrawEntry.draw_amount}
                        onChange={(e) => setNewDrawEntry({ ...newDrawEntry, draw_amount: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Input 
                        placeholder="e.g., Initial Draw, Draw #2 - Framing"
                        value={newDrawEntry.description}
                        onChange={(e) => setNewDrawEntry({ ...newDrawEntry, description: e.target.value })}
                      />
                    </div>
                    
                    {/* Rate Change Prompt */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox"
                          id="rate-changed"
                          checked={newDrawEntry.rate_changed}
                          onChange={(e) => setNewDrawEntry({ ...newDrawEntry, rate_changed: e.target.checked, new_rate: '' })}
                          className="w-4 h-4 rounded border-amber-300"
                        />
                        <Label htmlFor="rate-changed" className="text-sm text-amber-800 font-medium cursor-pointer">
                          Did your interest rate change on this date?
                        </Label>
                      </div>
                      {newDrawEntry.rate_changed && (
                        <div className="pl-6">
                          <Label className="text-sm text-amber-700">New Rate (%)</Label>
                          <Input 
                            type="number" 
                            step="0.001"
                            placeholder="7.125"
                            value={newDrawEntry.new_rate}
                            onChange={(e) => setNewDrawEntry({ ...newDrawEntry, new_rate: e.target.value })}
                            className="mt-1 bg-white"
                            required={newDrawEntry.rate_changed}
                          />
                          <p className="text-xs text-amber-600 mt-1">
                            Current rate: {project?.loan_interest_rate || 'Not set'}%
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setIsAddDrawDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1">Record Draw</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Edit Draw Dialog */}
              <Dialog open={isEditDrawDialogOpen} onOpenChange={setIsEditDrawDialogOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Edit Draw Entry</DialogTitle>
                    <DialogDescription>Update draw information</DialogDescription>
                  </DialogHeader>
                  {editingDrawEntry && (
                    <form onSubmit={handleUpdateDrawEntry} className="space-y-4">
                      <div>
                        <Label>Draw Date</Label>
                        <Input 
                          type="date" 
                          value={editingDrawEntry.draw_date}
                          onChange={(e) => setEditingDrawEntry({ ...editingDrawEntry, draw_date: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Draw Amount ($)</Label>
                        <Input 
                          type="number" 
                          step="0.01"
                          value={editingDrawEntry.draw_amount}
                          onChange={(e) => setEditingDrawEntry({ ...editingDrawEntry, draw_amount: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Description (Optional)</Label>
                        <Input 
                          value={editingDrawEntry.description}
                          onChange={(e) => setEditingDrawEntry({ ...editingDrawEntry, description: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditDrawDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="flex-1">Save Changes</Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              {/* Confirm Deposit Dialog */}
              <Dialog open={isConfirmDepositDialogOpen} onOpenChange={setIsConfirmDepositDialogOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>💰 Confirm Bank Deposit</DialogTitle>
                    <DialogDescription>
                      Confirm that the funds from this draw request have been deposited. This will record the draw and start interest accrual from the deposit date.
                    </DialogDescription>
                  </DialogHeader>
                  {selectedReimbursementForDeposit && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700">
                          <span className="font-semibold">Draw Amount:</span> ${selectedReimbursementForDeposit.total_amount?.toLocaleString()}
                        </p>
                        <p className="text-sm text-blue-700">
                          <span className="font-semibold">Request Date:</span> {formatDateString(selectedReimbursementForDeposit.request_date?.split('T')[0])}
                        </p>
                      </div>
                      <div>
                        <Label>Deposit Date (when funds hit your account)</Label>
                        <Input 
                          type="date" 
                          value={depositDate}
                          onChange={(e) => setDepositDate(e.target.value)}
                          required
                        />
                        <p className="text-xs text-slate-500 mt-1">Interest will start accruing from this date</p>
                      </div>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" className="flex-1" onClick={() => setIsConfirmDepositDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleConfirmDeposit} className="flex-1 bg-green-600 hover:bg-green-700">
                          ✓ Confirm Deposit
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Draw Details Dialog */}
              <Dialog open={isDrawDetailsDialogOpen} onOpenChange={setIsDrawDetailsDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      📋 Draw #{selectedDrawDetails?.draw_number || '?'} Details
                    </DialogTitle>
                    <DialogDescription>
                      Items included in this draw request
                    </DialogDescription>
                  </DialogHeader>
                  {selectedDrawDetails && (
                    <div className="space-y-4">
                      {/* Summary */}
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-blue-600 uppercase">Total Amount</p>
                            <p className="font-bold text-xl text-blue-700">${selectedDrawDetails.total_amount?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600 uppercase">Request Date</p>
                            <p className="font-semibold">{formatDateString(selectedDrawDetails.request_date?.split('T')[0])}</p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600 uppercase">Status</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              selectedDrawDetails.status === 'deposited' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {selectedDrawDetails.status === 'deposited' ? '✓ Deposited' : '⏳ Pending'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Phase Items */}
                      {selectedDrawDetails.phase_details && selectedDrawDetails.phase_details.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                              {selectedDrawDetails.phase_details.length}
                            </span>
                            Construction Phases
                          </h4>
                          <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="text-left p-2">Phase</th>
                                  <th className="text-left p-2">Contractor/Vendor</th>
                                  <th className="text-right p-2">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedDrawDetails.phase_details.map((phase, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="p-2">
                                      <span className="font-medium">{phase.name}</span>
                                      {phase.bank_category && (
                                        <span className="text-xs text-slate-500 block">{phase.bank_category}</span>
                                      )}
                                    </td>
                                    <td className="p-2 text-slate-600">
                                      {phase.contractor_name || phase.supplier_vendor || '-'}
                                    </td>
                                    <td className="p-2 text-right font-semibold text-green-600">
                                      ${phase.amount?.toLocaleString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Expense Items */}
                      {selectedDrawDetails.expense_details && selectedDrawDetails.expense_details.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs">
                              {selectedDrawDetails.expense_details.length}
                            </span>
                            Soft Costs / Expenses
                          </h4>
                          <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="text-left p-2">Type</th>
                                  <th className="text-left p-2">Description</th>
                                  <th className="text-right p-2">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedDrawDetails.expense_details.map((expense, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="p-2 capitalize">{expense.expense_type?.replace('_', ' ')}</td>
                                    <td className="p-2 text-slate-600">{expense.description || '-'}</td>
                                    <td className="p-2 text-right font-semibold text-green-600">
                                      ${expense.amount?.toLocaleString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Legacy fallback for old draws without details */}
                      {(!selectedDrawDetails.phase_details || selectedDrawDetails.phase_details.length === 0) && 
                       (!selectedDrawDetails.expense_details || selectedDrawDetails.expense_details.length === 0) && (
                        <div className="text-center text-slate-500 py-4">
                          <p>Detailed breakdown not available for this draw request.</p>
                          <p className="text-xs">(Draw requests before the details feature was added)</p>
                        </div>
                      )}

                      <Button onClick={() => setIsDrawDetailsDialogOpen(false)} className="w-full">
                        Close
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Loan Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-slate-600" />
                    Loan Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 uppercase font-medium">Amount Drawn</p>
                      <p className="font-bold text-xl text-blue-700">${project?.amount_drawn?.toLocaleString() || '0'}</p>
                      <p className="text-xs text-blue-500">Interest calculated on this</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Total Loan</p>
                      <p className="font-semibold">${project?.loan_principal_amount?.toLocaleString() || '-'}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs text-green-600 uppercase">Available for Draw</p>
                      <p className="font-semibold text-green-700">${project?.loan_available_for_draw?.toLocaleString() || '-'}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Interest Start Date</p>
                      <p className="font-semibold">
                        {project?.interest_start_date 
                          ? formatDate(project.interest_start_date) 
                          : '-'}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Bank Name</p>
                      <p className="font-semibold">{project?.bank_name || '-'}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Loan Number</p>
                      <p className="font-semibold">{project?.loan_number || '-'}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Draw Account</p>
                      <p className="font-semibold">{project?.draw_account || '-'}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t flex gap-3">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline">
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit Loan Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Edit Loan Details</DialogTitle>
                          <DialogDescription>Update your loan information</DialogDescription>
                        </DialogHeader>
                        {/* v2.5.14 — local state so Available for Draw recomputes live as the
                            user types in Total Loan or Amount Drawn. Previously the field was
                            free-text and stored as-is, so changing Total Loan didn't move it. */}
                        <LoanDetailsEditForm
                          project={project}
                          userId={user?.id}
                          onSubmit={async (payload) => {
                            try {
                              await apiService.updateProject(projectId, payload);
                              toast.success('Loan details updated!');
                              loadProjectData();
                            } catch (error) {
                              toast.error('Failed to update loan details');
                            }
                          }}
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
      </div>

      {/* v2.5.5.6 — Send timing questionnaire modal */}
      <SendScheduleQuestionnaireModal
        open={showScheduleQModal}
        onOpenChange={setShowScheduleQModal}
        project={project}
        user={user}
      />

      {/* Start Project / Break Ground Dialog */}
      <Dialog open={isStartProjectDialogOpen} onOpenChange={setIsStartProjectDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="start-project-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-blue-600" />
              Start Project — break ground
            </DialogTitle>
            <DialogDescription>
              You're about to notify {contractorCount} contractor{contractorCount === 1 ? '' : 's'} that
              <strong> {project?.name}</strong> is officially live. Phase 1 will auto-status to
              <strong> in progress</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
              <p className="font-semibold mb-1">Every contractor will receive:</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-800">
                <li>"We broke ground today" email (their phase highlighted)</li>
                <li>Full project schedule grid</li>
                <li>Job site rules (if configured)</li>
                <li>Your contact info + company logo (if uploaded)</li>
              </ul>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">
                Optional message (appended to every contractor email)
              </label>
              <textarea
                value={startCustomMessage}
                onChange={(e) => setStartCustomMessage(e.target.value)}
                placeholder="E.g. Looking forward to a smooth build — let's hit the ground running!"
                rows={3}
                className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="start-project-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStartProjectDialogOpen(false)} disabled={isStartingProject}>
              Not yet
            </Button>
            <Button
              onClick={handleStartProject}
              disabled={isStartingProject}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="start-project-confirm"
            >
              {isStartingProject ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Starting…</>
              ) : (
                <><Rocket className="w-4 h-4 mr-1.5" />Break ground & notify</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push Start Date Dialog — opens from /project/{id}?action=push-start (from email)
          or by adding a 3-dot menu item later. */}
      <Dialog open={isPushStartDialogOpen} onOpenChange={setIsPushStartDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="push-start-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              Push start date
            </DialogTitle>
            <DialogDescription>
              Schedule slipped? Pick a new planned start date for <strong>{project?.name}</strong>.
              We'll shift phase dates, re-arm reminders, and notify every contractor by email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <p>
                Current planned start:{' '}
                <strong>
                  {project?.start_date
                    ? formatDate(project.start_date)
                    : 'Not set'}
                </strong>
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">New start date</label>
              <input
                type="date"
                value={pushStartNewDate}
                onChange={(e) => setPushStartNewDate(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                data-testid="push-start-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">
                Reason <span className="font-normal text-slate-400">(optional — sent to contractors)</span>
              </label>
              <textarea
                value={pushStartReason}
                onChange={(e) => setPushStartReason(e.target.value)}
                placeholder="E.g. Permit delayed, weather, material backorder…"
                rows={2}
                className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                data-testid="push-start-reason"
              />
            </div>
            <div className="space-y-2 rounded-md bg-slate-50 border border-slate-200 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pushStartShiftPhases}
                  onChange={(e) => setPushStartShiftPhases(e.target.checked)}
                  className="mt-0.5"
                  data-testid="push-start-shift-phases"
                />
                <div>
                  <p className="text-xs font-semibold text-slate-800">Shift all phase dates by the same delta</p>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Phases already in progress are skipped. Recommended — keeps the cascade aligned.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pushStartNotifyContractors}
                  onChange={(e) => setPushStartNotifyContractors(e.target.checked)}
                  className="mt-0.5"
                  data-testid="push-start-notify"
                />
                <div>
                  <p className="text-xs font-semibold text-slate-800">
                    Notify all {contractorCount > 0 ? contractorCount : ''} contractor{contractorCount === 1 ? '' : 's'} by email
                  </p>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Each contractor gets their new phase start date highlighted, plus your reason if you provided one.
                  </p>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPushStartDialogOpen(false)} disabled={isPushingStart}>
              Cancel
            </Button>
            <Button
              onClick={handlePushStartDate}
              disabled={isPushingStart || !pushStartNewDate}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="push-start-confirm"
            >
              {isPushingStart ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Pushing…</>
              ) : (
                <><Calendar className="w-4 h-4 mr-1.5" />Push start date</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Financial Analysis Dialog */}
      <Dialog open={isFinancialAnalysisDialogOpen} onOpenChange={setIsFinancialAnalysisDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📊 Financial Analysis: Sell vs. Hold</DialogTitle>
            <DialogDescription>
              Analyze your investment returns for build-to-sell or build-to-rent scenarios
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-2 border-b mb-4">
            <button
              onClick={() => {
                setFinancialAnalysisTab('sell');
                setFinancialAnalysisResults(null);
              }}
              className={`px-6 py-3 font-medium transition-colors ${
                financialAnalysisTab === 'sell'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Build-to-Sell
            </button>
            <button
              onClick={() => {
                setFinancialAnalysisTab('rent');
                setFinancialAnalysisResults(null);
              }}
              className={`px-6 py-3 font-medium transition-colors ${
                financialAnalysisTab === 'rent'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Build-to-Rent
            </button>
          </div>

          {/* Build-to-Sell Form */}
          {financialAnalysisTab === 'sell' && (
            <div className="space-y-6">
              {/* Sale Info */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-3">💰 Sale Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Expected Sale Price *</Label>
                    <Input
                      type="number"
                      value={financialAnalysisInputs.sale_price || ''}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, sale_price: parseFloat(e.target.value) || 0 })}
                      placeholder="450000"
                    />
                  </div>
                  <div>
                    <Label>Agent Commission (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={financialAnalysisInputs.agent_commission_percent}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, agent_commission_percent: parseFloat(e.target.value) || 6 })}
                    />
                  </div>
                  <div>
                    <Label>Closing Costs ($)</Label>
                    <Input
                      type="number"
                      value={financialAnalysisInputs.closing_costs_fixed || ''}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, closing_costs_fixed: parseFloat(e.target.value) || 0 })}
                      placeholder="5000"
                    />
                  </div>
                </div>
              </div>

              {/* Cost Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-3">🏗️ Project Costs</h4>
                <p className="text-xs text-blue-600 mb-3">Enter your costs OR let the system use project budget. Your inputs override automatic calculations.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Land Cost ($)</Label>
                    <Input
                      type="number"
                      value={financialAnalysisInputs.land_cost || ''}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, land_cost: parseFloat(e.target.value) || 0 })}
                      placeholder="100000"
                    />
                  </div>
                  <div>
                    <Label>Construction Cost ($)</Label>
                    <Input
                      type="number"
                      value={financialAnalysisInputs.construction_cost || ''}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, construction_cost: parseFloat(e.target.value) || 0 })}
                      placeholder="250000"
                    />
                  </div>
                  <div>
                    <Label>Total Project Cost (Override)</Label>
                    <Input
                      type="number"
                      value={financialAnalysisInputs.total_project_cost || ''}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, total_project_cost: parseFloat(e.target.value) || 0 })}
                      placeholder={`Auto: ${project?.total_budget?.toLocaleString() || 0}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to use Land + Construction or project budget</p>
                  </div>
                  <div>
                    <Label>Holding Costs ($)</Label>
                    <Input
                      type="number"
                      value={financialAnalysisInputs.holding_costs || ''}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, holding_costs: parseFloat(e.target.value) || 0 })}
                      placeholder="Taxes, insurance during construction"
                    />
                  </div>
                </div>
              </div>

              {/* Financing */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-3">💳 Financing Costs</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Interest Expense ($)</Label>
                    <Input
                      type="number"
                      value={financialAnalysisInputs.interest_expense || ''}
                      onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, interest_expense: parseFloat(e.target.value) || 0 })}
                      placeholder="Total interest paid during project"
                    />
                    {interestCalc?.total_interest > 0 && (
                      <p className="text-xs text-orange-600 mt-1">
                        Loan interest accrued: ${interestCalc.total_interest.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <Button onClick={handleCalculateFinancialAnalysis} disabled={isCalculatingAnalysis} className="w-full bg-green-600 hover:bg-green-700">
                {isCalculatingAnalysis ? 'Calculating...' : '📊 Calculate Build-to-Sell Analysis'}
              </Button>

              {/* Sell Results */}
              {financialAnalysisResults && financialAnalysisResults.analysis_type === 'sell' && (
                <div className="mt-6 space-y-4 bg-gradient-to-br from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
                  <h3 className="text-lg font-bold text-green-900">📈 Analysis Results</h3>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                      <p className="text-sm text-gray-600">Net Profit</p>
                      <p className={`text-2xl font-bold ${financialAnalysisResults.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${financialAnalysisResults.net_profit?.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                      <p className="text-sm text-gray-600">ROI</p>
                      <p className={`text-2xl font-bold ${financialAnalysisResults.roi_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {financialAnalysisResults.roi_percent?.toFixed(1)}%
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                      <p className="text-sm text-gray-600">Net Proceeds</p>
                      <p className="text-2xl font-bold text-blue-600">
                        ${financialAnalysisResults.net_proceeds?.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Detailed Breakdown */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-700 border-b pb-1">Revenue</h4>
                      <div className="flex justify-between">
                        <span>Sale Price:</span>
                        <span className="font-medium">${financialAnalysisResults.sale_price?.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-700 border-b pb-1">Selling Costs</h4>
                      <div className="flex justify-between">
                        <span>Agent Commission ({financialAnalysisResults.agent_commission_percent}%):</span>
                        <span className="text-red-600">-${financialAnalysisResults.agent_commission?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Closing Costs:</span>
                        <span className="text-red-600">-${financialAnalysisResults.closing_costs_fixed?.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-700 border-b pb-1">Project Costs</h4>
                      {financialAnalysisResults.land_cost > 0 && (
                        <div className="flex justify-between">
                          <span>Land Cost:</span>
                          <span>${financialAnalysisResults.land_cost?.toLocaleString()}</span>
                        </div>
                      )}
                      {financialAnalysisResults.construction_cost > 0 && (
                        <div className="flex justify-between">
                          <span>Construction:</span>
                          <span>${financialAnalysisResults.construction_cost?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-medium">
                        <span>Total Project Cost:</span>
                        <span>${financialAnalysisResults.total_project_cost?.toLocaleString()}</span>
                      </div>
                      {financialAnalysisResults.holding_costs > 0 && (
                        <div className="flex justify-between">
                          <span>Holding Costs:</span>
                          <span>${financialAnalysisResults.holding_costs?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Interest Expense:</span>
                        <span>${financialAnalysisResults.interest_expense?.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-700 border-b pb-1">Summary</h4>
                      <div className="flex justify-between">
                        <span>Total All-In Cost:</span>
                        <span className="font-medium">${financialAnalysisResults.total_all_in_cost?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Selling Costs:</span>
                        <span className="text-red-600">-${financialAnalysisResults.total_selling_costs?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Net Profit:</span>
                        <span className={financialAnalysisResults.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                          ${financialAnalysisResults.net_profit?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Build-to-Rent Form */}
          {financialAnalysisTab === 'rent' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-lg font-semibold">Property Information</Label>
                </div>
                <div>
                  <Label>Property Value</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.property_value || project?.total_budget || 0}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, property_value: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Monthly Rental Income</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.monthly_rental_income}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, monthly_rental_income: parseFloat(e.target.value) })}
                    placeholder="7200"
                  />
                </div>

                <div className="col-span-2 mt-4">
                  <Label className="text-lg font-semibold">Operating Expenses (Annual)</Label>
                </div>
                <div>
                  <Label>Insurance</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.annual_insurance}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, annual_insurance: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Maintenance & Repairs</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.annual_maintenance}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, annual_maintenance: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Property Tax</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.annual_property_tax}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, annual_property_tax: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Management Fee (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={financialAnalysisInputs.management_fee_percent}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, management_fee_percent: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Annual Reserves</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.annual_reserves}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, annual_reserves: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Misc. Expenses</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.misc_expenses}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, misc_expenses: parseFloat(e.target.value) })}
                  />
                </div>

                <div className="col-span-2 mt-4">
                  <Label className="text-lg font-semibold">Financing</Label>
                </div>
                <div>
                  <Label>First Mortgage Amount</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.first_mortgage_amount}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, first_mortgage_amount: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>First Mortgage Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={financialAnalysisInputs.first_mortgage_rate}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, first_mortgage_rate: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>First Mortgage Term (Years)</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.first_mortgage_term_years}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, first_mortgage_term_years: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Second Mortgage Amount</Label>
                  <Input
                    type="number"
                    value={financialAnalysisInputs.second_mortgage_amount}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, second_mortgage_amount: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Second Mortgage Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={financialAnalysisInputs.second_mortgage_rate}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, second_mortgage_rate: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="interest-only"
                    checked={financialAnalysisInputs.second_mortgage_interest_only}
                    onChange={(e) => setFinancialAnalysisInputs({ ...financialAnalysisInputs, second_mortgage_interest_only: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="interest-only" className="text-sm">Interest Only (2nd Mortgage)</label>
                </div>
              </div>

              <Button onClick={handleCalculateFinancialAnalysis} disabled={isCalculatingAnalysis} className="w-full">
                {isCalculatingAnalysis ? 'Calculating...' : 'Calculate Build-to-Rent Analysis'}
              </Button>

              {/* Rent Results */}
              {financialAnalysisResults && financialAnalysisResults.analysis_type === 'rent' && (
                <div className="mt-6 space-y-4">
                  {/* Summary Metrics */}
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-lg font-bold text-green-900 mb-4">Key Metrics</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Year 1 NOI</p>
                        <p className="text-xl font-bold text-green-700">${financialAnalysisResults.summary?.year1_noi?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Year 1 Cash Flow</p>
                        <p className="text-xl font-bold text-green-700">${financialAnalysisResults.summary?.year1_cash_flow?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Debt Coverage Ratio</p>
                        <p className="text-xl font-bold">{financialAnalysisResults.summary?.year1_dcr}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Cap Rate</p>
                        <p className="text-xl font-bold">{financialAnalysisResults.summary?.cap_rate_percent?.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Cash-on-Cash Return</p>
                        <p className="text-xl font-bold">{financialAnalysisResults.summary?.cash_on_cash_return_percent?.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">10-Year Total Cash Flow</p>
                        <p className="text-xl font-bold text-green-700">${financialAnalysisResults.summary?.total_10yr_cash_flow?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* 10-Year Projection Table */}
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <h3 className="text-lg font-bold p-4 bg-gray-50 border-b">10-Year Cash Flow Projection</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left">Year</th>
                            <th className="px-4 py-2 text-right">Gross Rent</th>
                            <th className="px-4 py-2 text-right">EGI</th>
                            <th className="px-4 py-2 text-right">Op. Expenses</th>
                            <th className="px-4 py-2 text-right">NOI</th>
                            <th className="px-4 py-2 text-right">Debt Service</th>
                            <th className="px-4 py-2 text-right">Cash Flow</th>
                            <th className="px-4 py-2 text-right">DCR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financialAnalysisResults.projections?.map((proj) => (
                            <tr key={proj.year} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">Year {proj.year}</td>
                              <td className="px-4 py-2 text-right">${proj.gross_rental_income?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right">${proj.effective_gross_income?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right">${proj.total_operating_expenses?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right font-semibold">${proj.noi?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right">${proj.debt_service?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right font-semibold text-green-600">${proj.cash_flow?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right">{proj.debt_coverage_ratio}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay Contractor Dialog - REMOVED (Stripe only for membership payments) */}

      {/* Edit Contractor Dialog - Outside all tabs */}
      <Dialog open={isEditContractorDialogOpen} onOpenChange={(open) => {
        setIsEditContractorDialogOpen(open);
        if (!open) setEditingContractor(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contractor</DialogTitle>
            <DialogDescription>Update contractor information</DialogDescription>
          </DialogHeader>
          {editingContractor && (
            <form onSubmit={handleUpdateContractor} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-contractor-company">Company Name</Label>
                <Input
                  id="edit-contractor-company"
                  placeholder="ABC Construction LLC"
                  value={editingContractor.company}
                  onChange={(e) => setEditingContractor({ ...editingContractor, company: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-contractor-name">Company Representative</Label>
                <Input
                  id="edit-contractor-name"
                  placeholder="John Smith"
                  value={editingContractor.name}
                  onChange={(e) => setEditingContractor({ ...editingContractor, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-contractor-email">Email</Label>
                <Input
                  id="edit-contractor-email"
                  type="email"
                  placeholder="john@example.com"
                  value={editingContractor.email}
                  onChange={(e) => setEditingContractor({ ...editingContractor, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-contractor-phone">Phone</Label>
                <Input
                  id="edit-contractor-phone"
                  placeholder="(555) 123-4567"
                  value={editingContractor.phone}
                  onChange={(e) => setEditingContractor({ ...editingContractor, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-contractor-address">Address</Label>
                <Input
                  id="edit-contractor-address"
                  placeholder="123 Main St, City, State ZIP"
                  value={editingContractor.address}
                  onChange={(e) => setEditingContractor({ ...editingContractor, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-contractor-ein">EIN/SSN (for 1099)</Label>
                <Input
                  id="edit-contractor-ein"
                  placeholder="XX-XXXXXXX"
                  value={editingContractor.ein_ssn}
                  onChange={(e) => setEditingContractor({ ...editingContractor, ein_ssn: e.target.value })}
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={() => {
                    handleDeleteContractor(editingContractor.id);
                    setIsEditContractorDialogOpen(false);
                  }}
                  className="flex-1"
                >
                  Delete Contractor
                </Button>
                <Button type="submit" className="flex-1">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* 1099 Report Dialog - Moved outside tab sections so it's accessible from all tabs */}
      <Dialog open={is1099DialogOpen} onOpenChange={setIs1099DialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>1099 Tax Report - {report1099Data?.contractor?.name || 'Contractor'}</DialogTitle>
            <DialogDescription>
              Annual payment report for tax filing
            </DialogDescription>
          </DialogHeader>
          {report1099Data && (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Contractor Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-600">Name:</span> {report1099Data.contractor.name}</div>
                  <div><span className="text-slate-600">Company:</span> {report1099Data.contractor.company || 'N/A'}</div>
                  <div><span className="text-slate-600">Email:</span> {report1099Data.contractor.email}</div>
                  <div><span className="text-slate-600">EIN/SSN:</span> {report1099Data.contractor.ein_ssn}</div>
                  <div className="col-span-2">
                    <span className="text-slate-600">W9 Status:</span>{' '}
                    {report1099Data.contractor.w9_received ? (
                      <span className="text-green-600 font-medium">✓ On File</span>
                    ) : (
                      <span className="text-red-600 font-medium">✗ Missing</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Payment Summary - {report1099Data.year}</h3>
                <div className="text-2xl font-bold text-blue-600">${report1099Data.total_payments.toLocaleString()}</div>
                <div className="text-sm text-slate-600 mt-1">
                  {report1099Data.payment_count} payment{report1099Data.payment_count !== 1 ? 's' : ''} across {report1099Data.project_count} project{report1099Data.project_count !== 1 ? 's' : ''}
                </div>
                {report1099Data.requires_1099 ? (
                  <div className="mt-2 text-orange-600 font-medium">⚠️ 1099 Form Required (≥ $600)</div>
                ) : (
                  <div className="mt-2 text-green-600">✓ Below 1099 threshold</div>
                )}
              </div>

              <div>
                <h3 className="font-semibold mb-2">Payment Details</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Project</th>
                        <th className="text-left p-2">Phase</th>
                        <th className="text-right p-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report1099Data.payments.map((payment, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-2">{formatDate(payment.date)}</td>
                          <td className="p-2">{payment.project}</td>
                          <td className="p-2">{payment.phase}</td>
                          <td className="p-2 text-right font-medium">${payment.amount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          {/* Sticky buttons at bottom */}
          {report1099Data && (
            <div className="flex gap-2 pt-4 border-t mt-4 bg-white">
              <Button className="flex-1" onClick={handleDownload1099}>
                <Download className="w-4 h-4 mr-2" />
                Download 1099
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => window.print()}>
                <FileText className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" onClick={() => setIs1099DialogOpen(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase Payment Dialog — v2.4 gates new 4-step LogPaymentDrawer
          behind `new_payment_flow_enabled` feature flag. Legacy
          PhasePaymentDialog stays as fallback for all other users. */}
      {useNewPaymentFlow ? (
        <LogPaymentDrawer
          isOpen={isPhasePaymentDialogOpen}
          onClose={() => {
            setIsPhasePaymentDialogOpen(false);
            setSelectedPhaseForPayment(null);
          }}
          phase={selectedPhaseForPayment}
          existingPayments={selectedPhaseForPayment ? (phasePayments[selectedPhaseForPayment.id] || []) : []}
          onPaymentAdded={(newPayment) => {
            if (selectedPhaseForPayment) {
              setPhasePayments(prev => ({
                ...prev,
                [selectedPhaseForPayment.id]: [...(prev[selectedPhaseForPayment.id] || []), newPayment]
              }));
            }
            loadProjectData();
          }}
        />
      ) : (
        <PhasePaymentDialog
          isOpen={isPhasePaymentDialogOpen}
          onClose={() => {
            setIsPhasePaymentDialogOpen(false);
            setSelectedPhaseForPayment(null);
          }}
          phase={selectedPhaseForPayment}
          existingPayments={selectedPhaseForPayment ? (phasePayments[selectedPhaseForPayment.id] || []) : []}
          onPaymentAdded={(newPayment) => {
            // Refresh phase payments for this phase
            if (selectedPhaseForPayment) {
              setPhasePayments(prev => ({
                ...prev,
                [selectedPhaseForPayment.id]: [...(prev[selectedPhaseForPayment.id] || []), newPayment]
              }));
            }
            // Reload all project data to reflect the new actual_cost in phases
            loadProjectData();
          }}
        />
      )}

      {/* Multi-Unit Schedule Wizard (Phase 2) */}
      {isScheduleWizardOpen && (
        <TransferWizard
          project={project}
          userId={user?.id}
          onClose={() => {
            setIsScheduleWizardOpen(false);
            // If user just locked, the Done click also lands them on the new Schedule tab
            setActiveTab('schedule');
          }}
          onLocked={() => { setActiveTab('schedule'); }}
        />
      )}

      <BidImportWizard
        open={bidImportOpen}
        onOpenChange={setBidImportOpen}
        projectId={projectId}
        userId={user?.id}
        onApplied={() => loadProjectData()}
      />
      <RequestCOIDialog
        open={isCOIRequestOpen}
        onOpenChange={setIsCOIRequestOpen}
        projectId={projectId}
        contractors={contractors}
      />
    </div>
  );
}

export default ProjectDetails;
